import { TS, WW, WH, T, SOLID, LIQUID, NONSOLID_PASS, BIOME_NAMES } from './constants';

// === PERLIN NOISE ===
export class Perlin {
    constructor(s) {
        this.p = new Uint8Array(512); const pm = new Uint8Array(256); for (let i = 0; i < 256; i++)pm[i] = i;
        for (let i = 255; i > 0; i--) { s = (s * 16807) % 2147483647; const j = s % (i + 1);[pm[i], pm[j]] = [pm[j], pm[i]]; }
        for (let i = 0; i < 512; i++)this.p[i] = pm[i & 255];
    }
    fade(t) { return t * t * t * (t * (t * 6 - 15) + 10); }
    lerp(a, b, t) { return a + t * (b - a); }
    grad(h, x, y) { const v = h & 3; return ((v & 1) ? -x : x) + ((v & 2) ? -y : y); }
    noise(x, y) {
        const X = Math.floor(x) & 255, Y = Math.floor(y) & 255; x -= Math.floor(x); y -= Math.floor(y);
        const u = this.fade(x), v = this.fade(y), A = this.p[X] + Y, B = this.p[X + 1] + Y;
        return this.lerp(this.lerp(this.grad(this.p[A], x, y), this.grad(this.p[B], x - 1, y), u),
            this.lerp(this.grad(this.p[A + 1], x, y - 1), this.grad(this.p[B + 1], x - 1, y - 1), u), v);
    }
    oct(x, y, o, p, l) { let t = 0, a = 1, f = 1, m = 0; for (let i = 0; i < o; i++) { t += this.noise(x * f, y * f) * a; m += a; a *= p; f *= l; } return t / m; }
}
export class SRng { constructor(s) { this.s = s || 1; } n() { this.s = (this.s * 16807) % 2147483647; return (this.s - 1) / 2147483646; } ni(a, b) { return Math.floor(this.n() * (b - a + 1)) + a; } }

// === WORLD GEN ===
export class WorldGen {
    constructor(seed) {
        this.seed = seed || Math.floor(Math.random() * 999999);
        this.nT = new Perlin(this.seed); this.nC = new Perlin(this.seed + 1);
        this.nB = new Perlin(this.seed + 2); this.nD = new Perlin(this.seed + 3);
        this.nO = new Perlin(this.seed + 4); this.nM = new Perlin(this.seed + 5);
        this.nU = new Perlin(this.seed + 7); this.nX = new Perlin(this.seed + 8);
        this.rng = new SRng(this.seed);
        this.tiles = new Uint8Array(WW * WH); this.bg = new Uint8Array(WW * WH);
        this.light = new Float32Array(WW * WH); this.blockLight = new Float32Array(WW * WH);
        this.liquidLevel = new Float32Array(WW * WH);
        this.heightMap = new Float32Array(WW); this.biomeMap = new Int8Array(WW);
        this.underBiomeMap = new Int8Array(WW);
        this.spawnX = WW / 2; this.spawnY = 0; this.mineProgress = {};
        this.UNDER_NAMES = ['Caverns', 'Marble Caves', 'Granite Caves', 'Ice Caves', 'Lush Caves', 'Mushroom Grotto', 'Spider Nest', 'Crystal Caves', 'Magma Depths', 'Fossil Layer', 'Living Caves'];
    }
    gt(x, y) { if (x < 0 || x >= WW || y < 0 || y >= WH) return T.STONE; return this.tiles[y * WW + x]; }
    st(x, y, t) { if (x < 0 || x >= WW || y < 0 || y >= WH) return; this.tiles[y * WW + x] = t; }
    gb(x, y) { if (x < 0 || x >= WW || y < 0 || y >= WH) return 0; return this.bg[y * WW + x]; }
    sb(x, y, t) { if (x < 0 || x >= WW || y < 0 || y >= WH) return; this.bg[y * WW + x] = t; }
    gl(x, y) { if (x < 0 || x >= WW || y < 0 || y >= WH) return 1; return Math.max(this.light[y * WW + x], this.blockLight[y * WW + x]); }
    gsl(x, y) { if (x < 0 || x >= WW || y < 0 || y >= WH) return 1; return this.light[y * WW + x]; }
    gbl(x, y) { if (x < 0 || x >= WW || y < 0 || y >= WH) return 0; return this.blockLight[y * WW + x]; }
    gll(x, y) { if (x < 0 || x >= WW || y < 0 || y >= WH) return 0; return this.liquidLevel[y * WW + x]; }
    sll(x, y, l) { if (x < 0 || x >= WW || y < 0 || y >= WH) return; this.liquidLevel[y * WW + x] = Math.max(0, Math.min(1.0, l)); }

    generate() {
        this.genBiomes(); this.genTerrain(); this.genUnderBiomes(); this.genCaves();
        this.genOres(); this.genMines(); this.genUnderLakes(); this.genGeodes();
        this.genRuinedArenas(); this.genMushroomGroves(); this.genIceChasms();
        this.genWormTunnels(); this.genMagmaForges(); this.genCrystalCathedrals();
        this.genLivingCaves(); this.genBiomeDungeons();
        this.genDungeons(); this.genSurfaceStructures(); this.genVillages(); this.genSkyIslands(); this.genTrees(); this.genVeg();
        this.genUnderFeatures(); this.initLiquids(); this.calcLight();
        const mid = Math.floor(WW / 2);
        for (let y = 0; y < WH; y++) { if (SOLID.has(this.gt(mid, y))) { this.spawnX = mid; this.spawnY = y - 3; break; } }
    }

    genBiomes() {
        const rng = new SRng(this.seed + 42);
        const nBorder = new Perlin(this.seed + 99);
        const allBiomes = [0, 1, 2, 3, 4, 5, 6, 7, 8, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30];
        const anchors = []; anchors.push({ x: Math.floor(WW / 2), b: 2, r: Math.floor(WW * 0.1) });
        const others = allBiomes.filter(b => b !== 2);
        for (let i = others.length - 1; i > 0; i--) { const j = Math.floor(rng.n() * i);[others[i], others[j]] = [others[j], others[i]]; }
        const segW = WW / (others.length + 2);
        const minR = Math.floor(segW * 0.35);
        for (let i = 0; i < others.length; i++) { const x = Math.floor(segW * (i + 0.5 + rng.n() * 0.5)); const r = Math.max(minR, Math.floor(segW * 0.3 + rng.n() * segW * 0.4)); anchors.push({ x, b: others[i], r }); }
        this._biomeAnchors = anchors;
        for (let x = 0; x < WW; x++) {
            let bestScore = -Infinity, bestB = 2;
            // Noise-modulated borders: each anchor gets unique wobble
            for (const a of anchors) {
                const d = Math.abs(x - a.x);
                const wobble = nBorder.noise(x * 0.015, a.b * 7.31) * 28;
                const score = a.r - d + wobble + (a.b === 2 ? WW * 0.03 : 0);
                if (score > bestScore) { bestScore = score; bestB = a.b; }
            }
            this.biomeMap[x] = bestB;
        }
        // Eliminate tiny biome patches (less than 10 tiles wide)
        const MIN_BIOME_WIDTH = 10;
        let runStart = 0, runBiome = this.biomeMap[0];
        for (let x = 1; x <= WW; x++) {
            const b = x < WW ? this.biomeMap[x] : -1;
            if (b !== runBiome) {
                const runLen = x - runStart;
                if (runLen < MIN_BIOME_WIDTH) {
                    const leftB = runStart > 0 ? this.biomeMap[runStart - 1] : runBiome;
                    for (let rx = runStart; rx < x; rx++) this.biomeMap[rx] = leftB;
                }
                runStart = x; runBiome = b;
            }
        }
        for (let x = 0; x < WW; x++) {
            const h = this.nT.oct(x / WW * 4, 0, 5, 0.5, 2) * 40;
            if (h < -20 && ![5, 6, 13, 17, 0, 18, 20, 23, 27, 28, 29, 30].includes(this.biomeMap[x])) this.biomeMap[x] = 9;
        }
    }

    genTerrain() {
        const bh = WH * 0.26;
        const hAdj = { 0: 15, 1: 12, 2: 0, 3: 3, 4: 0, 5: -8, 6: -5, 7: 5, 8: -18, 9: -25, 10: -2, 11: 0, 12: -5, 13: 20, 14: -3, 15: 5, 16: -5, 17: 25, 18: -20, 19: 10, 20: -30, 21: 15, 22: 5, 23: -5, 24: -2, 25: -10, 26: 8, 27: -22, 28: -23, 29: 35, 30: -5 };
        for (let x = 0; x < WW; x++) {
            const nx = x / WW, b = this.biomeMap[x];
            let h = this.nT.oct(nx * 4, 0, 5, 0.5, 2) * 40 + this.nT.oct(nx * 8, 10, 3, 0.45, 2) * 15 + this.nD.oct(nx * 16, 5, 2, 0.4, 2) * 5;
            const mt = this.nT.oct(nx * 2, 20, 3, 0.5, 2); if (mt > 0.3) h += (mt - 0.3) * 70;
            let totalAdj = 0, totalW = 0;
            const BLEND_R = 40;
            for (let dx = -BLEND_R; dx <= BLEND_R; dx++) { const nx2 = x + dx; if (nx2 < 0 || nx2 >= WW) continue; const w = Math.exp(-Math.abs(dx) * 0.06); totalAdj += (hAdj[this.biomeMap[nx2]] || 0) * w; totalW += w; }
            h += totalAdj / totalW;
            // Biome-specific terrain modifiers with edge blending
            const bBlend = this._biomeBlendWeight(x, b);
            let biomeH = 0;
            switch (b) {
                case 5: biomeH = Math.sin(x * 0.15) * 6 + Math.sin(x * 0.4) * 2; break;
                case 6: if (h > 0) biomeH = (Math.round(h / 8) * 8 + 3) - h; break;
                case 7: biomeH = this.nT.noise(x * 0.05, 40) * 14; break;
                case 8: biomeH = (h * 0.3 - 12) - h; break;
                case 13: biomeH = Math.abs(this.nT.noise(x * 0.1, 50)) * 20; break;
                case 14: biomeH = (h * 0.5 + Math.max(0, this.nT.noise(x / 40, 60) - 0.1) * 20) - h; break;
                case 15: biomeH = this.nT.noise(x * 0.06, 70) * 10; break;
                case 17: biomeH = (Math.round(h / 6) * 6 + 4) - h; break;
                case 18: biomeH = (h * 0.3 - 18) - h; break;
                case 19: biomeH = this.nT.noise(x * 0.08, 80) * 12 + Math.abs(Math.sin(x * 0.04)) * 8; break;
                case 20: biomeH = (h * 0.2 - 25) - h; break;
                case 21: biomeH = Math.abs(this.nT.noise(x * 0.06, 85)) * 18 + this.nT.noise(x * 0.12, 90) * 5; break;
                case 22: biomeH = this.nT.noise(x * 0.04, 95) * 10 + Math.sin(x * 0.02) * 5; break;
                case 23: biomeH = Math.sin(x * 0.08) * 8 + this.nT.noise(x * 0.1, 100) * 6; break;
                case 24: biomeH = this.nT.noise(x * 0.05, 105) * 8; break;
                case 25: biomeH = Math.sin(x * 0.03) * 10 + this.nT.noise(x * 0.07, 110) * 8 + Math.abs(Math.sin(x * 0.01)) * 12; break;
                case 26: biomeH = (Math.round(h / 10) * 10 + 5) - h + this.nT.noise(x * 0.04, 115) * 4; break;
                case 27: biomeH = (h * 0.25 - 20) - h + Math.sin(x * 0.12) * 3; break;
                case 28: {
                    // Tidal Flats — terraced tidal pools with stepped elevation
                    const tideBase = Math.sin(x * 0.06) * 8 + this.nT.noise(x * 0.03, 120) * 6 - 18;
                    const stepped = Math.round(tideBase / 5) * 5; // Create flat terraces
                    biomeH = stepped + this.nT.noise(x * 0.15, 125) * 2; // Slight variation within steps
                    break;
                }
                case 29: {
                    // Storm Peaks — dramatic jagged peaks with sharp ridges
                    const baseRidge = Math.abs(this.nT.noise(x * 0.04, 130)) * 45;
                    const sharpPeak = Math.pow(Math.abs(this.nT.noise(x * 0.08, 132)), 0.5) * 25;
                    const jagged = Math.abs(Math.sin(x * 0.3 + this.nT.noise(x * 0.1, 134) * 5)) * 12;
                    const cliff = this.nT.noise(x * 0.02, 136) > 0.3 ? 15 : 0; // Sudden cliff faces
                    biomeH = baseRidge + sharpPeak + jagged + cliff + this.nT.noise(x * 0.12, 135) * 6;
                    break;
                }
                case 30: {
                    // Living Caves — organic undulating terrain like breathing flesh
                    const breathe = Math.sin(x * 0.04) * 6;
                    const organic = this.nT.noise(x * 0.06, 140) * 10;
                    const bumps = Math.pow(Math.abs(Math.sin(x * 0.15 + this.nT.noise(x * 0.08, 142) * 3)), 2) * 8;
                    biomeH = organic + breathe + bumps;
                    break;
                }
            }
            h += biomeH * bBlend;
            const sy = Math.floor(bh - h); this.heightMap[x] = sy;
            const surfTiles = [T.SNOW, T.SNOW, T.GRASS, T.GRASS, T.GRASS, T.SAND, T.REDSAND, T.JGRASS, T.MUD, T.SAND,
            T.CORGRASS, T.HALGRASS, T.MYCELIUM, T.ASH, T.SAVGRASS, T.DFORESTGRASS, T.FLOWERGRASS, T.REDSAND, T.SNOW,
            T.CRYSTALGRASS, T.ABYSSMOSS, T.EMBERSAND, T.RUINMOSS, T.BLOODGRASS, T.SHIMMERSTONE,
            T.SPOREGRASS, T.PETRIGRASS, T.PRISMGRASS,
            T.TIDALSAND, T.STORMGRASS, T.BLOODGRASS];
            const subTiles = [T.FROZENDIRT, T.FROZENDIRT, T.DIRT, T.DIRT, T.DIRT, T.SAND, T.REDSAND, T.MUD, T.MUD, T.WETSAND,
            T.CORRUPT, T.HALLOW, T.MYCELIUM, T.ASH, T.DIRT, T.DIRT, T.DIRT, T.REDSAND, T.FROZENDIRT,
            T.CRYSTALBLOCK, T.ABYSSSTONE, T.EMBERITE, T.RUINBRICK, T.BLOODSTONE, T.SHIMMERSTONE,
            T.SPORESTONE, T.PETRISTONE, T.PRISMSAND,
            T.TIDALROCK, T.STORMSTONE, T.FLESHSTONE];
            const deepT = [null, null, null, null, null, T.SANDSTONE, T.REDSANDST, null, null, null,
                T.CORSTONE, T.HALSTONE, null, T.VOLROCK, null, null, null, T.TERRACOTTA, T.FROZENDIRT,
                T.CRYSTALBLOCK, T.ABYSSSTONE, T.EMBERITE, T.RUINBRICK, T.BLOODSTONE, null,
                T.SPORESTONE, T.PETRISTONE, T.PRISMSAND,
                T.TIDALROCK, T.STORMSTONE, T.FLESHSTONE];
            // Compute neighbor biome for transition blending
            let neighborB = -1, blendFactor = 0;
            const TRANS_R = 20;
            for (let dx = 1; dx <= TRANS_R; dx++) {
                const lx = x - dx, rx = x + dx;
                if (lx >= 0 && this.biomeMap[lx] !== b) { neighborB = this.biomeMap[lx]; blendFactor = 1 - dx / TRANS_R; break; }
                if (rx < WW && this.biomeMap[rx] !== b) { neighborB = this.biomeMap[rx]; blendFactor = 1 - dx / TRANS_R; break; }
            }
            for (let y = 0; y < WH; y++) {
                if (y < sy) { this.st(x, y, T.AIR); continue; }
                if (y === sy) {
                    // Surface tile blending: in transition zone, probabilistically use neighbor's tile
                    if (neighborB >= 0 && blendFactor > 0 && this.rng.n() < blendFactor * 0.5) {
                        this.st(x, y, surfTiles[neighborB] || T.GRASS);
                    } else {
                        this.st(x, y, surfTiles[b] || T.GRASS);
                    }
                }
                else if (y < sy + 4) {
                    if (neighborB >= 0 && blendFactor > 0 && this.rng.n() < blendFactor * 0.4) {
                        this.st(x, y, subTiles[neighborB] || T.DIRT);
                    } else {
                        this.st(x, y, subTiles[b] || T.DIRT);
                    }
                }
                else if (y < sy + 8) { const dt = deepT[b]; this.st(x, y, dt || (b < 2 || b === 18 ? T.FROZENDIRT : T.DIRT)); }
                else if (y < sy + 12 && b === 17) this.st(x, y, [T.BADLANDCLAY, T.BADLANDCLAY2, T.BADLANDCLAY3][(y - sy) % 3]);
                else if (y > WH * 0.85) this.st(x, y, T.DEEPSLATE);
                else if (y > WH * 0.75) this.st(x, y, this.rng.n() > 0.5 ? T.DSTONE : T.STONE);
                else this.st(x, y, T.STONE);
                if (y > sy + 2) this.sb(x, y, T.STONE);
            }
        }
        const wl = Math.floor(bh + 10);
        for (let x = 0; x < WW; x++) {
            const sy = this.heightMap[x];
            const b2 = this.biomeMap[x];
            // Deeper water for coastal/aquatic biomes
            const extraDepth = [8, 9, 27, 28].includes(b2) ? 4 : 0;
            if (sy > wl - 7 - extraDepth) for (let y = Math.floor(sy); y <= wl + extraDepth; y++) {
                if (y >= WH) break;
                if (this.gt(x, y) === T.AIR) { this.st(x, y, b2 === 18 && (y === wl - 1 || y === wl - 2) ? T.ICE : T.WATER); this.sll(x, y, 1.0); }
            }
        }
    }



    // Fast line-of-sight check (Bresenham) to prevent light bleeding through walls
    castRay(x0, y0, x1, y1) {
        let dx = Math.abs(x1 - x0), dy = Math.abs(y1 - y0);
        let sx = (x0 < x1) ? 1 : -1, sy = (y0 < y1) ? 1 : -1;
        let err = dx - dy;
        while (true) {
            if (x0 === x1 && y0 === y1) break;
            const e2 = 2 * err;
            if (e2 > -dy) { err -= dy; x0 += sx; }
            if (e2 < dx) { err += dx; y0 += sy; }
            if (x0 === x1 && y0 === y1) break;
            // Check blockage at intermediate step
            if (SOLID.has(this.tiles[y0 * WW + x0])) return false;
        }
        return true;
    }

    genUnderBiomes() {
        // Build 2D underground biome anchors from surface biome anchors
        const nUB = new Perlin(this.seed + 77);
        // Map surface biome -> preferred underground biome
        const surfToUnder = {
            0: 3, 1: 3, 2: 0, 3: 4, 4: 4, 5: 9, 6: 9, 7: 5, 8: 5,
            9: 0, 10: 6, 11: 7, 12: 5, 13: 8, 14: 0, 15: 4, 16: 0,
            17: 9, 18: 3, 19: 7, 20: 10, 21: 8, 22: 4, 23: 6, 24: 7,
            25: 5, 26: 9, 27: 7, 28: 0, 29: 1, 30: 10
        };
        // Create 2D anchors: one per surface biome anchor, placed underground
        this._underAnchors = [];
        if (this._biomeAnchors) {
            for (const sa of this._biomeAnchors) {
                const surfY = Math.floor(this.heightMap[Math.max(0, Math.min(WW - 1, sa.x))]);
                const ub = surfToUnder[sa.b] !== undefined ? surfToUnder[sa.b] : 0;
                // Primary anchor directly under surface biome
                this._underAnchors.push({
                    x: sa.x, y: surfY + 50 + Math.floor(nUB.noise(sa.x * 0.01, sa.b * 3.7) * 30),
                    ub, r: sa.r * 0.8 + 20
                });
                // Secondary deeper anchor for variety
                const deepUb = [1, 2, 7, 8, 9][Math.floor((nUB.noise(sa.x * 0.02, sa.b * 5.1) * 0.5 + 0.5) * 5)];
                this._underAnchors.push({
                    x: sa.x + Math.floor(nUB.noise(sa.x * 0.03, 99) * 40),
                    y: Math.floor(WH * 0.6) + Math.floor(nUB.noise(sa.x * 0.015, 77) * 40),
                    ub: deepUb, r: 30 + Math.floor(nUB.noise(sa.x * 0.025, 55) * 20)
                });
            }
        }
        // Add a few deep-layer magma anchors (sparse, small radius)
        const deepSpacing = Math.floor(WW / 3);
        for (let i = 0; i < 3; i++) {
            const dx = Math.floor(deepSpacing * (i + 0.5));
            this._underAnchors.push({ x: dx, y: Math.floor(WH * 0.82), ub: 8, r: 20 });
        }
        // Fill underBiomeMap using 1D (kept for backward compat) — use dominant at mid-depth
        for (let x = 0; x < WW; x++) {
            const sy = Math.floor(this.heightMap[x]);
            this.underBiomeMap[x] = this.getUnderBiome2D(x, sy + 50);
        }
        // Apply underground biome tiles using 2D lookup
        for (let x = 0; x < WW; x++) {
            const sy = Math.floor(this.heightMap[x]);
            for (let y = sy + 15; y < WH - 5; y++) {
                if (!SOLID.has(this.gt(x, y))) continue; const t = this.gt(x, y);
                if ([T.DIRT, T.MUD, T.FROZENDIRT, T.SAND].includes(t)) continue;
                const ub = this.getUnderBiome2D(x, y);
                switch (ub) {
                    case 1: if (this.nU.noise(x / 20, y / 20) > 0.1) this.st(x, y, T.MARBLE); break;
                    case 2: if (this.nU.noise(x / 18, y / 18) > 0.05) this.st(x, y, T.GRANITE); break;
                    case 3: if (this.nU.noise(x / 15, y / 15) > 0) this.st(x, y, this.rng.n() > 0.3 ? T.PICKEDICE : T.ICE); break;
                    case 4: {
                        const ln = this.nU.noise(x / 22, y / 22);
                        if (ln > 0.55) this.st(x, y, T.MOSSTONE);
                        break;
                    }
                    case 5: if (this.nU.noise(x / 22, y / 22) > 0.45) this.st(x, y, T.MUSHGRASS); break;
                    case 6: if (this.nU.noise(x / 20, y / 20) > 0.5) this.st(x, y, T.SPIDERNEST); break;
                    case 7: {
                        const cn = this.nU.noise(x / 16, y / 16);
                        if (cn > 0.6) this.st(x, y, T.CRYSTAL);
                        else if (cn > 0.45) this.st(x, y, this.rng.n() > 0.5 ? T.AMETHYST : T.SAPPHIRE);
                        break;
                    }
                    case 8: if (y > WH * 0.68) this.st(x, y, this.rng.n() > 0.4 ? T.BASALT : T.HELLSTONE); break;
                    case 9: if (this.nU.noise(x / 12, y / 12) > 0.6) this.st(x, y, T.FOSSIL); break;
                    case 10: {
                        const flN = this.nU.noise(x / 10, y / 10);
                        if (flN > 0.6) this.st(x, y, T.FLESHSTONE);
                        else if (flN > 0.4) this.st(x, y, T.PULSEWALL);
                        break;
                    }
                }
            }
        }
    }

    // 2D underground biome lookup: returns under-biome index for any (x,y)
    getUnderBiome2D(x, y) {
        if (!this._underAnchors || this._underAnchors.length === 0) return 0;
        let bestScore = -Infinity, bestUB = 0;
        const nw = this.nU;
        for (const a of this._underAnchors) {
            const dx = x - a.x, dy = y - a.y;
            const dist = Math.sqrt(dx * dx + dy * dy * 4); // stretch Y so biomes are wider than tall
            const wobble = nw.noise(x * 0.02 + a.ub * 3.3, y * 0.02 + a.ub * 7.7) * 18;
            const score = a.r - dist + wobble;
            if (score > bestScore) { bestScore = score; bestUB = a.ub; }
        }
        return bestUB;
    }

    genCaves() {
        for (let x = 0; x < WW; x++) {
            const sy = this.heightMap[x];
            for (let y = Math.floor(sy + 5); y < WH - 5; y++) {
                const nx = x / 55, ny = y / 55;
                const c1 = this.nC.oct(nx, ny, 4, 0.5, 2), c2 = this.nC.oct(nx * 1.5 + 100, ny * 1.5 + 100, 3, 0.5, 2);
                const cheese = Math.abs(c1) + Math.abs(c2) * 0.5;
                const df = Math.min(1, (y - sy) / 50);
                if (cheese < 0.11 + df * 0.06) { this.st(x, y, T.AIR); this.sb(x, y, T.STONE); }
                const s1 = this.nC.noise(x / 28, y / 28), s2 = this.nC.noise(x / 28 + 50, y / 28 + 50);
                if (Math.abs(s1) < 0.025 && Math.abs(s2) < 0.07) { this.st(x, y, T.AIR); this.sb(x, y, T.STONE); }
                if (this.nC.oct(x / 80, y / 80, 3, 0.5, 2) > 0.35 && y > sy + 30) { this.st(x, y, T.AIR); this.sb(x, y, T.STONE); }
            }
        }
    }

    genOres() {
        for (let x = 0; x < WW; x++) {
            const sy = this.heightMap[x];
            for (let y = Math.floor(sy + 3); y < WH; y++) {
                if (!SOLID.has(this.gt(x, y))) continue; const t = this.gt(x, y);
                if ([T.SAND, T.SANDSTONE, T.REDSAND, T.REDSANDST, T.ICE, T.PICKEDICE, T.HIVE, T.BADLANDCLAY, T.BADLANDCLAY2, T.BADLANDCLAY3, T.TERRACOTTA].includes(t)) continue;
                const d = y - sy, nx = x / 20, ny = y / 20;
                if (d < 20 && this.nO.noise(nx * 3, ny * 3) > 0.6) this.st(x, y, T.CLAY);
                if (this.nO.noise(nx * 2 + 100, ny * 2 + 100) > 0.65) this.st(x, y, T.GRAVEL);
                if (d > 5 && this.nO.noise(nx * 4 + 50, ny * 4 + 50) > 0.68) this.st(x, y, T.COPPER);
                if (d > 8 && this.nO.noise(nx * 4.5 + 80, ny * 4.5 + 80) > 0.72) this.st(x, y, T.TIN);
                if (d > 10 && this.nO.noise(nx * 4, ny * 4) > 0.7) this.st(x, y, T.COAL);
                if (d > 25 && this.nO.noise(nx * 5 + 200, ny * 5 + 200) > 0.74) this.st(x, y, T.IRON);
                if (d > 40 && this.nO.noise(nx * 5.5 + 300, ny * 5.5 + 300) > 0.78) this.st(x, y, T.AMETHYST);
                if (d > 50 && this.nO.noise(nx * 6 + 400, ny * 6 + 400) > 0.8) this.st(x, y, T.GOLD);
                if (d > 55 && this.nO.noise(nx * 6 + 500, ny * 6 + 500) > 0.82) this.st(x, y, T.RUBY);
                if (d > 60 && this.nO.noise(nx * 6.5 + 550, ny * 6.5 + 550) > 0.83) this.st(x, y, T.EMERALD);
                if (d > 65 && this.nO.noise(nx * 6.5 + 600, ny * 6.5 + 600) > 0.84) this.st(x, y, T.SAPPHIRE);
                if (d > 80 && this.nO.noise(nx * 7 + 700, ny * 7 + 700) > 0.87) this.st(x, y, T.DIAMOND);
                if (d > 90 && this.nO.noise(nx * 7 + 800, ny * 7 + 800) > 0.89) this.st(x, y, T.TITANIUM);
                if (d > 100 && this.nO.noise(nx * 5 + 900, ny * 5 + 900) > 0.9) this.st(x, y, T.CRYSTAL);
            }
        }
    }

    // === NEW: Abandoned Mines ===
    genMines() {
        const num = 5 + Math.floor(this.rng.n() * 5);
        for (let i = 0; i < num; i++) {
            const mx = 50 + Math.floor(this.rng.n() * (WW - 100));
            const sy = Math.floor(this.heightMap[Math.min(mx, WW - 1)]);
            const my = sy + 18 + Math.floor(this.rng.n() * 35);
            if (my >= WH - 15) continue; this.buildMineShaft(mx, my);
        }
    }
    buildMineShaft(sx, sy) {
        let x = sx, y = sy; const segs = 3 + Math.floor(this.rng.n() * 3);
        for (let s = 0; s < segs; s++) {
            const len = 12 + Math.floor(this.rng.n() * 20); const dir = this.rng.n() > 0.5 ? 1 : -1;
            for (let dx = 0; dx < len; dx++) {
                const cx = x + dx * dir; if (cx < 2 || cx >= WW - 2) break;
                for (let dy = -2; dy <= 0; dy++) {
                    if (SOLID.has(this.gt(cx, y + dy))) { this.st(cx, y + dy, T.AIR); this.sb(cx, y + dy, T.STONE); }
                }
                if (dx % 5 === 0) { this.st(cx, y - 2, T.MINEBEAM); this.st(cx, y, T.MINEBEAM); }
                if (this.rng.n() > 0.93) this.st(cx, y - 2, T.COBWEB);
                if (this.rng.n() > 0.96) this.st(cx, y - 2, T.TORCH);
            }
            x += len * dir;
            const vLen = 3 + Math.floor(this.rng.n() * 4); const vDir = this.rng.n() > 0.5 ? 1 : -1;
            for (let dy = 0; dy < vLen; dy++) { const ny = y + dy * vDir; if (ny > 2 && ny < WH - 2) { this.st(x, ny, T.LADDER); this.sb(x, ny, T.STONE); } }
            y += vLen * vDir; if (y < 10 || y >= WH - 10) break;
            if (this.rng.n() > 0.5) this.st(x + dir, y - 1, T.CHEST);
        }
    }

    // === NEW: Underground Lakes ===
    genUnderLakes() {
        // === LARGE UNDERGROUND LAKES (expanded) ===
        const num = 10 + Math.floor(this.rng.n() * 8);
        for (let i = 0; i < num; i++) {
            const lx = 30 + Math.floor(this.rng.n() * (WW - 60));
            const sy = Math.floor(this.heightMap[Math.min(lx, WW - 1)]);
            const ly = sy + 20 + Math.floor(this.rng.n() * 55); if (ly >= WH - 15) continue;
            const rw = 10 + Math.floor(this.rng.n() * 14), rh = 5 + Math.floor(this.rng.n() * 6);
            // Carve cavern
            for (let dx = -rw; dx <= rw; dx++)for (let dy = -rh; dy <= rh; dy++) {
                const nx2 = lx + dx, ny = ly + dy; if (nx2 < 1 || nx2 >= WW - 1 || ny < 1 || ny >= WH - 1) continue;
                if ((dx * dx) / (rw * rw) + (dy * dy) / (rh * rh) <= 1) { this.st(nx2, ny, T.AIR); this.sb(nx2, ny, T.STONE); }
            }
            // Fill bottom half with water
            for (let dx = -rw; dx <= rw; dx++)for (let dy = -1; dy <= rh; dy++) {
                const nx2 = lx + dx, ny = ly + dy; if (nx2 < 1 || nx2 >= WW - 1) continue;
                if ((dx * dx) / (rw * rw) + (dy * dy) / (rh * rh) <= 1 && this.gt(nx2, ny) === T.AIR) { this.st(nx2, ny, T.WATER); this.sll(nx2, ny, 1.0); }
            }
            // Decoration around lake
            for (let dx = -rw - 1; dx <= rw + 1; dx++) {
                const nx2 = lx + dx; if (nx2 < 1 || nx2 >= WW - 1) continue;
                if (this.gt(nx2, ly + rh - 1) === T.AIR && SOLID.has(this.gt(nx2, ly + rh)) && this.rng.n() > 0.6) this.st(nx2, ly + rh - 1, T.GLOWMUSH);
                if (SOLID.has(this.gt(nx2, ly - rh)) && this.gt(nx2, ly - rh + 1) === T.AIR && this.rng.n() > 0.7) this.st(nx2, ly - rh + 1, T.STALAC);
            }
            // Sometimes add kelp in deep lakes
            if (rh >= 6) {
                for (let dx = -rw + 2; dx <= rw - 2; dx += 2 + Math.floor(this.rng.n() * 3)) {
                    const nx2 = lx + dx; if (nx2 < 1 || nx2 >= WW - 1) continue;
                    if (this.gt(nx2, ly + rh) !== T.AIR && this.rng.n() > 0.5) {
                        const kh = 2 + Math.floor(this.rng.n() * 4);
                        for (let ky = 0; ky < kh; ky++) {
                            const ky2 = ly + rh - 1 - ky;
                            if (ky2 > 0 && this.gt(nx2, ky2) === T.WATER) this.st(nx2, ky2, T.KELP);
                        }
                    }
                }
            }
        }

        // === SURFACE RIVERS / WATER FEATURES ===
        // Generate winding surface rivers in certain biomes
        const riverCount = 3 + Math.floor(this.rng.n() * 3);
        for (let ri = 0; ri < riverCount; ri++) {
            const riverStartX = 100 + Math.floor(this.rng.n() * (WW - 200));
            const biome = this.biomeMap[riverStartX];
            // Rivers appear in swamp, jungle, forest, plains, tidal, shimmer
            if (![2, 3, 7, 8, 24, 28].includes(biome)) continue;
            const riverLen = 40 + Math.floor(this.rng.n() * 60);
            const riverDir = this.rng.n() > 0.5 ? 1 : -1;
            let rx = riverStartX;
            for (let rs = 0; rs < riverLen; rs++) {
                rx += riverDir;
                if (rx < 2 || rx >= WW - 2) break;
                const sy2 = Math.floor(this.heightMap[rx]);
                const riverW = 2 + Math.floor(this.rng.n() * 3);
                const riverDepth = 3 + Math.floor(this.rng.n() * 3);
                // Carve river bed
                for (let dy = 0; dy < riverDepth; dy++) {
                    for (let ddx = -riverW; ddx <= riverW; ddx++) {
                        const nx2 = rx + ddx; if (nx2 < 1 || nx2 >= WW - 1) continue;
                        const ny = sy2 + dy;
                        if (ny >= WH - 2) continue;
                        if (dy === 0 && Math.abs(ddx) <= riverW - 1) {
                            this.st(nx2, ny, T.WATER); this.sll(nx2, ny, 1.0);
                        } else if (dy > 0 && Math.abs(ddx) < riverW) {
                            this.st(nx2, ny, T.WATER); this.sll(nx2, ny, 1.0);
                        }
                    }
                }
                // Occasional meander
                if (this.rng.n() > 0.7) rx += riverDir;
                if (this.rng.n() > 0.9) rx -= riverDir * 2;
            }
        }

        // === WATER POOLS in shallow caves near surface ===
        const poolCount = 8 + Math.floor(this.rng.n() * 6);
        for (let pi = 0; pi < poolCount; pi++) {
            const px = 20 + Math.floor(this.rng.n() * (WW - 40));
            const sy2 = Math.floor(this.heightMap[Math.min(px, WW - 1)]);
            const py = sy2 + 8 + Math.floor(this.rng.n() * 18); if (py >= WH - 10) continue;
            const pw = 4 + Math.floor(this.rng.n() * 6), ph = 2 + Math.floor(this.rng.n() * 3);
            for (let dx = -pw; dx <= pw; dx++) for (let dy = 0; dy <= ph; dy++) {
                const nx2 = px + dx, ny = py + dy; if (nx2 < 1 || nx2 >= WW - 1 || ny >= WH - 1) continue;
                if ((dx * dx) / (pw * pw) + (dy * dy) / (ph * ph) <= 1 && this.gt(nx2, ny) === T.AIR) {
                    this.st(nx2, ny, T.WATER); this.sll(nx2, ny, 1.0);
                }
            }
        }
    }

    // === NEW: Crystal Geodes ===
    genGeodes() {
        const num = 8 + Math.floor(this.rng.n() * 8);
        for (let i = 0; i < num; i++) {
            const gx = 20 + Math.floor(this.rng.n() * (WW - 40));
            const sy = Math.floor(this.heightMap[Math.min(gx, WW - 1)]);
            const gy = sy + 30 + Math.floor(this.rng.n() * 50); if (gy >= WH - 10) continue;
            const r = 3 + Math.floor(this.rng.n() * 3);
            const ct = [T.CRYSTAL, T.AMETHYST, T.ACRYSTAL][Math.floor(this.rng.n() * 3)];
            for (let dx = -r; dx <= r; dx++)for (let dy = -r; dy <= r; dy++) {
                const d = Math.sqrt(dx * dx + dy * dy), nx2 = gx + dx, ny = gy + dy;
                if (nx2 < 1 || nx2 >= WW - 1 || ny < 1 || ny >= WH - 1) continue;
                if (d <= r - 1.2) { this.st(nx2, ny, T.AIR); this.sb(nx2, ny, T.STONE); }
                else if (d <= r) this.st(nx2, ny, ct);
            }
        }
    }

    // === NEW DUNGEON STRUCTURES ===
    genRuinedArenas() {
        const num = 3 + Math.floor(this.rng.n() * 2);
        for (let i = 0; i < num; i++) {
            const ax = 60 + Math.floor(this.rng.n() * (WW - 120));
            const sy = Math.floor(this.heightMap[Math.min(ax, WW - 1)]);
            const ay = sy + 30 + Math.floor(this.rng.n() * 40);
            if (ay >= WH - 20) continue;
            const rw = 16 + Math.floor(this.rng.n() * 8), rh = 10 + Math.floor(this.rng.n() * 4);
            // Carve elliptical arena
            for (let dx = -rw; dx <= rw; dx++) for (let dy = -rh; dy <= rh; dy++) {
                const nx = ax + dx, ny = ay + dy;
                if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
                if ((dx * dx) / (rw * rw) + (dy * dy) / (rh * rh) <= 1) {
                    this.st(nx, ny, T.AIR); this.sb(nx, ny, T.ARENABRICK);
                }
            }
            // Floor
            for (let dx = -rw + 2; dx <= rw - 2; dx++) {
                const nx = ax + dx; if (nx < 2 || nx >= WW - 2) continue;
                this.st(nx, ay + rh - 1, T.ARENAFLOOR); this.st(nx, ay + rh, T.ARENABRICK);
            }
            // Pillars
            for (let p = -rw + 4; p <= rw - 4; p += 5) {
                const px2 = ax + p; if (px2 < 2 || px2 >= WW - 2) continue;
                for (let py = ay - rh + 2; py < ay + rh - 1; py++) this.st(px2, py, T.ARENAPILLAR);
                // Broken tops (some pillars are ruined)
                if (this.rng.n() > 0.4) {
                    const breakH = 2 + Math.floor(this.rng.n() * 3);
                    for (let py = ay - rh + 2; py < ay - rh + 2 + breakH; py++) this.st(px2, py, T.AIR);
                }
            }
            // Center spawner + chest
            this.st(ax, ay + rh - 2, T.DSPAWNER); this.st(ax - 3, ay + rh - 2, T.CHEST); this.st(ax + 3, ay + rh - 2, T.CHEST);
            // Torches
            this.st(ax - rw + 3, ay - rh + 3, T.TORCH); this.st(ax + rw - 3, ay - rh + 3, T.TORCH);
        }
    }

    genMushroomGroves() {
        const num = 2 + Math.floor(this.rng.n() * 3);
        for (let i = 0; i < num; i++) {
            const gx = 40 + Math.floor(this.rng.n() * (WW - 80));
            const sy = Math.floor(this.heightMap[Math.min(gx, WW - 1)]);
            const gy = sy + 25 + Math.floor(this.rng.n() * 30);
            if (gy >= WH - 25) continue;
            const gw = 20 + Math.floor(this.rng.n() * 15), gh = 12 + Math.floor(this.rng.n() * 6);
            // Carve cavern
            for (let dx = -gw; dx <= gw; dx++) for (let dy = -gh; dy <= gh; dy++) {
                const nx = gx + dx, ny = gy + dy;
                if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
                if ((dx * dx) / (gw * gw) + (dy * dy) / (gh * gh) <= 1) {
                    this.st(nx, ny, T.AIR); this.sb(nx, ny, T.MUSHGRASS);
                }
            }
            // Mycelium floor
            for (let dx = -gw + 1; dx <= gw - 1; dx++) {
                const nx = gx + dx; if (nx < 2 || nx >= WW - 2) continue;
                this.st(nx, gy + gh - 1, T.MYCELIUM);
            }
            // Giant mushroom trees
            for (let m = 0; m < 5 + Math.floor(this.rng.n() * 4); m++) {
                const mx = gx - gw + 4 + Math.floor(this.rng.n() * (gw * 2 - 8));
                if (mx < 2 || mx >= WW - 2) continue;
                const mh2 = 4 + Math.floor(this.rng.n() * 5);
                for (let dy = 0; dy < mh2; dy++) this.st(mx, gy + gh - 2 - dy, T.MUSTEM);
                const capR = 2 + Math.floor(this.rng.n() * 2);
                for (let cx = -capR; cx <= capR; cx++) {
                    const cnx = mx + cx; if (cnx < 2 || cnx >= WW - 2) continue;
                    this.st(cnx, gy + gh - 2 - mh2, T.MUCAP);
                    if (Math.abs(cx) < capR) this.st(cnx, gy + gh - 3 - mh2, T.MUCAP);
                }
            }
            // Glowing mushrooms on ceiling
            for (let dx = -gw + 2; dx <= gw - 2; dx += 2 + Math.floor(this.rng.n() * 3)) {
                const nx = gx + dx; if (nx < 2 || nx >= WW - 2) continue;
                if (this.rng.n() > 0.4) this.st(nx, gy - gh + 2, T.GLOWMUSH);
            }
            // Biovines
            for (let dx = -gw + 1; dx <= gw - 1; dx += 3) {
                const nx = gx + dx; if (nx < 2 || nx >= WW - 2) continue;
                if (this.rng.n() > 0.5) { const vl = 2 + Math.floor(this.rng.n() * 4); for (let v = 0; v < vl; v++) this.st(nx, gy - gh + 1 + v, T.BIOVINE); }
            }
            // Honey pools
            for (let dx = -gw / 2 | 0; dx <= gw / 2; dx++) {
                const nx = gx + dx; if (nx < 2 || nx >= WW - 2) continue;
                if (this.rng.n() > 0.7) { this.st(nx, gy + gh - 2, T.HONEY); this.sll(nx, gy + gh - 2, 0.8); }
            }
        }
    }

    genIceChasms() {
        const num = 2 + Math.floor(this.rng.n() * 2);
        for (let i = 0; i < num; i++) {
            const cx = 50 + Math.floor(this.rng.n() * (WW - 100));
            const sy = Math.floor(this.heightMap[Math.min(cx, WW - 1)]);
            const startY = sy + 15 + Math.floor(this.rng.n() * 20);
            if (startY >= WH - 40) continue;
            const depth = 25 + Math.floor(this.rng.n() * 15), halfW = 3 + Math.floor(this.rng.n() * 3);
            // Carve vertical chasm
            for (let dy = 0; dy < depth; dy++) {
                const wobble = Math.floor(Math.sin(dy / 4) * 2);
                for (let dx = -halfW + wobble; dx <= halfW + wobble; dx++) {
                    const nx = cx + dx, ny = startY + dy;
                    if (nx < 2 || nx >= WW - 2 || ny >= WH - 2) continue;
                    this.st(nx, ny, T.AIR); this.sb(nx, ny, T.ICEBRICK);
                }
            }
            // Ice walls
            for (let dy = 0; dy < depth; dy++) {
                const wobble = Math.floor(Math.sin(dy / 4) * 2);
                const lx = cx - halfW - 1 + wobble, rx = cx + halfW + 1 + wobble;
                if (lx >= 2) this.st(lx, startY + dy, T.PICKEDICE);
                if (rx < WW - 2) this.st(rx, startY + dy, T.PICKEDICE);
            }
            // Ice platforms with chests
            for (let p = 0; p < 4; p++) {
                const py = startY + 5 + p * 6; if (py >= startY + depth - 2) break;
                const platW = 3 + Math.floor(this.rng.n() * 2);
                const side = this.rng.n() > 0.5 ? -1 : 1;
                for (let dx = 0; dx < platW; dx++) {
                    const nx = cx + side * (halfW - platW + dx);
                    if (nx >= 2 && nx < WW - 2) this.st(nx, py, T.ICEPLAT);
                }
                if (this.rng.n() > 0.5) this.st(cx + side * (halfW - 1), py - 1, T.FROZCHEST);
            }
            // Stalactites
            for (let dx = -halfW; dx <= halfW; dx++) {
                if (this.rng.n() > 0.6) { this.st(cx + dx, startY + 1, T.STALAC); if (this.rng.n() > 0.5) this.st(cx + dx, startY + 2, T.STALAC); }
            }
        }
    }

    genWormTunnels() {
        const num = 3 + Math.floor(this.rng.n() * 3);
        for (let i = 0; i < num; i++) {
            let wx = 40 + Math.floor(this.rng.n() * (WW - 80));
            const sy = Math.floor(this.heightMap[Math.min(wx, WW - 1)]);
            let wy = sy + 20 + Math.floor(this.rng.n() * 30);
            if (wy >= WH - 20) continue;
            const segs = 10 + Math.floor(this.rng.n() * 15);
            let angle = this.rng.n() * Math.PI * 1.5 + Math.PI * 0.25; // bias downward/sideways, avoid going up
            for (let s = 0; s < segs; s++) {
                angle += (this.rng.n() - 0.5) * 0.8;
                const len = 4 + Math.floor(this.rng.n() * 4);
                for (let l = 0; l < len; l++) {
                    const tx = Math.floor(wx + Math.cos(angle) * l), ty = Math.floor(wy + Math.sin(angle) * l);
                    const surfAtTx = this.heightMap[Math.max(0, Math.min(WW - 1, tx))];
                    if (tx < 3 || tx >= WW - 3 || ty < 3 || ty >= WH - 3) continue;
                    if (ty < surfAtTx + 10) continue; // stay underground
                    for (let dx = -2; dx <= 2; dx++) for (let dy = -2; dy <= 2; dy++) {
                        if (dx * dx + dy * dy <= 5) {
                            const nx = tx + dx, ny = ty + dy;
                            if (nx >= 1 && nx < WW - 1 && ny >= 1 && ny < WH - 1) {
                                this.st(nx, ny, T.AIR); this.sb(nx, ny, T.STONE);
                            }
                        }
                    }
                    // Slime on walls
                    if (l % 3 === 0) {
                        for (let dx = -3; dx <= 3; dx++) for (let dy = -3; dy <= 3; dy++) {
                            if (dx * dx + dy * dy > 5 && dx * dx + dy * dy <= 10) {
                                const nx = tx + dx, ny = ty + dy;
                                if (nx >= 1 && nx < WW - 1 && ny >= 1 && ny < WH - 1 && SOLID.has(this.gt(nx, ny))) {
                                    if (this.rng.n() > 0.6) this.st(nx, ny, T.WORMSLIME);
                                }
                            }
                        }
                    }
                }
                wx += Math.cos(angle) * len; wy += Math.sin(angle) * len;
                // Redirect down if getting too close to surface
                const surfHere = this.heightMap[Math.max(0, Math.min(WW - 1, Math.floor(wx)))];
                if (wy < surfHere + 15) angle = Math.PI * 0.5 + (this.rng.n() - 0.5) * 0.5; // force downward
            }
            // Nest with loot at end
            for (let dx = -3; dx <= 3; dx++) for (let dy = -3; dy <= 3; dy++) {
                if (dx * dx + dy * dy <= 9) {
                    const nx = Math.floor(wx) + dx, ny = Math.floor(wy) + dy;
                    if (nx >= 1 && nx < WW - 1 && ny >= 1 && ny < WH - 1) { this.st(nx, ny, T.AIR); this.sb(nx, ny, T.WORMSLIME); }
                }
            }
            this.st(Math.floor(wx), Math.floor(wy), T.CHEST);
        }
    }

    genMagmaForges() {
        const num = 2 + Math.floor(this.rng.n() * 2);
        for (let i = 0; i < num; i++) {
            const fx = 50 + Math.floor(this.rng.n() * (WW - 100));
            const fy = Math.floor(WH * 0.7) + Math.floor(this.rng.n() * (WH * 0.2));
            if (fy >= WH - 15) continue;
            const fw = 12 + Math.floor(this.rng.n() * 6), fh = 8 + Math.floor(this.rng.n() * 3);
            // Carve room
            for (let dx = -fw; dx <= fw; dx++) for (let dy = -fh; dy <= fh; dy++) {
                const nx = fx + dx, ny = fy + dy;
                if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
                if (Math.abs(dx) === fw || Math.abs(dy) === fh) this.st(nx, ny, T.FORGEBRICK);
                else { this.st(nx, ny, T.AIR); this.sb(nx, ny, T.FORGEBRICK); }
            }
            // Floor
            for (let dx = -fw + 1; dx < fw; dx++) this.st(fx + dx, fy + fh - 1, T.FORGEFLOOR);
            // Lava river through middle
            for (let dx = -fw + 2; dx < fw - 2; dx++) {
                if (Math.abs(dx) > 3) continue;
                this.st(fx + dx, fy + fh - 2, T.LAVA); this.sll(fx + dx, fy + fh - 2, 1.0);
            }
            // Bridge over lava
            for (let dx = -3; dx <= 3; dx++) this.st(fx + dx, fy + fh - 3, T.OBSIDIAN);
            // Anvils
            this.st(fx - fw + 3, fy + fh - 2, T.FORGEANVIL); this.st(fx + fw - 3, fy + fh - 2, T.FORGEANVIL);
            // Chests + torches
            this.st(fx - fw + 2, fy + fh - 2, T.CHEST); this.st(fx + fw - 2, fy + fh - 2, T.CHEST);
            this.st(fx - fw + 1, fy - fh + 2, T.EMBER); this.st(fx + fw - 1, fy - fh + 2, T.EMBER);
        }
    }

    genCrystalCathedrals() {
        const num = 2 + Math.floor(this.rng.n() * 2);
        for (let i = 0; i < num; i++) {
            const cx = 60 + Math.floor(this.rng.n() * (WW - 120));
            const sy = Math.floor(this.heightMap[Math.min(cx, WW - 1)]);
            const cy = sy + 35 + Math.floor(this.rng.n() * 30);
            if (cy >= WH - 25) continue;
            const cw = 10 + Math.floor(this.rng.n() * 6), ch = 15 + Math.floor(this.rng.n() * 8);
            // Carve tall cavity
            for (let dx = -cw; dx <= cw; dx++) for (let dy = -ch; dy <= ch; dy++) {
                const nx = cx + dx, ny = cy + dy;
                if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
                if ((dx * dx) / (cw * cw) + (dy * dy) / (ch * ch) <= 1) {
                    this.st(nx, ny, T.AIR); this.sb(nx, ny, T.STONE);
                }
            }
            // Crystal formations on walls
            const crystTypes = [T.CRYSTAL, T.AMETHYST, T.ACRYSTAL, T.RUBY, T.EMERALD, T.SAPPHIRE];
            // Ceiling crystals (stalactites of crystal)
            for (let dx = -cw + 2; dx <= cw - 2; dx += 2) {
                if (this.rng.n() > 0.4) continue;
                const nx = cx + dx; if (nx < 2 || nx >= WW - 2) continue;
                const ct = crystTypes[Math.floor(this.rng.n() * crystTypes.length)];
                const clen = 2 + Math.floor(this.rng.n() * 4);
                for (let dy2 = 0; dy2 < clen; dy2++) {
                    const ny2 = cy - ch + 2 + dy2; if (ny2 < 2 || ny2 >= WH - 2) continue;
                    this.st(nx, ny2, ct);
                }
            }
            // Floor crystals (stalagmites of crystal)
            for (let dx = -cw + 2; dx <= cw - 2; dx += 2) {
                if (this.rng.n() > 0.5) continue;
                const nx = cx + dx; if (nx < 2 || nx >= WW - 2) continue;
                const ct = crystTypes[Math.floor(this.rng.n() * crystTypes.length)];
                const clen = 1 + Math.floor(this.rng.n() * 3);
                for (let dy2 = 0; dy2 < clen; dy2++) {
                    const ny2 = cy + ch - 2 - dy2; if (ny2 < 2 || ny2 >= WH - 2) continue;
                    this.st(nx, ny2, ct);
                }
            }
            // Crystal shards scattered
            for (let dx = -cw + 1; dx <= cw - 1; dx++) {
                const nx = cx + dx; if (nx < 2 || nx >= WW - 2) continue;
                if (this.rng.n() > 0.7 && this.gt(nx, cy + ch - 1) === T.AIR) this.st(nx, cy + ch - 1, T.CRYSTSHARD);
            }
            // Glowing mushrooms for extra light
            this.st(cx - 2, cy + ch - 1, T.GLOWMUSH); this.st(cx + 2, cy + ch - 1, T.GLOWMUSH);
        }
    }

    genLivingCaves() {
        // Generate organic cave systems with flesh walls and nerve veins
        const num = 2 + Math.floor(this.rng.n() * 2);
        for (let i = 0; i < num; i++) {
            const cx = 60 + Math.floor(this.rng.n() * (WW - 120));
            const sy = Math.floor(this.heightMap[Math.min(cx, WW - 1)]);
            const cy = sy + 30 + Math.floor(this.rng.n() * 40);
            if (cy >= WH - 30) continue;
            // Check if this is near a Living Caves underground biome
            if (this.underBiomeMap[Math.min(cx, WW - 1)] !== 10) continue;
            const cw = 15 + Math.floor(this.rng.n() * 10), ch = 10 + Math.floor(this.rng.n() * 6);
            // Carve organic cavity — use irregular shape
            for (let dx = -cw; dx <= cw; dx++) for (let dy = -ch; dy <= ch; dy++) {
                const nx = cx + dx, ny = cy + dy;
                if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
                const distSq = (dx * dx) / (cw * cw) + (dy * dy) / (ch * ch);
                const wobble = this.nU.noise(dx * 0.3, dy * 0.3) * 0.3;
                if (distSq + wobble <= 1) {
                    if (distSq + wobble > 0.85) {
                        // Walls — flesh stone
                        this.st(nx, ny, T.PULSEWALL);
                    } else {
                        this.st(nx, ny, T.AIR); this.sb(nx, ny, T.FLESHSTONE);
                    }
                }
            }
            // Nerve veins — organic lines radiating from center
            for (let v = 0; v < 6; v++) {
                let vx = cx, vy = cy;
                let vAngle = (v / 6) * Math.PI * 2 + this.rng.n() * 0.5;
                const vLen = 8 + Math.floor(this.rng.n() * 8);
                for (let s = 0; s < vLen; s++) {
                    vAngle += (this.rng.n() - 0.5) * 0.5;
                    vx += Math.cos(vAngle) * 1.5;
                    vy += Math.sin(vAngle) * 1.5;
                    const nvx = Math.floor(vx), nvy = Math.floor(vy);
                    if (nvx < 2 || nvx >= WW - 2 || nvy < 2 || nvy >= WH - 2) break;
                    if (this.gt(nvx, nvy) === T.AIR) this.st(nvx, nvy, T.NERVEVEIN);
                    // Nerve vein also places on walls
                    if (SOLID.has(this.gt(nvx, nvy)) && this.gt(nvx, nvy) !== T.NERVEVEIN) {
                        this.st(nvx, nvy, T.NERVEVEIN);
                    }
                }
            }
            // Eye blocks — scattered watching eyes
            for (let e = 0; e < 4 + Math.floor(this.rng.n() * 4); e++) {
                const ex = cx + Math.floor((this.rng.n() - 0.5) * cw * 1.4);
                const ey = cy + Math.floor((this.rng.n() - 0.5) * ch * 1.4);
                if (ex >= 2 && ex < WW - 2 && ey >= 2 && ey < WH - 2) {
                    if (SOLID.has(this.gt(ex, ey))) this.st(ex, ey, T.EYEBLOCK);
                }
            }
            // Organic moss on floor
            for (let dx = -cw + 2; dx <= cw - 2; dx++) {
                const nx = cx + dx; if (nx < 2 || nx >= WW - 2) continue;
                if (this.gt(nx, cy + ch - 1) === T.AIR && SOLID.has(this.gt(nx, cy + ch))) {
                    if (this.rng.n() > 0.4) this.st(nx, cy + ch - 1, T.ORGANICMOSS);
                }
            }
            // Central heart block — pulsating core
            this.st(cx, cy, T.HEARTBLOCK);
            this.st(cx - 1, cy, T.HEARTBLOCK); this.st(cx + 1, cy, T.HEARTBLOCK);
            this.st(cx, cy - 1, T.HEARTBLOCK); this.st(cx, cy + 1, T.HEARTBLOCK);
            // Chest near heart
            if (this.rng.n() > 0.3) this.st(cx + 2, cy + ch - 1, T.CHEST);
        }
    }

    // === BIOME-SPECIFIC DUNGEONS ===
    genBiomeDungeons() {
        // Find each biome's center and generate a unique dungeon under it
        const placed = new Set();
        // Collect biome runs to find centers
        const biomeRuns = [];
        let rStart = 0, rBiome = this.biomeMap[0];
        for (let x = 1; x <= WW; x++) {
            const b = x < WW ? this.biomeMap[x] : -1;
            if (b !== rBiome) {
                biomeRuns.push({ b: rBiome, start: rStart, end: x - 1, cx: Math.floor((rStart + x - 1) / 2) });
                rStart = x; rBiome = b;
            }
        }
        for (const run of biomeRuns) {
            if (placed.has(run.b)) continue;
            if (run.end - run.start < 30) continue; // skip tiny biomes
            const cx = run.cx;
            const sy = Math.floor(this.heightMap[Math.min(cx, WW - 1)]);
            const dungY = sy + 25 + Math.floor(this.rng.n() * 30);
            if (dungY >= WH - 30) continue;
            placed.add(run.b);
            switch (run.b) {
                case 0: this._buildIceFortress(cx, dungY); break;
                case 1: this._buildTaigaBurrow(cx, dungY); break;
                case 3: this._buildRootCatacombs(cx, dungY); break;
                case 5: this._buildSandTomb(cx, dungY); break;
                case 7: this._buildJungleLabyrinth(cx, dungY); break;
                case 10: this._buildShadowVault(cx, dungY); break;
                case 11: this._buildHallowSanctum(cx, dungY); break;
                case 12: this._buildMyceliumNest(cx, dungY); break;
                case 13: this._buildInfernalForge(cx, dungY); break;
                case 19: this._buildCrystalVault(cx, dungY); break;
                case 20: this._buildAbyssCrypt(cx, dungY); break;
                case 21: this._buildEmberCitadel(cx, dungY); break;
                case 23: this._buildBloodCrypt(cx, dungY); break;
                case 25: this._buildSporeLair(cx, dungY); break;
                case 29: this._buildStormVault(cx, dungY); break;
                case 30: this._buildFleshHeart(cx, dungY); break;
            }
        }
    }

    // Helper: carve a rectangular room with walls, floor, background
    _carveRoom(cx, cy, hw, hh, wallT, floorT, bgT) {
        for (let dx = -hw; dx <= hw; dx++) for (let dy = -hh; dy <= hh; dy++) {
            const nx = cx + dx, ny = cy + dy;
            if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
            if (Math.abs(dx) === hw || Math.abs(dy) === hh) this.st(nx, ny, wallT);
            else { this.st(nx, ny, T.AIR); this.sb(nx, ny, bgT); }
        }
        for (let dx = -hw + 1; dx < hw; dx++) {
            const nx = cx + dx;
            if (nx >= 2 && nx < WW - 2) this.st(nx, cy + hh - 1, floorT);
        }
    }

    // Helper: carve a connecting corridor between two points
    _carveCorridor(x1, y1, x2, y2, bgT, halfW) {
        const hw = halfW || 2;
        // Horizontal then vertical
        const stepX = x1 < x2 ? 1 : -1;
        for (let x = x1; x !== x2; x += stepX) {
            for (let dy = -hw; dy <= hw; dy++) {
                const ny = y1 + dy;
                if (x >= 2 && x < WW - 2 && ny >= 2 && ny < WH - 2) {
                    if (SOLID.has(this.gt(x, ny))) { this.st(x, ny, T.AIR); this.sb(x, ny, bgT); }
                }
            }
        }
        const stepY = y1 < y2 ? 1 : -1;
        for (let y = y1; y !== y2; y += stepY) {
            for (let dx = -hw; dx <= hw; dx++) {
                const nx = x2 + dx;
                if (nx >= 2 && nx < WW - 2 && y >= 2 && y < WH - 2) {
                    if (SOLID.has(this.gt(nx, y))) { this.st(nx, y, T.AIR); this.sb(nx, y, bgT); }
                }
            }
        }
    }

    // Ice Fortress (Tundra) — frozen rooms connected by icy bridges
    _buildIceFortress(cx, cy) {
        const rooms = 4 + Math.floor(this.rng.n() * 3);
        let rx = cx, ry = cy;
        for (let i = 0; i < rooms; i++) {
            const rw = 8 + Math.floor(this.rng.n() * 5), rh = 6 + Math.floor(this.rng.n() * 3);
            this._carveRoom(rx, ry, rw, rh, T.ICEBRICK, T.ICEFLOOR, T.ICEBRICK);
            // Ice pillars
            for (let p = -rw + 3; p <= rw - 3; p += 4) {
                if (rx + p >= 2 && rx + p < WW - 2) {
                    for (let py = ry - rh + 2; py < ry + rh - 1; py++) this.st(rx + p, py, T.PICKEDICE);
                }
            }
            // Frozen chests
            if (this.rng.n() > 0.4) this.st(rx + Math.floor(this.rng.n() * 4) - 2, ry + rh - 2, T.FROZCHEST);
            // Icicles from ceiling
            for (let dx = -rw + 2; dx <= rw - 2; dx += 2) {
                if (this.rng.n() > 0.5) this.st(rx + dx, ry - rh + 2, T.STALAC);
            }
            // TRAP: Spike floors in some rooms
            if (i > 0 && this.rng.n() > 0.5) {
                for (let dx = -rw + 3; dx <= rw - 3; dx += 3) {
                    if (this.rng.n() > 0.4) this.st(rx + dx, ry + rh - 2, T.SPIKE);
                }
                this.st(rx, ry + rh - 2, T.PRESSPLATE);
            }
            // TRAP: Hidden spike corridor with secret reward
            if (i === 1 && this.rng.n() > 0.4) {
                const trapDir = this.rng.n() > 0.5 ? 1 : -1;
                const trapX = rx + trapDir * (rw + 2);
                for (let dy = -2; dy <= 2; dy++) {
                    if (ry + dy >= 2 && ry + dy < WH - 2) {
                        this.st(trapX, ry + dy, T.AIR); this.sb(trapX, ry + dy, T.ICEBRICK);
                    }
                }
                this.st(trapX, ry + 2, T.SPIKE);
                this.st(trapX, ry - 2, T.FROZCHEST);
            }
            // Torches
            this.st(rx - rw + 2, ry - rh + 2, T.TORCH); this.st(rx + rw - 2, ry - rh + 2, T.TORCH);
            if (i < rooms - 1) {
                const nrx = rx + (this.rng.n() > 0.5 ? 1 : -1) * (rw + 5 + Math.floor(this.rng.n() * 5));
                const nry = ry + Math.floor(this.rng.n() * 8);
                this._carveCorridor(rx, ry, nrx, nry, T.ICEBRICK, 2);
                // Ice bridge floor with occasional gaps
                const minX2 = Math.min(rx, nrx), maxX2 = Math.max(rx, nrx);
                for (let bx = minX2; bx <= maxX2; bx++) {
                    if (bx >= 2 && bx < WW - 2 && ry + 2 >= 2 && ry + 2 < WH - 2) {
                        if (this.rng.n() > 0.15) this.st(bx, ry + 2, T.ICEPLAT);
                        else this.st(bx, ry + 3, T.SPIKE);
                    }
                }
                rx = nrx; ry = nry;
            }
        }
        // Boss room at end — larger, more dramatic
        const bossRW = 14, bossRH = 10;
        this._carveRoom(rx, ry + 14, bossRW, bossRH, T.ICEBRICK, T.ICEFLOOR, T.ICEBRICK);
        // Decorative pillars
        for (let p = -bossRW + 4; p <= bossRW - 4; p += 5) {
            for (let py = ry + 14 - bossRH + 2; py < ry + 14 + bossRH - 1; py++) this.st(rx + p, py, T.PICKEDICE);
        }
        // Spike ring around spawner
        for (let dx = -3; dx <= 3; dx++) for (let dy = -3; dy <= 3; dy++) {
            if (Math.abs(dx) === 3 || Math.abs(dy) === 3) {
                const sx2 = rx + dx, sy2 = ry + 14 + dy;
                if (sx2 >= 2 && sx2 < WW - 2 && sy2 >= 2 && sy2 < WH - 2) this.st(sx2, sy2, T.SPIKE);
            }
        }
        this.st(rx, ry + 14, T.DSPAWNER);
        this.st(rx - 5, ry + 14 + bossRH - 2, T.FROZCHEST);
        this.st(rx + 5, ry + 14 + bossRH - 2, T.FROZCHEST);
        this.st(rx, ry + 14 + bossRH - 2, T.DCHEST);
        // Icicle ceiling in boss room
        for (let dx = -bossRW + 2; dx <= bossRW - 2; dx++) {
            if (this.rng.n() > 0.4) this.st(rx + dx, ry + 14 - bossRH + 2, T.STALAC);
        }
    }

    // Taiga Burrow — wooden tunnels with roots
    _buildTaigaBurrow(cx, cy) {
        let rx = cx, ry = cy;
        for (let i = 0; i < 3; i++) {
            const rw = 7 + Math.floor(this.rng.n() * 4), rh = 5 + Math.floor(this.rng.n() * 3);
            this._carveRoom(rx, ry, rw, rh, T.PINEWOOD, T.WOOD, T.PINEWOOD);
            // Support beams
            for (let p = -rw + 2; p <= rw - 2; p += 3) {
                if (rx + p >= 2 && rx + p < WW - 2) this.st(rx + p, ry + rh - 2, T.MINEBEAM);
            }
            // Roots from ceiling
            for (let dx = -rw + 1; dx <= rw - 1; dx += 2) {
                if (this.rng.n() > 0.6) this.st(rx + dx, ry - rh + 2, T.ROOT);
            }
            this.st(rx, ry + rh - 2, T.CHEST);
            if (i < 2) {
                const nrx = rx + (this.rng.n() > 0.5 ? 1 : -1) * (rw + 4);
                const nry = ry + 6 + Math.floor(this.rng.n() * 4);
                this._carveCorridor(rx, ry, nrx, nry, T.PINEWOOD, 1);
                rx = nrx; ry = nry;
            }
        }
    }

    // Root Catacombs (Forest) — organic tunnels with roots and moss
    _buildRootCatacombs(cx, cy) {
        let rx = cx, ry = cy;
        const rooms = 4 + Math.floor(this.rng.n() * 2);
        for (let i = 0; i < rooms; i++) {
            const rw = 6 + Math.floor(this.rng.n() * 5), rh = 5 + Math.floor(this.rng.n() * 3);
            this._carveRoom(rx, ry, rw, rh, T.MOSSTONE, T.DFLOOR, T.MOSSTONE);
            // Roots and vines
            for (let dx = -rw + 1; dx <= rw - 1; dx++) {
                if (this.rng.n() > 0.6) {
                    const vl = 1 + Math.floor(this.rng.n() * 3);
                    for (let v = 0; v < vl; v++) {
                        if (ry - rh + 2 + v < WH - 2) this.st(rx + dx, ry - rh + 2 + v, T.ROOT);
                    }
                }
                if (this.rng.n() > 0.7) this.st(rx + dx, ry + rh - 2, T.CAVEMOSS);
            }
            if (this.rng.n() > 0.5) this.st(rx + Math.floor(this.rng.n() * 4) - 2, ry + rh - 2, T.CHEST);
            this.st(rx - rw + 2, ry - rh + 2, T.TORCH); this.st(rx + rw - 2, ry - rh + 2, T.TORCH);
            if (i < rooms - 1) {
                const nrx = rx + (this.rng.n() > 0.5 ? 1 : -1) * (rw + 3 + Math.floor(this.rng.n() * 6));
                const nry = ry + 4 + Math.floor(this.rng.n() * 6);
                this._carveCorridor(rx, ry, nrx, nry, T.MOSSTONE, 2);
                rx = nrx; ry = nry;
            }
        }
    }

    // Sand Tomb (Desert) — pyramid interior with sand traps
    _buildSandTomb(cx, cy) {
        // Main burial chamber
        this._carveRoom(cx, cy, 12, 8, T.SANDSTONE, T.ANCFLOOR, T.SANDSTONE);
        // Sarcophagus
        this.st(cx, cy + 6, T.SARCOPH); this.st(cx - 1, cy + 6, T.SARCOPH); this.st(cx + 1, cy + 6, T.SARCOPH);
        // Pillars
        for (const px of [-8, -4, 4, 8]) {
            for (let py = cy - 6; py < cy + 7; py++) {
                if (cx + px >= 2 && cx + px < WW - 2) this.st(cx + px, py, T.ANCPILLAR);
            }
        }
        // Treasure rooms on sides
        for (const side of [-1, 1]) {
            const trx = cx + side * 18;
            this._carveRoom(trx, cy, 6, 5, T.ANCBRICK, T.ANCFLOOR, T.ANCBRICK);
            this._carveCorridor(cx + side * 12, cy, trx - side * 6, cy, T.SANDSTONE, 2);
            this.st(trx, cy + 3, T.ANCCHEST); this.st(trx + side * 2, cy + 3, T.ANCCHEST);
            // Torches
            this.st(trx - 4, cy - 3, T.TORCH); this.st(trx + 4, cy - 3, T.TORCH);
        }
        // Sand traps (sand falling from ceiling)
        for (let dx = -10; dx <= 10; dx += 3) {
            if (this.rng.n() > 0.5) this.st(cx + dx, cy - 7, T.SAND);
        }
        this.st(cx, cy - 6, T.DTORCH); this.st(cx, cy, T.DSPAWNER);
    }

    // Jungle Labyrinth — maze-like with overgrown walls
    _buildJungleLabyrinth(cx, cy) {
        const mw = 28, mh = 20;
        // Carve main area
        for (let dx = -mw; dx <= mw; dx++) for (let dy = -mh; dy <= mh; dy++) {
            const nx = cx + dx, ny = cy + dy;
            if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
            this.st(nx, ny, T.MUD); this.sb(nx, ny, T.MUD);
        }
        // Carve maze corridors
        const cellW = 5, cellH = 5;
        const cols = Math.floor(mw * 2 / cellW), rows = Math.floor(mh * 2 / cellH);
        for (let col = 0; col < cols; col++) for (let row = 0; row < rows; row++) {
            const lx = cx - mw + col * cellW + 1, ly = cy - mh + row * cellH + 1;
            for (let dx = 0; dx < cellW - 1; dx++) for (let dy = 0; dy < cellH - 1; dy++) {
                const nx = lx + dx, ny = ly + dy;
                if (nx >= 2 && nx < WW - 2 && ny >= 2 && ny < WH - 2) { this.st(nx, ny, T.AIR); this.sb(nx, ny, T.MUD); }
            }
            if (col < cols - 1 && this.rng.n() > 0.35) {
                for (let dy = 1; dy < cellH - 2; dy++) {
                    const nx = lx + cellW - 1, ny = ly + dy;
                    if (nx >= 2 && nx < WW - 2 && ny >= 2 && ny < WH - 2) { this.st(nx, ny, T.AIR); this.sb(nx, ny, T.MUD); }
                }
            }
            if (row < rows - 1 && this.rng.n() > 0.35) {
                for (let dx = 1; dx < cellW - 2; dx++) {
                    const nx = lx + dx, ny = ly + cellH - 1;
                    if (nx >= 2 && nx < WW - 2 && ny >= 2 && ny < WH - 2) { this.st(nx, ny, T.AIR); this.sb(nx, ny, T.MUD); }
                }
            }
            // TRAP: Thorn floors in dead-end cells
            if (this.rng.n() > 0.7) {
                const trapNx = lx + 1, trapNy = ly + cellH - 2;
                if (trapNx >= 2 && trapNx < WW - 2 && trapNy >= 2 && trapNy < WH - 2) {
                    for (let tdx = 0; tdx < 2; tdx++) this.st(trapNx + tdx, trapNy, T.THORN);
                }
            }
            // TRAP: Cobweb ambush corners
            if (this.rng.n() > 0.75) {
                const cwx = lx, cwy = ly;
                if (cwx >= 2 && cwx < WW - 2 && cwy >= 2 && cwy < WH - 2) {
                    this.st(cwx, cwy, T.COBWEB);
                    this.st(cwx + 1, cwy, T.COBWEB);
                }
            }
        }
        // Vines and ferns
        for (let dx = -mw + 1; dx < mw; dx++) for (let dy = -mh + 1; dy < mh; dy++) {
            const nx = cx + dx, ny = cy + dy;
            if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
            if (this.gt(nx, ny) === T.AIR && SOLID.has(this.gt(nx, ny - 1)) && this.rng.n() > 0.75) this.st(nx, ny, T.VINE);
            if (this.gt(nx, ny) === T.AIR && SOLID.has(this.gt(nx, ny + 1)) && this.rng.n() > 0.85) this.st(nx, ny, T.FERN);
        }
        // SECRET SIDE ROOMS — hidden treasure alcoves
        for (let s = 0; s < 3; s++) {
            const srx = cx + Math.floor((this.rng.n() - 0.5) * mw * 1.2);
            const sry = cy + Math.floor((this.rng.n() - 0.5) * mh * 0.8);
            if (srx >= 4 && srx < WW - 4 && sry >= 4 && sry < WH - 4) {
                this._carveRoom(srx, sry, 4, 3, T.ANCBRICK, T.ANCFLOOR, T.MUD);
                this.st(srx, sry + 1, T.CHEST);
                this.st(srx - 2, sry - 1, T.TORCH);
                // Gate entrance — player must find pressure plate
                this.st(srx - 4, sry, T.DGATE); this.st(srx - 4, sry - 1, T.DGATE);
                // Pressure plate somewhere nearby
                const ppx = srx - 6 - Math.floor(this.rng.n() * 4);
                if (ppx >= 2 && ppx < WW - 2 && this.gt(ppx, sry) === T.AIR) this.st(ppx, sry + 2, T.PRESSPLATE);
            }
        }
        // Central treasure room — larger with guardians
        this._carveRoom(cx, cy, 8, 5, T.ANCBRICK, T.ANCFLOOR, T.MUD);
        // Decorative pillars
        this.st(cx - 5, cy - 3, T.ANCPILLAR); this.st(cx + 5, cy - 3, T.ANCPILLAR);
        for (let py = cy - 3; py < cy + 3; py++) { this.st(cx - 5, py, T.ANCPILLAR); this.st(cx + 5, py, T.ANCPILLAR); }
        this.st(cx, cy + 3, T.ANCCHEST);
        this.st(cx - 3, cy + 3, T.CHEST);
        this.st(cx + 3, cy + 3, T.CHEST);
        this.st(cx, cy - 3, T.DSPAWNER);
        this.st(cx - 3, cy - 3, T.DSPAWNER); // Extra spawner = harder
        this.st(cx - 6, cy - 3, T.TORCH); this.st(cx + 6, cy - 3, T.TORCH);
        this.st(cx - 6, cy + 3, T.TORCH); this.st(cx + 6, cy + 3, T.TORCH);
    }

    // Shadow Vault (Corruption) — dark corridors with tentacles
    _buildShadowVault(cx, cy) {
        const rooms = 4 + Math.floor(this.rng.n() * 3);
        let rx = cx, ry = cy;
        for (let i = 0; i < rooms; i++) {
            const rw = 9 + Math.floor(this.rng.n() * 5), rh = 7 + Math.floor(this.rng.n() * 3);
            this._carveRoom(rx, ry, rw, rh, T.CORSTONE, T.DFLOOR, T.CORSTONE);
            // Thorns/tentacles — more dense
            for (let dx = -rw + 1; dx <= rw - 1; dx++) {
                if (this.rng.n() > 0.4) this.st(rx + dx, ry + rh - 2, T.THORN);
                if (this.rng.n() > 0.55) this.st(rx + dx, ry - rh + 2, T.THORN);
            }
            // TRAP: Spike gauntlet corridors
            if (i > 0 && this.rng.n() > 0.4) {
                for (let dx = -rw + 2; dx <= rw - 2; dx += 2) {
                    if (this.rng.n() > 0.6) this.st(rx + dx, ry + rh - 2, T.SPIKE);
                }
            }
            // Dark banners for atmosphere
            if (this.rng.n() > 0.5) {
                this.st(rx - rw + 3, ry - rh + 3, T.DBANNER);
                this.st(rx + rw - 3, ry - rh + 3, T.DBANNER);
            }
            // Dark torches
            this.st(rx - rw + 2, ry - rh + 2, T.DTORCH); this.st(rx + rw - 2, ry - rh + 2, T.DTORCH);
            if (this.rng.n() > 0.4) this.st(rx, ry + rh - 2, T.DCHEST);
            // SECRET: hidden alcove behind wall
            if (i === 2 && this.rng.n() > 0.3) {
                const secDir = this.rng.n() > 0.5 ? 1 : -1;
                const secX = rx + secDir * (rw + 3);
                if (secX >= 4 && secX < WW - 4) {
                    this._carveRoom(secX, ry, 3, 3, T.CORSTONE, T.DFLOOR, T.CORSTONE);
                    this.st(secX, ry + 1, T.DCHEST);
                    this.st(secX, ry - 1, T.DTORCH);
                }
            }
            if (i < rooms - 1) {
                const nrx = rx + (this.rng.n() > 0.5 ? 1 : -1) * (rw + 4 + Math.floor(this.rng.n() * 4));
                const nry = ry + 5 + Math.floor(this.rng.n() * 5);
                this._carveCorridor(rx, ry, nrx, nry, T.CORSTONE, 2);
                // Spikes in corridors
                const midCorrX = Math.floor((rx + nrx) / 2);
                if (midCorrX >= 2 && midCorrX < WW - 2) {
                    this.st(midCorrX, ry + 2, T.SPIKE);
                    this.st(midCorrX + 1, ry + 2, T.SPIKE);
                }
                rx = nrx; ry = nry;
            }
        }
        // Dark altar boss room — grand chamber
        const bRW = 12, bRH = 9;
        this._carveRoom(rx, ry + 14, bRW, bRH, T.CORSTONE, T.DFLOOR, T.CORSTONE);
        // Corruption pillars
        for (let p = -bRW + 4; p <= bRW - 4; p += 4) {
            for (let py = ry + 14 - bRH + 2; py < ry + 14 + bRH - 1; py++) this.st(rx + p, py, T.DPILLAR);
        }
        // Spike ring
        for (let dx = -4; dx <= 4; dx++) {
            this.st(rx + dx, ry + 14 - 4, T.THORN);
            this.st(rx + dx, ry + 14 + 4, T.THORN);
        }
        this.st(rx, ry + 14, T.DALTAR);
        this.st(rx - 2, ry + 14 - 2, T.DSPAWNER); this.st(rx + 2, ry + 14 - 2, T.DSPAWNER);
        this.st(rx - 5, ry + 14 + bRH - 2, T.DCHEST); this.st(rx + 5, ry + 14 + bRH - 2, T.DCHEST);
        this.st(rx, ry + 14 + bRH - 2, T.DCHEST);
        // Dark banners flanking altar
        this.st(rx - bRW + 3, ry + 14 - bRH + 3, T.DBANNER); this.st(rx + bRW - 3, ry + 14 - bRH + 3, T.DBANNER);
        this.st(rx - bRW + 3, ry + 14 + bRH - 3, T.DBANNER); this.st(rx + bRW - 3, ry + 14 + bRH - 3, T.DBANNER);
    }

    // Hallow Sanctum — crystal rooms with rainbow light
    _buildHallowSanctum(cx, cy) {
        const rw = 14, rh = 10;
        this._carveRoom(cx, cy, rw, rh, T.HALSTONE, T.DFLOOR, T.HALSTONE);
        // Crystal pillars
        for (let p = -rw + 3; p <= rw - 3; p += 4) {
            for (let py = cy - rh + 2; py < cy + rh - 1; py++) {
                if (cx + p >= 2 && cx + p < WW - 2) this.st(cx + p, py, T.RCRYSTAL);
            }
        }
        // Crystal shard floor decorations
        for (let dx = -rw + 2; dx <= rw - 2; dx += 2) {
            if (this.rng.n() > 0.6) this.st(cx + dx, cy + rh - 2, T.CRYSTSHARD);
        }
        this.st(cx, cy + rh - 2, T.CHEST);
        this.st(cx - rw + 2, cy - rh + 2, T.TORCH); this.st(cx + rw - 2, cy - rh + 2, T.TORCH);
        // Side chapels
        for (const side of [-1, 1]) {
            const sx = cx + side * (rw + 7);
            this._carveRoom(sx, cy, 5, 5, T.HALSTONE, T.DFLOOR, T.HALSTONE);
            this._carveCorridor(cx + side * rw, cy, sx - side * 5, cy, T.HALSTONE, 2);
            this.st(sx, cy + 3, T.CHEST);
        }
    }

    // Mycelium Nest (Mushroom Fields) — organic fungal chambers
    _buildMyceliumNest(cx, cy) {
        const rooms = 4;
        let rx = cx, ry = cy;
        for (let i = 0; i < rooms; i++) {
            const rw = 7 + Math.floor(this.rng.n() * 5), rh = 5 + Math.floor(this.rng.n() * 3);
            // Elliptical carve for organic shape
            for (let dx = -rw; dx <= rw; dx++) for (let dy = -rh; dy <= rh; dy++) {
                const nx = rx + dx, ny = ry + dy;
                if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
                if ((dx * dx) / (rw * rw) + (dy * dy) / (rh * rh) <= 1) {
                    this.st(nx, ny, T.AIR); this.sb(nx, ny, T.MUSHGRASS);
                }
            }
            // Mycelium floor
            for (let dx = -rw + 1; dx <= rw - 1; dx++) {
                if (rx + dx >= 2 && rx + dx < WW - 2) this.st(rx + dx, ry + rh - 1, T.MYCELIUM);
            }
            // Giant mushrooms
            const mh2 = 3 + Math.floor(this.rng.n() * 3);
            for (let dy = 0; dy < mh2; dy++) this.st(rx, ry + rh - 2 - dy, T.MUSTEM);
            for (let dx2 = -2; dx2 <= 2; dx2++) this.st(rx + dx2, ry + rh - 2 - mh2, T.MUCAP);
            // Glowing mushrooms
            for (let dx = -rw + 2; dx <= rw - 2; dx += 2) {
                if (this.rng.n() > 0.5) this.st(rx + dx, ry + rh - 2, T.GLOWMUSH);
            }
            if (i < rooms - 1) {
                const nrx = rx + (this.rng.n() > 0.5 ? 1 : -1) * (rw + 3);
                const nry = ry + 5 + Math.floor(this.rng.n() * 4);
                this._carveCorridor(rx, ry, nrx, nry, T.MUSHGRASS, 2);
                rx = nrx; ry = nry;
            }
        }
        // Queen chamber
        this._carveRoom(rx, ry + 10, 10, 7, T.MYCELIUM, T.MUSHGRASS, T.MUSHGRASS);
        this.st(rx, ry + 10, T.DSPAWNER); this.st(rx, ry + 16, T.CHEST);
    }

    // Infernal Forge (Volcanic) — lava-filled forge with obsidian bridges
    _buildInfernalForge(cx, cy) {
        const rw = 16, rh = 10;
        this._carveRoom(cx, cy, rw, rh, T.BASALT, T.FORGEFLOOR, T.BASALT);
        // Lava pool in center
        for (let dx = -rw + 3; dx <= rw - 3; dx++) {
            for (let dy = 2; dy <= rh - 2; dy++) {
                const nx = cx + dx, ny = cy + dy;
                if (nx >= 2 && nx < WW - 2 && ny >= 2 && ny < WH - 2 && this.gt(nx, ny) === T.AIR) {
                    this.st(nx, ny, T.LAVA); this.sll(nx, ny, 1.0);
                }
            }
        }
        // Obsidian bridges
        for (let dx = -rw + 3; dx <= rw - 3; dx++) this.st(cx + dx, cy + 1, T.OBSIDIAN);
        for (let dy = -rh + 2; dy < rh - 1; dy++) this.st(cx, cy + dy, T.OBSIDIAN);
        // Forges and anvils on platforms
        this.st(cx - rw + 3, cy, T.FORGEANVIL); this.st(cx + rw - 3, cy, T.FORGEANVIL);
        this.st(cx - rw + 4, cy, T.CHEST); this.st(cx + rw - 4, cy, T.CHEST);
        // Ember torches
        for (let dx = -rw + 2; dx <= rw - 2; dx += 4) this.st(cx + dx, cy - rh + 2, T.EMBER);
        this.st(cx, cy - rh + 3, T.DSPAWNER);
    }

    // Crystal Vault (Crystal Caverns) — prismatic rooms
    _buildCrystalVault(cx, cy) {
        const rw = 12, rh = 10;
        this._carveRoom(cx, cy, rw, rh, T.CRYSTALBLOCK, T.DFLOOR, T.CRYSTALBLOCK);
        // Crystal formations
        const crystTypes = [T.CRYSTAL, T.AMETHYST, T.ACRYSTAL, T.SAPPHIRE, T.EMERALD];
        for (let dx = -rw + 2; dx <= rw - 2; dx += 2) {
            if (this.rng.n() > 0.4) {
                const ct = crystTypes[Math.floor(this.rng.n() * crystTypes.length)];
                const ch = 2 + Math.floor(this.rng.n() * 3);
                for (let dy = 0; dy < ch; dy++) this.st(cx + dx, cy - rh + 2 + dy, ct);
            }
            if (this.rng.n() > 0.5) {
                const ct = crystTypes[Math.floor(this.rng.n() * crystTypes.length)];
                const ch = 1 + Math.floor(this.rng.n() * 2);
                for (let dy = 0; dy < ch; dy++) this.st(cx + dx, cy + rh - 2 - dy, ct);
            }
        }
        this.st(cx, cy + rh - 2, T.CHEST); this.st(cx - 5, cy + rh - 2, T.CHEST);
        this.st(cx, cy - rh + 3, T.GLOWMUSH); this.st(cx - rw + 2, cy - rh + 2, T.GLOWMUSH); this.st(cx + rw - 2, cy - rh + 2, T.GLOWMUSH);
    }

    // Abyss Crypt (The Abyss) — dark, deep, oppressive
    _buildAbyssCrypt(cx, cy) {
        let rx = cx, ry = cy;
        for (let i = 0; i < 4; i++) {
            const rw = 7 + Math.floor(this.rng.n() * 4), rh = 5 + Math.floor(this.rng.n() * 3);
            this._carveRoom(rx, ry, rw, rh, T.ABYSSSTONE, T.DFLOOR, T.ABYSSSTONE);
            // Chains hanging from ceiling
            for (let dx = -rw + 2; dx <= rw - 2; dx += 3) {
                const cl = 2 + Math.floor(this.rng.n() * 3);
                for (let dy = 0; dy < cl; dy++) this.st(rx + dx, ry - rh + 2 + dy, T.CHAIN);
            }
            if (this.rng.n() > 0.5) this.st(rx, ry + rh - 2, T.DCHEST);
            this.st(rx - rw + 2, ry - rh + 2, T.DTORCH); this.st(rx + rw - 2, ry - rh + 2, T.DTORCH);
            if (i < 3) {
                const nrx = rx + (this.rng.n() > 0.5 ? 1 : -1) * (rw + 4);
                const nry = ry + 6 + Math.floor(this.rng.n() * 5);
                this._carveCorridor(rx, ry, nrx, nry, T.ABYSSSTONE, 2);
                rx = nrx; ry = nry;
            }
        }
        // Deep boss chamber
        this._carveRoom(rx, ry + 12, 12, 8, T.ABYSSSTONE, T.DFLOOR, T.ABYSSSTONE);
        this.st(rx, ry + 12, T.DSPAWNER); this.st(rx, ry + 18, T.DCHEST);
    }

    // Ember Citadel (Ember Wastes) — forge-like with lava channels
    _buildEmberCitadel(cx, cy) {
        this._carveRoom(cx, cy, 14, 9, T.FORGEBRICK, T.FORGEFLOOR, T.FORGEBRICK);
        // Lava channels
        for (let dy = -7; dy <= 7; dy++) {
            for (const side of [-10, 10]) {
                const nx = cx + side;
                if (nx >= 2 && nx < WW - 2 && cy + dy >= 2 && cy + dy < WH - 2) {
                    this.st(nx, cy + dy, T.LAVA); this.sll(nx, cy + dy, 1.0);
                }
            }
        }
        // Forges
        this.st(cx - 5, cy + 7, T.FORGEANVIL); this.st(cx + 5, cy + 7, T.FORGEANVIL);
        this.st(cx, cy + 7, T.CHEST);
        // Ember lights
        for (let dx = -12; dx <= 12; dx += 4) this.st(cx + dx, cy - 7, T.EMBER);
        this.st(cx, cy - 5, T.DSPAWNER);
    }

    // Blood Crypt (Blood Plains) — crimson-drenched horror
    _buildBloodCrypt(cx, cy) {
        let rx = cx, ry = cy;
        for (let i = 0; i < 3; i++) {
            const rw = 8 + Math.floor(this.rng.n() * 4), rh = 6 + Math.floor(this.rng.n() * 3);
            this._carveRoom(rx, ry, rw, rh, T.BLOODSTONE, T.DFLOOR, T.BLOODSTONE);
            // Blood pools (water tinted via biome)
            for (let dx = -rw + 3; dx <= rw - 3; dx++) {
                if (this.rng.n() > 0.6) { this.st(rx + dx, ry + rh - 2, T.LAVA); this.sll(rx + dx, ry + rh - 2, 0.5); }
            }
            // Thorns
            for (let dx = -rw + 1; dx <= rw - 1; dx += 2) {
                if (this.rng.n() > 0.5) this.st(rx + dx, ry - rh + 2, T.THORN);
            }
            // Bone piles
            if (this.rng.n() > 0.4) this.st(rx - 3, ry + rh - 2, T.BONEPILE);
            if (this.rng.n() > 0.4) this.st(rx + 3, ry + rh - 2, T.BONEPILE);
            this.st(rx - rw + 2, ry - rh + 2, T.DTORCH); this.st(rx + rw - 2, ry - rh + 2, T.DTORCH);
            if (i < 2) {
                const nrx = rx + (this.rng.n() > 0.5 ? 1 : -1) * (rw + 4);
                const nry = ry + 5 + Math.floor(this.rng.n() * 4);
                this._carveCorridor(rx, ry, nrx, nry, T.BLOODSTONE, 2);
                rx = nrx; ry = nry;
            }
        }
        // Sacrificial chamber
        this._carveRoom(rx, ry + 12, 10, 7, T.BLOODSTONE, T.DFLOOR, T.BLOODSTONE);
        this.st(rx, ry + 12, T.DALTAR); this.st(rx, ry + 18, T.DCHEST);
    }

    // Spore Lair (Fungal Abyss) — pulsating mushroom chambers
    _buildSporeLair(cx, cy) {
        let rx = cx, ry = cy;
        for (let i = 0; i < 4; i++) {
            const rw = 8 + Math.floor(this.rng.n() * 5), rh = 6 + Math.floor(this.rng.n() * 3);
            // Organic elliptical shape
            for (let dx = -rw; dx <= rw; dx++) for (let dy = -rh; dy <= rh; dy++) {
                const nx = rx + dx, ny = ry + dy;
                if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
                const wobble = this.nU.noise(dx * 0.3, dy * 0.3) * 0.2;
                if ((dx * dx) / (rw * rw) + (dy * dy) / (rh * rh) + wobble <= 1) {
                    this.st(nx, ny, T.AIR); this.sb(nx, ny, T.SPORESTONE);
                }
            }
            // Sporecaps and glowing mushrooms
            for (let dx = -rw + 2; dx <= rw - 2; dx += 2) {
                if (this.rng.n() > 0.5) this.st(rx + dx, ry + rh - 1, T.GLOWMUSH);
                if (this.rng.n() > 0.7) this.st(rx + dx, ry - rh + 1, T.SPORECAP);
            }
            // Pulsevines
            for (let dx = -rw + 1; dx <= rw - 1; dx += 3) {
                if (this.rng.n() > 0.5) {
                    const vl = 2 + Math.floor(this.rng.n() * 3);
                    for (let v = 0; v < vl; v++) this.st(rx + dx, ry - rh + 2 + v, T.PULSEVINE);
                }
            }
            if (i < 3) {
                const nrx = rx + (this.rng.n() > 0.5 ? 1 : -1) * (rw + 3);
                const nry = ry + 5 + Math.floor(this.rng.n() * 4);
                this._carveCorridor(rx, ry, nrx, nry, T.SPORESTONE, 2);
                rx = nrx; ry = nry;
            }
        }
        // Hive core
        this._carveRoom(rx, ry + 10, 8, 6, T.SPORESTONE, T.SPORESTONE, T.SPORESTONE);
        this.st(rx, ry + 10, T.DSPAWNER); this.st(rx, ry + 14, T.CHEST);
    }

    // Storm Vault (Storm Peaks) — electrified chambers
    _buildStormVault(cx, cy) {
        const rw = 14, rh = 10;
        this._carveRoom(cx, cy, rw, rh, T.STORMSTONE, T.DFLOOR, T.STORMSTONE);
        // Charged crystal pillars
        for (let p = -rw + 3; p <= rw - 3; p += 5) {
            for (let py = cy - rh + 2; py < cy + rh - 1; py++) {
                if (cx + p >= 2 && cx + p < WW - 2) this.st(cx + p, py, T.OBSIDIANSPIRE);
            }
            this.st(cx + p, cy - rh + 2, T.CHARGEDCRYSTAL);
        }
        // Storm glass floor patterns
        for (let dx = -rw + 2; dx <= rw - 2; dx += 2) {
            if (this.rng.n() > 0.5) this.st(cx + dx, cy + rh - 2, T.STORMGLASS);
        }
        this.st(cx, cy + rh - 2, T.CHEST); this.st(cx - 6, cy + rh - 2, T.CHEST);
        this.st(cx, cy - rh + 3, T.DSPAWNER);
        // Side chambers
        for (const side of [-1, 1]) {
            const sx = cx + side * (rw + 6);
            this._carveRoom(sx, cy, 5, 5, T.STORMSTONE, T.DFLOOR, T.STORMSTONE);
            this._carveCorridor(cx + side * rw, cy, sx - side * 5, cy, T.STORMSTONE, 2);
            this.st(sx, cy + 3, T.CHARGEDCRYSTAL);
        }
    }

    // Flesh Heart (Living Caves) — organic, pulsating horror dungeon
    _buildFleshHeart(cx, cy) {
        // Main heart chamber — elliptical
        const rw = 14, rh = 10;
        for (let dx = -rw; dx <= rw; dx++) for (let dy = -rh; dy <= rh; dy++) {
            const nx = cx + dx, ny = cy + dy;
            if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
            const wobble = this.nU.noise(dx * 0.2, dy * 0.2) * 0.25;
            const d = (dx * dx) / (rw * rw) + (dy * dy) / (rh * rh);
            if (d + wobble <= 1) {
                if (d + wobble > 0.8) this.st(nx, ny, T.PULSEWALL);
                else { this.st(nx, ny, T.AIR); this.sb(nx, ny, T.FLESHSTONE); }
            }
        }
        // Nerve veins radiating out
        for (let v = 0; v < 8; v++) {
            let vx = cx, vy = cy;
            let angle = (v / 8) * Math.PI * 2;
            for (let s = 0; s < 12; s++) {
                angle += (this.rng.n() - 0.5) * 0.4;
                vx += Math.cos(angle) * 1.5; vy += Math.sin(angle) * 1.5;
                const nvx = Math.floor(vx), nvy = Math.floor(vy);
                if (nvx < 2 || nvx >= WW - 2 || nvy < 2 || nvy >= WH - 2) break;
                this.st(nvx, nvy, T.NERVEVEIN);
            }
        }
        // Eye blocks watching
        for (let e = 0; e < 6; e++) {
            const ex = cx + Math.floor((this.rng.n() - 0.5) * rw * 1.2);
            const ey = cy + Math.floor((this.rng.n() - 0.5) * rh * 1.2);
            if (ex >= 2 && ex < WW - 2 && ey >= 2 && ey < WH - 2 && SOLID.has(this.gt(ex, ey))) this.st(ex, ey, T.EYEBLOCK);
        }
        // Central heart
        this.st(cx, cy, T.HEARTBLOCK); this.st(cx - 1, cy, T.HEARTBLOCK);
        this.st(cx + 1, cy, T.HEARTBLOCK); this.st(cx, cy - 1, T.HEARTBLOCK); this.st(cx, cy + 1, T.HEARTBLOCK);
        // Organic moss floor
        for (let dx = -rw + 2; dx <= rw - 2; dx++) {
            if (this.rng.n() > 0.4 && this.gt(cx + dx, cy + rh - 1) === T.AIR) this.st(cx + dx, cy + rh - 1, T.ORGANICMOSS);
        }
        this.st(cx + 3, cy + rh - 2, T.CHEST);
    }

    genDungeons() {
        const cnt = 5, sp = WW / (cnt + 1);
        for (let d = 0; d < cnt; d++) {
            const bx = Math.floor(sp * (d + 0.5) + this.rng.n() * sp * 0.3);
            const sy = Math.floor(this.heightMap[Math.min(bx, WW - 1)]); this.buildDungeon(bx, sy, d % 3);
        }
    }
    buildDungeon(ex, sy, type) {
        const bt = [T.DBRICK, T.ANCBRICK, T.MAGBRICK][type], ft = [T.DFLOOR, T.ANCFLOOR, T.MAGFLOOR][type],
            wt = [T.DWALL, T.CRYPTWALL, T.MAGBRICK][type], pt = [T.DPILLAR, T.ANCPILLAR, T.DPILLAR][type],
            tt = [T.DTORCH, T.TORCH, T.EMBER][type], ct = [T.DCHEST, T.ANCCHEST, T.CHEST][type];
        const w = 8, h = 7, sx = ex - 4;
        for (let bx = sx - 2; bx < sx + w + 2; bx++)for (let by = sy - h - 3; by <= sy + 1; by++) {
            if (bx === sx - 2 || bx === sx + w + 1 || by === sy - h - 3) this.st(bx, by, bt);
            else if (by < sy) this.st(bx, by, T.AIR); this.sb(bx, by, wt);
        }
        for (let by = sy - h - 1; by <= sy; by++) { this.st(sx - 1, by, pt); this.st(sx + w, by, pt); }
        for (let bx = sx; bx < sx + w; bx++) { this.st(bx, sy, ft); this.st(bx, sy + 1, bt); }
        this.st(sx + 1, sy - h + 1, tt); this.st(sx + w - 2, sy - h + 1, tt);
        let cx = ex, cy = sy + 1, dir = this.rng.n() > 0.5 ? 1 : -1;
        const floors = 4 + Math.floor(this.rng.n() * 2), flH = Math.floor((WH * 0.4) / floors);
        for (let f = 0; f < floors; f++) {
            const tY = sy + flH * (f + 1);
            while (cy < tY) {
                const seg = 3 + Math.floor(this.rng.n() * 3);
                for (let s = 0; s < seg && cy < tY; s++) {
                    for (let dx = -3; dx <= 3; dx++) { for (let dy = -4; dy <= 0; dy++) { this.st(cx + dx, cy + dy, T.AIR); this.sb(cx + dx, cy + dy, wt); } this.st(cx + dx, cy + 1, ft); }
                    this.st(cx - 4, cy, bt); this.st(cx - 4, cy - 1, bt); this.st(cx + 4, cy, bt); this.st(cx + 4, cy - 1, bt);
                    this.st(cx, cy, T.LADDER); this.sb(cx, cy, wt); cx += dir; cy++;
                }
                if (this.rng.n() > 0.5) dir = -dir;
            }
            const rw = 12 + Math.floor(this.rng.n() * 10), rh = 7 + Math.floor(this.rng.n() * 4), rx = cx - Math.floor(rw / 2);
            for (let x2 = rx - 1; x2 <= rx + rw; x2++)for (let y2 = cy - rh - 1; y2 <= cy + 1; y2++) {
                if (x2 === rx - 1 || x2 === rx + rw || y2 === cy - rh - 1 || y2 === cy + 1) this.st(x2, y2, bt);
                else { this.st(x2, y2, T.AIR); this.sb(x2, y2, wt); }
            }
            for (let x2 = rx; x2 < rx + rw; x2++)this.st(x2, cy, ft);
            this.st(rx + 1, cy - rh + 1, tt); this.st(rx + rw - 2, cy - rh + 1, tt);
            this.st(rx + Math.floor(rw / 2), cy - 1, ct);
            // Decorations in rooms
            if (this.rng.n() > 0.5) { this.st(rx + 2, cy - 1, T.BONEPILE); this.st(rx + rw - 3, cy - 1, T.BONEPILE); }
            if (type === 0 && this.rng.n() > 0.6) { this.st(rx + 3, cy - rh + 1, T.CHAIN); this.st(rx + 3, cy - rh + 2, T.CHAIN); this.st(rx + rw - 4, cy - rh + 1, T.CHAIN); }
        }
        const brw = 20, brh = 12, brx = cx - 10;
        for (let x2 = brx - 1; x2 <= brx + brw; x2++)for (let y2 = cy - brh - 1; y2 <= cy + 1; y2++) {
            if (x2 === brx - 1 || x2 === brx + brw || y2 === cy - brh - 1 || y2 === cy + 1) this.st(x2, y2, bt);
            else { this.st(x2, y2, T.AIR); this.sb(x2, y2, wt); }
        }
        for (let x2 = brx; x2 < brx + brw; x2++)this.st(x2, cy, ft);
        this.st(brx + 10, cy - 6, T.DSPAWNER); this.st(brx + 5, cy - 1, T.DALTAR); this.st(brx + 15, cy - 1, T.DALTAR);
        this.st(brx + 10, cy - 1, ct); this.st(brx + 3, cy - 1, T.BONEPILE); this.st(brx + 17, cy - 1, T.BONEPILE);
    }

    // === VILLAGE GENERATION ===
    genVillages() {
        const villageSpacing = 280;
        this.villageLocations = [];
        this.villageData = []; // Store detailed village info for NPC spawning
        for (let attempt = 0; attempt < 40; attempt++) {
            const cx = 60 + Math.floor(this.rng.n() * (WW - 120));
            let tooClose = false;
            for (const vl of this.villageLocations) { if (Math.abs(vl - cx) < villageSpacing) { tooClose = true; break; } }
            if (tooClose) continue;
            const b = this.biomeMap[cx];
            if (![1, 2, 3, 5, 7, 14, 16].includes(b)) continue;
            const sy = Math.floor(this.heightMap[cx]);
            // Check flatness over wider area
            let flat = true, maxDeviation = 0;
            for (let dx = -25; dx <= 25; dx++) {
                const nx = cx + dx; if (nx < 0 || nx >= WW) { flat = false; break; }
                const dev = Math.abs(this.heightMap[nx] - sy);
                if (dev > 4) { flat = false; break; }
                maxDeviation = Math.max(maxDeviation, dev);
            }
            if (!flat) continue;
            this.villageLocations.push(cx);
            const vData = this._buildVillage(cx, sy, b);
            this.villageData.push(vData);
            if (this.villageLocations.length >= 7) break;
        }
    }

    _getVillageStyle(biome) {
        const styles = {
            1: { wall: T.WOOD, floor: T.PLANK, roof: T.PINELEAF, pillar: T.PINEWOOD, path: T.COBBLEPATH, fence: T.FENCE, name: 'Taiga', accent: T.PINEWOOD, roofTile: T.PINELEAF, window: T.GLASS },
            2: { wall: T.COBBLEPATH, floor: T.PLANK, roof: T.ROOFTILE, pillar: T.VPILLAR, path: T.COBBLEPATH, fence: T.FENCE, name: 'Plains', accent: T.STONE, roofTile: T.ROOFTILE, window: T.GLASS },
            3: { wall: T.WOOD, floor: T.PLANK, roof: T.LEAVES, pillar: T.WOOD, path: T.COBBLEPATH, fence: T.FENCE, name: 'Forest', accent: T.WOOD, roofTile: T.LEAVES, window: T.GLASS },
            5: { wall: T.SANDSTONE, floor: T.SAND, roof: T.CLAYBRICK, pillar: T.SANDSTONE, path: T.SAND, fence: T.CLAYBRICK, name: 'Desert', accent: T.SANDSTONE, roofTile: T.CLAYBRICK, window: T.GLASS },
            7: { wall: T.WOOD, floor: T.PLANK, roof: T.CANOPY, pillar: T.BAMBOO, path: T.MUD, fence: T.BAMBOO, name: 'Jungle', accent: T.BAMBOO, roofTile: T.CANOPY, window: T.GLASS },
            14: { wall: T.CLAYBRICK, floor: T.PLANK, roof: T.THATCH, pillar: T.ACACIA, path: T.COBBLEPATH, fence: T.FENCE, name: 'Savanna', accent: T.ACACIA, roofTile: T.THATCH, window: T.GLASS },
            16: { wall: T.COBBLEPATH, floor: T.PLANK, roof: T.THATCH, pillar: T.VPILLAR, path: T.COBBLEPATH, fence: T.FENCE, name: 'Meadow', accent: T.STONE, roofTile: T.THATCH, window: T.GLASS },
        };
        return styles[biome] || styles[2];
    }

    _buildVillage(cx, sy, biome) {
        const style = this._getVillageStyle(biome);
        const buildings = [];
        const buildingInfo = []; // track building types for NPC placement
        const numBuildings = 5 + Math.floor(this.rng.n() * 5); // 5-9 buildings

        // Central plaza with fountain/well
        this._buildVillagePlaza(cx, sy, style);
        buildings.push({ x: cx - 3, w: 7 });

        // Guarantee at least one of each important type
        const guaranteedTypes = [0, 2, 5]; // house, blacksmith, farm
        const extraTypes = [0, 1, 3, 4, 5, 6, 7, 8, 9, 10]; // all types

        let posLeft = cx - 14, posRight = cx + 10;
        for (let i = 0; i < numBuildings; i++) {
            let buildType;
            if (i < guaranteedTypes.length) {
                buildType = guaranteedTypes[i];
            } else {
                buildType = extraTypes[Math.floor(this.rng.n() * extraTypes.length)];
            }

            const bw = this._getBuildingWidth(buildType);
            const isLeft = i % 2 === 0;
            let bx;
            if (isLeft) { bx = posLeft - bw; posLeft = bx - 3 - Math.floor(this.rng.n() * 2); }
            else { bx = posRight; posRight = bx + bw + 3 + Math.floor(this.rng.n() * 2); }
            if (bx < 5 || bx + bw > WW - 5) continue;

            // Level the terrain under building
            let bsy = sy;
            for (let dx = -1; dx <= bw; dx++) {
                const hx = bx + dx; if (hx >= 0 && hx < WW) bsy = Math.max(bsy, Math.floor(this.heightMap[hx]));
            }
            // Fill foundation gaps
            for (let dx = -1; dx <= bw; dx++) {
                const hx = bx + dx; if (hx < 0 || hx >= WW) continue;
                for (let fy = Math.floor(this.heightMap[hx]); fy <= bsy; fy++) {
                    if (this.gt(hx, fy) === T.AIR || NONSOLID_PASS.has(this.gt(hx, fy))) this.st(hx, fy, style.floor);
                }
            }

            buildings.push({ x: bx, w: bw });
            buildingInfo.push({ type: buildType, x: bx, w: bw, sy: bsy });

            switch (buildType) {
                case 0: this._buildVillageHouse(bx, bsy, bw, 5 + Math.floor(this.rng.n() * 2), style); break;
                case 1: this._buildVillageTavern(bx, bsy, style); break;
                case 2: this._buildVillageBlacksmith(bx, bsy, style); break;
                case 3: this._buildVillageWatchtower(bx, bsy, style); break;
                case 4: this._buildVillageHouse(bx, bsy, bw, 6, style); break;
                case 5: this._buildVillageFarm(bx, bsy, style); break;
                case 6: this._buildVillageMarket(bx, bsy, style); break;
                case 7: this._buildVillageLibrary(bx, bsy, style); break;
                case 8: this._buildVillageChapel(bx, bsy, style); break;
                case 9: this._buildVillageBakery(bx, bsy, style); break;
                case 10: this._buildVillageStable(bx, bsy, style); break;
            }
        }

        // Cobblestone paths connecting everything
        const minX = Math.min(...buildings.map(b => b.x)) - 3;
        const maxX = Math.max(...buildings.map(b => b.x + b.w)) + 3;
        for (let px = minX; px <= maxX; px++) {
            if (px < 0 || px >= WW) continue;
            const psy = Math.floor(this.heightMap[px]);
            if (this.gt(px, psy) !== T.AIR && !NONSOLID_PASS.has(this.gt(px, psy))) {
                this.st(px, psy, style.path);
            }
        }

        // Village entrance arches
        for (const edge of [minX - 1, maxX + 1]) {
            if (edge < 2 || edge >= WW - 2) continue;
            const esy = Math.floor(this.heightMap[edge]);
            // Entrance pillars
            for (let fy = 0; fy < 4; fy++) {
                if (this.gt(edge, esy - 1 - fy) === T.AIR) this.st(edge, esy - 1 - fy, style.pillar);
                if (this.gt(edge + 1, esy - 1 - fy) === T.AIR && fy >= 3) this.st(edge + 1, esy - 1 - fy, style.pillar);
            }
            // Archway on top
            if (this.gt(edge, esy - 5) === T.AIR) this.st(edge, esy - 5, T.ARCHWAY);
            // Sign
            if (edge === minX - 1 && this.gt(edge - 1, esy - 1) === T.AIR) this.st(edge - 1, esy - 1, T.VILLSIGN);
        }

        // Fences along edges with gaps for paths
        for (const edge of [minX - 2, maxX + 2]) {
            if (edge < 1 || edge >= WW - 1) continue;
            const esy = Math.floor(this.heightMap[edge]);
            for (let fy = 0; fy < 3; fy++) {
                if (this.gt(edge, esy - 1 - fy) === T.AIR) this.st(edge, esy - 1 - fy, T.FENCE);
            }
        }

        // Scattered lanterns + benches along path
        for (let px = minX; px <= maxX; px += 5 + Math.floor(this.rng.n() * 3)) {
            if (px < 0 || px >= WW) continue;
            const psy = Math.floor(this.heightMap[px]);
            if (this.gt(px, psy - 1) === T.AIR) this.st(px, psy - 1, T.LANTERN);
            // Bench next to lantern sometimes
            if (this.rng.n() > 0.5 && px + 1 < WW && this.gt(px + 1, psy - 1) === T.AIR) {
                this.st(px + 1, psy - 1, T.BENCH);
            }
        }

        // Flower beds near buildings
        for (const b of buildings) {
            if (this.rng.n() > 0.4) continue;
            const fbx = b.x - 2;
            if (fbx > 0 && fbx < WW) {
                const fby = Math.floor(this.heightMap[fbx]);
                if (this.gt(fbx, fby - 1) === T.AIR) {
                    const fl = [T.FLOWER_R, T.FLOWER_Y, T.FLOWER_B, T.FLOWER_P, T.FLOWER_W];
                    this.st(fbx, fby - 1, fl[Math.floor(this.rng.n() * fl.length)]);
                }
            }
        }

        return { cx, sy, biome, style, buildings: buildingInfo, minX, maxX };
    }

    _getBuildingWidth(type) {
        switch (type) {
            case 1: return 12; // tavern
            case 3: return 4;  // watchtower
            case 5: return 10; // farm (wider)
            case 7: return 10; // library
            case 8: return 8;  // chapel
            case 9: return 8;  // bakery
            case 10: return 10; // stable
            default: return 8 + Math.floor(Math.random() * 3); // houses
        }
    }

    _buildVillagePlaza(x, sy, style) {
        // Wider plaza area
        for (let dx = -3; dx <= 3; dx++) {
            if (x + dx < 0 || x + dx >= WW) continue;
            this.st(x + dx, sy, style.path);
        }
        // Central well/fountain
        for (let dx = -2; dx <= 2; dx++) { this.st(x + dx, sy, T.STONE); this.st(x + dx, sy - 3, T.STONE); }
        this.st(x - 2, sy - 1, T.STONE); this.st(x - 2, sy - 2, T.STONE);
        this.st(x + 2, sy - 1, T.STONE); this.st(x + 2, sy - 2, T.STONE);
        for (let dy = 1; dy <= 6; dy++) { this.st(x - 1, sy + dy, T.STONE); this.st(x + 1, sy + dy, T.STONE); this.st(x, sy + dy, T.WATER); this.sll(x, sy + dy, 1.0); }
        // Roof structure over well
        this.st(x, sy - 4, T.WELLROOF); this.st(x - 1, sy - 4, T.WELLROOF); this.st(x + 1, sy - 4, T.WELLROOF);
        this.st(x - 2, sy - 4, T.WOOD); this.st(x + 2, sy - 4, T.WOOD);
        // Decorative elements around plaza
        if (this.gt(x - 3, sy - 1) === T.AIR) this.st(x - 3, sy - 1, T.FLOWERPOT);
        if (this.gt(x + 3, sy - 1) === T.AIR) this.st(x + 3, sy - 1, T.FLOWERPOT);
        // Benches on plaza
        if (this.gt(x - 4, sy - 1) === T.AIR) this.st(x - 4, sy - 1, T.BENCH);
        if (this.gt(x + 4, sy - 1) === T.AIR) this.st(x + 4, sy - 1, T.BENCH);
    }

    _buildVillageHouse(x, sy, bw, bh, style) {
        // Foundation
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy, style.floor);
        // Walls
        for (let dy = 0; dy <= bh; dy++) {
            this.st(x - 1, sy - dy, style.wall);
            this.st(x + bw, sy - dy, style.wall);
        }
        // Ceiling
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy - bh, style.wall);
        // Interior — clear and fill background
        for (let dx = 0; dx < bw; dx++) for (let dy = 1; dy < bh; dy++) {
            this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, style.wall);
        }
        // Roof (A-frame) with extended eaves
        const roofPeak = Math.floor(bw / 2) + 2;
        for (let i = 0; i <= roofPeak; i++) {
            this.st(x - 2 + i, sy - bh - i, style.roofTile || style.roof);
            this.st(x + bw + 1 - i, sy - bh - i, style.roofTile || style.roof);
        }
        // Door with step
        const doorX = x + Math.floor(bw / 2);
        this.st(doorX, sy - 1, T.VDOOR); this.st(doorX, sy - 2, T.VDOOR);
        // Windows with window boxes
        if (bw > 5) {
            this.st(x + 1, sy - 3, T.GLASS); this.st(x + bw - 2, sy - 3, T.GLASS);
            // Window boxes (flower boxes under windows)
            if (this.rng.n() > 0.4) this.st(x + 1, sy - 2, T.WINDOWBOX);
            if (this.rng.n() > 0.4) this.st(x + bw - 2, sy - 2, T.WINDOWBOX);
        }
        // Interior furniture — more variety
        this.st(x + 1, sy - 1, T.CHEST);
        this.st(x + 1, sy - bh + 1, T.TORCH);
        this.st(x + bw - 2, sy - bh + 1, T.TORCH);
        // Random furniture
        const furn = this.rng.n();
        if (furn > 0.7) { this.st(x + bw - 2, sy - 1, T.BARREL); this.st(x + bw - 3, sy - 1, T.CRATE); }
        else if (furn > 0.4) { this.st(x + bw - 2, sy - 1, T.BOOKSHELF); this.st(x + bw - 2, sy - 2, T.BOOKSHELF); }
        else { this.st(x + bw - 2, sy - 1, T.BARREL); }
        if (this.rng.n() > 0.5) this.st(x + 2, sy - 1, T.FLOWERPOT);
        // Carpet in larger houses
        if (bw > 6) {
            for (let dx = 3; dx < bw - 3; dx++) this.st(x + dx, sy, T.CARPET);
        }
        // Chimney
        if (this.rng.n() > 0.3) {
            const chimX = x + bw - 1;
            for (let cy = bh + 1; cy <= bh + 3; cy++) this.st(chimX, sy - cy, T.CHIMNEY);
        }
    }

    _buildVillageTavern(x, sy, style) {
        const bw = 12, bh = 7;
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy, style.floor);
        for (let dy = 0; dy <= bh; dy++) {
            this.st(x - 1, sy - dy, style.wall); this.st(x + bw, sy - dy, style.wall);
        }
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy - bh, style.wall);
        for (let dx = 0; dx < bw; dx++) for (let dy = 1; dy < bh; dy++) {
            this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, style.wall);
        }
        // Roof
        for (let i = 0; i <= Math.floor(bw / 2) + 2; i++) {
            this.st(x - 2 + i, sy - bh - i, style.roofTile || style.roof);
            this.st(x + bw + 1 - i, sy - bh - i, style.roofTile || style.roof);
        }
        // Double door
        this.st(x + 5, sy - 1, T.VDOOR); this.st(x + 5, sy - 2, T.VDOOR);
        this.st(x + 6, sy - 1, T.VDOOR); this.st(x + 6, sy - 2, T.VDOOR);
        // Windows
        this.st(x + 2, sy - 4, T.GLASS); this.st(x + 3, sy - 4, T.GLASS);
        this.st(x + bw - 4, sy - 4, T.GLASS); this.st(x + bw - 3, sy - 4, T.GLASS);
        // Bar counter with stools (benches)
        for (let dx = 1; dx <= 4; dx++) this.st(x + dx, sy - 1, T.PLANK);
        this.st(x + 5, sy - 1, T.BENCH); // stool at bar
        // Barrels behind counter
        this.st(x + 1, sy - 2, T.BARREL); this.st(x + 2, sy - 2, T.BARREL);
        this.st(x + 3, sy - 2, T.CRATE);
        // Dining area — benches
        this.st(x + 8, sy - 1, T.BENCH); this.st(x + 9, sy - 1, T.PLANK); this.st(x + 10, sy - 1, T.BENCH);
        // Bookshelves (tavern board)
        this.st(x + bw - 2, sy - 1, T.BOOKSHELF); this.st(x + bw - 2, sy - 2, T.BOOKSHELF);
        // Lighting — chandeliers (lanterns on chains)
        this.st(x + 3, sy - bh + 1, T.CHAIN); this.st(x + 3, sy - bh + 2, T.LANTERN);
        this.st(x + bw - 4, sy - bh + 1, T.CHAIN); this.st(x + bw - 4, sy - bh + 2, T.LANTERN);
        // Loot
        this.st(x + bw - 3, sy - 1, T.CHEST);
        // Chimney with stove
        this.st(x + 1, sy - 3, T.STOVE);
        for (let cy = bh + 1; cy <= bh + 4; cy++) this.st(x + bw - 1, sy - cy, T.CHIMNEY);
        // Carpet
        for (let dx = 5; dx < bw - 2; dx++) this.st(x + dx, sy, T.CARPET);
    }

    _buildVillageBlacksmith(x, sy, style) {
        const bw = 9, bh = 6;
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy, style.floor);
        for (let dy = 0; dy <= bh; dy++) { this.st(x - 1, sy - dy, style.wall); this.st(x + bw, sy - dy, style.wall); }
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy - bh, style.wall);
        for (let dx = 0; dx < bw; dx++) for (let dy = 1; dy < bh; dy++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, style.wall); }
        // Roof
        for (let i = 0; i <= Math.floor(bw / 2) + 2; i++) {
            this.st(x - 2 + i, sy - bh - i, style.roofTile || style.roof); this.st(x + bw + 1 - i, sy - bh - i, style.roofTile || style.roof);
        }
        // Door
        this.st(x + Math.floor(bw / 2), sy - 1, T.VDOOR); this.st(x + Math.floor(bw / 2), sy - 2, T.VDOOR);
        // Forge area (lava + anvil + crates)
        this.st(x + 1, sy - 1, T.LAVA); this.sll(x + 1, sy - 1, 1.0);
        this.st(x + 1, sy, T.STONE); this.st(x + 2, sy, T.STONE);
        this.st(x + 3, sy - 1, T.FORGEANVIL);
        // Forge surround
        this.st(x + 1, sy - 2, T.STONE); // back wall of forge
        // Chimney (taller for blacksmith)
        for (let cy = bh + 1; cy <= bh + 6; cy++) this.st(x + 1, sy - cy, T.CHIMNEY);
        // Tool rack (crates + barrels)
        this.st(x + bw - 2, sy - 1, T.CHEST);
        this.st(x + bw - 3, sy - 1, T.BARREL);
        this.st(x + bw - 3, sy - 2, T.CRATE);
        // Lighting
        this.st(x + 2, sy - bh + 1, T.TORCH); this.st(x + bw - 2, sy - bh + 1, T.TORCH);
        // Window
        this.st(x + bw - 2, sy - 4, T.GLASS);
        // Outdoor anvil display
        if (x - 2 > 0 && this.gt(x - 2, sy - 1) === T.AIR) this.st(x - 2, sy - 1, T.FORGEANVIL);
    }

    _buildVillageWatchtower(x, sy, style) {
        const bw = 4, bh = 16; // taller tower
        for (let dy = 0; dy <= bh; dy++) { this.st(x - 1, sy - dy, style.wall); this.st(x + bw, sy - dy, style.wall); }
        for (let dx = 0; dx < bw; dx++) for (let dy = 1; dy < bh; dy++) {
            this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, style.wall);
        }
        // Floor platforms at intervals
        for (let dx = -1; dx <= bw; dx++) {
            this.st(x + dx, sy, style.floor);
            this.st(x + dx, sy - Math.floor(bh / 3), style.floor);
            this.st(x + dx, sy - Math.floor(bh * 2 / 3), style.floor);
            this.st(x + dx, sy - bh, style.floor);
        }
        // Crenellations at top
        for (let dx = -2; dx <= bw + 1; dx++) this.st(x + dx, sy - bh - 1, style.wall);
        this.st(x - 2, sy - bh - 2, style.wall); this.st(x + bw + 1, sy - bh - 2, style.wall);
        this.st(x, sy - bh - 2, style.wall); this.st(x + bw - 1, sy - bh - 2, style.wall);
        // Windows at each level
        this.st(x + 1, sy - Math.floor(bh / 3) - 2, T.GLASS);
        this.st(x + 2, sy - Math.floor(bh * 2 / 3) - 2, T.GLASS);
        // Ladder up entire height
        for (let dy = 1; dy < bh; dy++) this.st(x + 1, sy - dy, T.LADDER);
        // Torch at top + bell
        this.st(x, sy - bh - 3, T.TORCH); this.st(x + bw - 1, sy - bh - 3, T.TORCH);
        this.st(x + 2, sy - bh - 2, T.BELLTOWER);
        // Chest and supplies at top
        this.st(x + 2, sy - bh + 1, T.CHEST);
        this.st(x + 3, sy - bh + 1, T.CRATE);
    }

    _buildVillageFarm(x, sy, style) {
        const bw = 10;
        // Fenced area with gate
        for (let dx = -1; dx <= bw; dx++) { this.st(x + dx, sy, style.path); }
        // Fences on sides
        for (let dx = -1; dx <= bw; dx += bw + 1) {
            for (let fy = 0; fy < 2; fy++) if (this.gt(x + dx, sy - 1 - fy) === T.AIR) this.st(x + dx, sy - 1 - fy, T.FENCE);
        }
        // Back fence
        for (let fy = 0; fy < 2; fy++) {
            for (let dx = 0; dx < bw; dx++) {
                // Leave a gate gap
                if (dx === Math.floor(bw / 2)) continue;
                // Only top row fence
                if (fy === 0 && this.rng.n() > 0.3) continue;
            }
        }
        // Crop rows with variety
        const crops = [T.TALLGRASS, T.FLOWER_Y, T.FLOWER_R, T.FLOWER_B, T.FLOWER_W, T.FERN];
        for (let dx = 1; dx < bw - 1; dx++) {
            if (this.gt(x + dx, sy - 1) !== T.AIR) continue;
            // Plant in rows
            const row = dx % 3;
            if (row === 0) this.st(x + dx, sy - 1, crops[Math.floor(this.rng.n() * 3)]);
            else if (row === 1) this.st(x + dx, sy - 1, crops[3 + Math.floor(this.rng.n() * 3)]);
            // row 2 = path between rows
        }
        // Hay storage corner
        this.st(x, sy - 1, T.HAY); this.st(x, sy - 2, T.HAY);
        this.st(x + 1, sy - 1, T.HAY);
        this.st(x + bw - 1, sy - 1, T.HAY);
        // Crate & barrel
        this.st(x + bw - 2, sy - 1, T.CRATE);
        // Water trough
        if (x + bw + 1 < WW && this.gt(x + bw + 1, sy - 1) === T.AIR) {
            this.st(x + bw + 1, sy, T.STONE);
        }
    }

    _buildVillageMarket(x, sy, style) {
        const bw = 7, bh = 4;
        // Open-sided stall with pillars
        for (let dy = 1; dy <= bh; dy++) {
            this.st(x, sy - dy, style.pillar);
            this.st(x + bw - 1, sy - dy, style.pillar);
        }
        // Colorful canopy roof
        for (let dx = -1; dx <= bw; dx++) {
            this.st(x + dx, sy - bh, T.CANOPY);
            this.st(x + dx, sy - bh - 1, style.roofTile || style.roof);
        }
        // Counter with goods
        for (let dx = 1; dx < bw - 1; dx++) this.st(x + dx, sy - 1, T.PLANK);
        // Variety of goods displayed
        this.st(x + 1, sy - 2, T.BARREL);
        this.st(x + 2, sy - 2, T.CRATE);
        this.st(x + 4, sy - 2, T.FLOWERPOT);
        this.st(x + 5, sy - 2, T.BARREL);
        // Lantern
        this.st(x + Math.floor(bw / 2), sy - bh + 1, T.LANTERN);
        // Floor
        for (let dx = 0; dx < bw; dx++) this.st(x + dx, sy, style.path);
        // Sign
        if (this.gt(x - 1, sy - 1) === T.AIR) this.st(x - 1, sy - 1, T.VILLSIGN);
    }

    // === NEW BUILDING TYPES ===
    _buildVillageLibrary(x, sy, style) {
        const bw = 10, bh = 7;
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy, style.floor);
        for (let dy = 0; dy <= bh; dy++) { this.st(x - 1, sy - dy, style.wall); this.st(x + bw, sy - dy, style.wall); }
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy - bh, style.wall);
        for (let dx = 0; dx < bw; dx++) for (let dy = 1; dy < bh; dy++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, style.wall); }
        // Tall pointed roof
        for (let i = 0; i <= Math.floor(bw / 2) + 3; i++) {
            this.st(x - 2 + i, sy - bh - i, style.roofTile || style.roof);
            this.st(x + bw + 1 - i, sy - bh - i, style.roofTile || style.roof);
        }
        // Door
        this.st(x + Math.floor(bw / 2), sy - 1, T.VDOOR); this.st(x + Math.floor(bw / 2), sy - 2, T.VDOOR);
        // Bookshelves along walls — floor to ceiling
        for (let dy = 1; dy < bh - 1; dy++) {
            this.st(x + 1, sy - dy, T.BOOKSHELF);
            this.st(x + bw - 2, sy - dy, T.BOOKSHELF);
        }
        // Reading desk (planks)
        this.st(x + 4, sy - 1, T.PLANK); this.st(x + 5, sy - 1, T.PLANK);
        this.st(x + 3, sy - 1, T.BENCH); this.st(x + 6, sy - 1, T.BENCH);
        // Globe/decoration
        this.st(x + 2, sy - 1, T.FLOWERPOT);
        // Lanterns for reading light
        this.st(x + 3, sy - bh + 1, T.LANTERN); this.st(x + bw - 4, sy - bh + 1, T.LANTERN);
        // Windows — larger
        this.st(x + 3, sy - 4, T.GLASS); this.st(x + 4, sy - 4, T.GLASS);
        this.st(x + bw - 5, sy - 4, T.GLASS); this.st(x + bw - 4, sy - 4, T.GLASS);
        // Chest with scroll loot
        this.st(x + bw - 3, sy - 1, T.CHEST);
        // Carpet
        for (let dx = 3; dx <= 6; dx++) this.st(x + dx, sy, T.CARPET);
    }

    _buildVillageChapel(x, sy, style) {
        const bw = 8, bh = 8;
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy, style.floor);
        for (let dy = 0; dy <= bh; dy++) { this.st(x - 1, sy - dy, style.wall); this.st(x + bw, sy - dy, style.wall); }
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy - bh, style.wall);
        for (let dx = 0; dx < bw; dx++) for (let dy = 1; dy < bh; dy++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, style.wall); }
        // Steep pointed roof (chapel style)
        for (let i = 0; i <= Math.floor(bw / 2) + 3; i++) {
            this.st(x - 2 + i, sy - bh - i, style.roofTile || style.roof);
            this.st(x + bw + 1 - i, sy - bh - i, style.roofTile || style.roof);
        }
        // Bell tower spike
        const mid = x + Math.floor(bw / 2);
        for (let i = 0; i < 3; i++) this.st(mid, sy - bh - Math.floor(bw / 2) - 3 - i, style.wall);
        this.st(mid, sy - bh - Math.floor(bw / 2) - 6, T.BELLTOWER);
        // Door
        this.st(mid, sy - 1, T.VDOOR); this.st(mid, sy - 2, T.VDOOR);
        // Stained glass windows
        this.st(x + 1, sy - 5, T.GLASS); this.st(x + 2, sy - 5, T.GLASS);
        this.st(x + bw - 3, sy - 5, T.GLASS); this.st(x + bw - 2, sy - 5, T.GLASS);
        // Altar area
        this.st(x + Math.floor(bw / 2), sy - 1, T.PLANK);
        this.st(x + Math.floor(bw / 2) - 1, sy - 1, T.TORCH);
        this.st(x + Math.floor(bw / 2) + 1, sy - 1, T.TORCH);
        // Pews (benches)
        this.st(x + 2, sy - 1, T.BENCH); this.st(x + bw - 3, sy - 1, T.BENCH);
        this.st(x + 2, sy - 2, T.BENCH); this.st(x + bw - 3, sy - 2, T.BENCH);
        // Carpet runner
        for (let dx = 3; dx < bw - 3; dx++) this.st(x + dx, sy, T.CARPET);
        // Chandeliers
        this.st(x + 2, sy - bh + 1, T.CHAIN); this.st(x + 2, sy - bh + 2, T.LANTERN);
        this.st(x + bw - 3, sy - bh + 1, T.CHAIN); this.st(x + bw - 3, sy - bh + 2, T.LANTERN);
    }

    _buildVillageBakery(x, sy, style) {
        const bw = 8, bh = 6;
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy, style.floor);
        for (let dy = 0; dy <= bh; dy++) { this.st(x - 1, sy - dy, style.wall); this.st(x + bw, sy - dy, style.wall); }
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy - bh, style.wall);
        for (let dx = 0; dx < bw; dx++) for (let dy = 1; dy < bh; dy++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, style.wall); }
        // Roof
        for (let i = 0; i <= Math.floor(bw / 2) + 2; i++) {
            this.st(x - 2 + i, sy - bh - i, style.roofTile || style.roof);
            this.st(x + bw + 1 - i, sy - bh - i, style.roofTile || style.roof);
        }
        // Door
        this.st(x + Math.floor(bw / 2), sy - 1, T.VDOOR); this.st(x + Math.floor(bw / 2), sy - 2, T.VDOOR);
        // Oven/stove area
        this.st(x + 1, sy - 1, T.STOVE); this.st(x + 1, sy - 2, T.STONE);
        this.st(x + 2, sy - 1, T.STOVE);
        // Chimney from oven
        for (let cy = bh + 1; cy <= bh + 4; cy++) this.st(x + 1, sy - cy, T.CHIMNEY);
        // Counter and display
        this.st(x + 4, sy - 1, T.PLANK); this.st(x + 5, sy - 1, T.PLANK);
        this.st(x + 4, sy - 2, T.CRATE); // bread display
        // Storage
        this.st(x + bw - 2, sy - 1, T.BARREL);
        this.st(x + bw - 3, sy - 1, T.BARREL);
        this.st(x + bw - 2, sy - 2, T.CRATE);
        // Lighting
        this.st(x + 3, sy - bh + 1, T.LANTERN); this.st(x + bw - 3, sy - bh + 1, T.LANTERN);
        // Window
        this.st(x + bw - 2, sy - 4, T.GLASS);
        this.st(x + 3, sy - 4, T.GLASS);
    }

    _buildVillageStable(x, sy, style) {
        const bw = 10, bh = 5;
        for (let dx = -1; dx <= bw; dx++) this.st(x + dx, sy, style.floor);
        // Open front — only back and side walls
        for (let dy = 0; dy <= bh; dy++) {
            this.st(x - 1, sy - dy, style.wall);
            this.st(x + bw, sy - dy, style.wall);
        }
        // Back wall
        for (let dx = 0; dx < bw; dx++) { this.st(x + dx, sy - bh, style.wall); }
        // Interior
        for (let dx = 0; dx < bw; dx++) for (let dy = 1; dy < bh; dy++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, style.wall); }
        // Sloped roof
        for (let dx = -1; dx <= bw; dx++) {
            this.st(x + dx, sy - bh, style.roofTile || style.roof);
            this.st(x + dx, sy - bh - 1, style.roofTile || style.roof);
        }
        // Stall dividers (fences inside)
        for (let s = 0; s < 3; s++) {
            const sx = x + 3 + s * 3;
            if (sx < x + bw - 1) {
                this.st(sx, sy - 1, T.FENCE); this.st(sx, sy - 2, T.FENCE);
            }
        }
        // Hay in stalls
        for (let s = 0; s < 3; s++) {
            const sx = x + 1 + s * 3;
            if (sx < x + bw - 1) {
                this.st(sx, sy - 1, T.HAY);
                if (this.rng.n() > 0.5) this.st(sx + 1, sy - 1, T.HAY);
            }
        }
        // Water trough
        this.st(x + bw - 2, sy - 1, T.BARREL);
        // Lanterns
        this.st(x + 2, sy - bh + 1, T.LANTERN); this.st(x + bw - 3, sy - bh + 1, T.LANTERN);
    }

    genSurfaceStructures() {
        for (let x = 20; x < WW - 20; x++) {
            const b = this.biomeMap[x], sy = Math.floor(this.heightMap[x]);
            if (this.gt(x, sy - 1) !== T.AIR) continue; const r = this.rng.n();
            if (b === 0 && r > 0.996) this.buildIgloo(x, sy);
            if (b === 1 && r > 0.994) this.buildCabin(x, sy);
            if (b === 2 && r > 0.997) this.buildWell(x, sy);
            if ((b === 3) && r > 0.994) this.buildCabin(x, sy);
            if (b === 5 && r > 0.996) this.buildPyramid(x, sy);
            if (b === 5 && r > 0.992 && r <= 0.996) this.buildOasis(x, sy);
            if (b === 7 && r > 0.995) this.buildJungleTemple(x, sy);
            if (b === 8 && r > 0.994) this.buildWitchHut(x, sy);
            if (b === 9 && r > 0.993) this.buildShipwreck(x, sy);
            if (b === 10 && r > 0.99) this.buildObelisk(x, sy);
            if (b === 11 && r > 0.993) this.buildFountain(x, sy);
            if (b === 13 && r > 0.995) this.buildObsidianTower(x, sy);
            if (b === 14 && r > 0.985) {
                const h2 = 6 + Math.floor(this.rng.n() * 6), w2 = 3 + Math.floor(this.rng.n() * 2);
                for (let dy = 0; dy < h2; dy++) { const cw = Math.floor(w2 * (1 - dy / h2 * 0.6)); for (let dx = -cw; dx <= cw; dx++)this.st(x + dx, sy - dy, T.TERMITEHILL); }
            }
            if (b === 14 && r > 0.993 && r <= 0.995) this.buildBaobab(x, sy);
            if (b === 15 && r > 0.97) {
                const h2 = 8 + Math.floor(this.rng.n() * 5);
                for (let dy = 1; dy <= h2; dy++)this.sb(x, sy - dy, T.GIANTMUSHSTEM);
                for (let dx = -4; dx <= 4; dx++) { this.st(x + dx, sy - h2 - 1, T.GIANTMUSHCAP); if (Math.abs(dx) < 4) this.st(x + dx, sy - h2 - 2, T.GIANTMUSHCAP); }
            }
            if (b === 15 && r > 0.996) this.buildDarkAltar(x, sy);
            if (b === 16 && r > 0.993) this.buildBeehive(x, sy);
            if (b === 17 && r > 0.994) this.buildCanyonBridge(x, sy);
            if (b === 18 && r > 0.995) this.buildFrozenShip(x, sy);
            // Phase 3 biome structures
            if (b === 23 && r > 0.993) this.buildGiantSkull(x, sy);
            if (b === 25 && r > 0.99) this.buildPulsatingHeart(x, sy);
            if (b === 26 && r > 0.993) this.buildFrozenTitan(x, sy);
            if (b === 27 && r > 0.992) this.buildCrystalLighthouse(x, sy);
            if (b === 10 && r > 0.992 && r <= 0.996) this.buildDarkObeliskCluster(x, sy);
            // Phase 4 biome structures
            if (b === 28 && r > 0.993) this.buildTidalLighthouse(x, sy);
            if (b === 28 && r > 0.996) this.buildShipwreck(x, sy);
            if (b === 29 && r > 0.993) this.buildStormObservatory(x, sy);
            // Structures for previously empty biomes
            if (b === 19 && r > 0.992) this.buildCrystalShrine(x, sy);
            if (b === 20 && r > 0.994) this.buildAbyssGateway(x, sy);
            if (b === 21 && r > 0.993) this.buildEmberForge(x, sy);
            if (b === 22 && r > 0.991) this.buildRuinedArch(x, sy);
            if (b === 23 && r > 0.992) this.buildBloodAltar(x, sy);
            if (b === 24 && r > 0.993) this.buildShimmerWell(x, sy);
            if (b === 30 && r > 0.992) this.buildFleshMound(x, sy);
        }
    }

    buildWell(x, sy) {
        for (let dx = -2; dx <= 2; dx++) { this.st(x + dx, sy, T.STONE); this.st(x + dx, sy - 3, T.STONE); }
        this.st(x - 2, sy - 1, T.STONE); this.st(x - 2, sy - 2, T.STONE); this.st(x + 2, sy - 1, T.STONE); this.st(x + 2, sy - 2, T.STONE);
        for (let dy = 1; dy <= 6; dy++) { this.st(x - 1, sy + dy, T.STONE); this.st(x + 1, sy + dy, T.STONE); this.st(x, sy + dy, T.WATER); this.sll(x, sy + dy, 1.0); }
        this.st(x, sy - 4, T.WOOD); this.st(x - 2, sy - 4, T.WOOD); this.st(x + 2, sy - 4, T.WOOD);
    }
    buildCabin(x, sy) {
        const w = 7, h = 5;
        for (let dx = -1; dx <= w; dx++) { this.st(x + dx, sy, T.WOOD); this.st(x + dx, sy - h, T.WOOD); }
        for (let dy = 0; dy < h; dy++) { this.st(x - 1, sy - dy, T.WOOD); this.st(x + w, sy - dy, T.WOOD); }
        for (let dx = 0; dx < w; dx++)for (let dy = 1; dy < h; dy++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.WOOD); }
        this.st(x + Math.floor(w / 2), sy - 1, T.AIR); this.st(x + Math.floor(w / 2), sy - 2, T.AIR);
        this.st(x + 2, sy - 1, T.CHEST); this.st(x + 1, sy - h + 1, T.TORCH); this.st(x + w - 2, sy - h + 1, T.TORCH);
        for (let i = 0; i <= Math.floor(w / 2) + 2; i++) { this.st(x - 2 + i, sy - h - i, T.WOOD); this.st(x + w + 1 - i, sy - h - i, T.WOOD); }
    }
    buildPyramid(x, sy) {
        const size = 14;
        for (let row = 0; row < size; row++) { const w2 = size - row; for (let dx = -w2; dx <= w2; dx++)this.st(x + dx, sy - row, T.SANDSTONE); }
        for (let row = 2; row < size - 2; row++) {
            const w2 = Math.max(0, size - row - 3);
            for (let dx = -w2; dx <= w2; dx++) { this.st(x + dx, sy - row, T.AIR); this.sb(x + dx, sy - row, T.SANDSTONE); }
        }
        this.st(x, sy - 3, T.CHEST); this.st(x - 2, sy - 3, T.TORCH); this.st(x + 2, sy - 3, T.TORCH);
        this.st(x - 1, sy - 2, T.SPIKE); this.st(x + 1, sy - 2, T.SPIKE);
    }
    buildShipwreck(x, sy) {
        const len = 12;
        for (let dx = 0; dx < len; dx++) {
            const h2 = Math.floor(3 + Math.sin(dx / len * Math.PI) * 3);
            for (let dy = 0; dy < h2; dy++)this.st(x + dx, sy - dy, T.WOOD);
            if (dx === 0 || dx === len - 1) for (let dy = 0; dy < h2 + 2; dy++)this.st(x + dx, sy - dy, T.WOOD);
        }
        for (let dx = 2; dx < len - 2; dx++)for (let dy = 1; dy < 3; dy++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.WOOD); }
        this.st(x + Math.floor(len / 2), sy - 1, T.CHEST);
    }
    buildObelisk(x, sy) {
        for (let dy = 0; dy < 10; dy++) { this.st(x, sy - dy, T.CORSTONE); this.st(x - 1, sy - dy, dy < 7 ? T.CORSTONE : T.AIR); this.st(x + 1, sy - dy, dy < 7 ? T.CORSTONE : T.AIR); }
        this.st(x, sy - 10, T.CRYSTAL); this.st(x, sy - 11, T.CRYSTAL);
    }
    buildFountain(x, sy) {
        for (let dx = -3; dx <= 3; dx++) {
            this.st(x + dx, sy, T.HALSTONE); this.st(x + dx, sy - 1, Math.abs(dx) > 1 ? T.HALSTONE : T.WATER);
            if (Math.abs(dx) > 1) this.sll(x + dx, sy - 1, 0);
        }
        this.st(x, sy - 1, T.WATER); this.sll(x, sy - 1, 1.0); this.st(x, sy - 2, T.RCRYSTAL); this.st(x, sy - 3, T.RCRYSTAL);
    }

    // === NEW Surface Structures ===
    buildIgloo(x, sy) {
        const r = 4;
        for (let dx = -r; dx <= r; dx++)for (let dy = -r; dy <= 0; dy++) {
            const d = Math.sqrt(dx * dx + dy * dy);
            if (d <= r && d > r - 1.5) this.st(x + dx, sy + dy, T.SNOW);
            else if (d < r - 1.5 && dy > -r + 1) { this.st(x + dx, sy + dy, T.AIR); this.sb(x + dx, sy + dy, T.ICE); }
        }
        this.st(x - r + 1, sy, T.AIR); this.st(x - r + 1, sy - 1, T.AIR);
        this.st(x, sy - 1, T.CHEST); this.st(x + 1, sy - r + 2, T.TORCH);
    }
    buildOasis(x, sy) {
        for (let dx = -4; dx <= 4; dx++) {
            for (let dy = 0; dy <= 2; dy++) { if (Math.abs(dx) + dy < 5) { this.st(x + dx, sy + dy, T.WATER); this.sll(x + dx, sy + dy, 1.0); } }
            if (Math.abs(dx) <= 4) this.st(x + dx, sy + 3, T.SAND);
        }
        if (this.gt(x - 4, sy - 1) === T.AIR) {
            const h2 = 6; for (let dy = 1; dy <= h2; dy++)this.sb(x - 4, sy - dy, T.PALMWOOD);
            for (let dx = -3; dx <= 1; dx++) { this.st(x - 4 + dx, sy - h2, T.PALMLEAF); if (Math.abs(dx) < 2) this.st(x - 4 + dx, sy - h2 - 1, T.PALMLEAF); }
        }
        this.st(x - 2, sy - 1, T.FLOWER_Y); this.st(x + 2, sy - 1, T.FLOWER_R);
    }
    buildJungleTemple(x, sy) {
        const w = 10, h = 8;
        for (let dx = -1; dx <= w; dx++) { this.st(x + dx, sy, T.MOSSTONE); this.st(x + dx, sy - h, T.MOSSTONE); }
        for (let dy = 0; dy <= h; dy++) { this.st(x - 1, sy - dy, T.MOSSTONE); this.st(x + w, sy - dy, T.MOSSTONE); }
        for (let dx = 0; dx < w; dx++)for (let dy = 1; dy < h; dy++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.MOSSTONE); }
        this.st(x + Math.floor(w / 2), sy - 1, T.AIR); this.st(x + Math.floor(w / 2), sy - 2, T.AIR);
        this.st(x + 3, sy - 1, T.CHEST); this.st(x + 1, sy - h + 1, T.TORCH); this.st(x + w - 2, sy - h + 1, T.TORCH);
        for (let dy = 1; dy < h; dy++) { if (this.rng.n() > 0.5) this.st(x - 2, sy - dy, T.VINE); if (this.rng.n() > 0.5) this.st(x + w + 1, sy - dy, T.VINE); }
    }
    buildWitchHut(x, sy) {
        for (let dy = 0; dy < 6; dy++) { this.st(x - 3, sy + dy, T.WOOD); this.st(x + 3, sy + dy, T.WOOD); }
        for (let dx = -4; dx <= 4; dx++)this.st(x + dx, sy - 1, T.WOOD);
        for (let dy = -4; dy <= -1; dy++) { this.st(x - 4, sy + dy, T.WOOD); this.st(x + 4, sy + dy, T.WOOD); }
        for (let dx = -5; dx <= 5; dx++)this.st(x + dx, sy - 5, T.WOOD);
        for (let dx = -3; dx <= 3; dx++)for (let dy = -4; dy <= -2; dy++) { this.st(x + dx, sy + dy, T.AIR); this.sb(x + dx, sy + dy, T.WOOD); }
        this.st(x, sy - 2, T.CHEST); this.st(x - 2, sy - 4, T.TORCH); this.st(x + 2, sy - 4, T.LANTERN);
    }
    buildObsidianTower(x, sy) {
        for (let dy = 0; dy < 15; dy++) {
            this.st(x - 2, sy - dy, T.OBSIDIAN); this.st(x + 2, sy - dy, T.OBSIDIAN);
            for (let dx = -1; dx <= 1; dx++) { if (dy < 2 || dy > 12) this.st(x + dx, sy - dy, T.OBSIDIAN); else { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.OBSIDIAN); } }
        }
        this.st(x, sy - 1, T.LADDER); this.st(x, sy - 2, T.LADDER); this.st(x, sy - 3, T.LADDER);
        this.st(x, sy - 12, T.CHEST); this.st(x, sy - 14, T.EMBER); this.st(x - 2, sy - 14, T.EMBER); this.st(x + 2, sy - 14, T.EMBER);
    }
    buildBaobab(x, sy) {
        const h = 10 + Math.floor(this.rng.n() * 5), tw = 3;
        for (let dy = 1; dy <= h; dy++)for (let dx = -tw; dx <= tw; dx++)this.sb(x + dx, sy - dy, T.ACACIA);
        const cw = 6 + Math.floor(this.rng.n() * 3);
        for (let dx = -cw; dx <= cw; dx++)for (let dy = 0; dy < 3; dy++)
            if (Math.abs(dx) <= cw - dy && this.gt(x + dx, sy - h - dy) === T.AIR) this.st(x + dx, sy - h - dy, T.ACACLEAF);
    }
    buildDarkAltar(x, sy) {
        for (let dx = -3; dx <= 3; dx++) { this.st(x + dx, sy, T.MOSSTONE); if (Math.abs(dx) === 3) { this.st(x + dx, sy - 1, T.MOSSTONE); this.st(x + dx, sy - 2, T.MOSSTONE); } }
        this.st(x, sy - 1, T.DALTAR); this.st(x - 3, sy - 3, T.DTORCH); this.st(x + 3, sy - 3, T.DTORCH);
    }
    buildBeehive(x, sy) {
        for (let dx = -2; dx <= 2; dx++)for (let dy = -3; dy <= 0; dy++) {
            const d = Math.abs(dx) + Math.abs(dy); if (d <= 3) { this.st(x + dx, sy - 4 + dy, T.HIVE); }
        }
        this.st(x, sy - 7, T.HIVE); this.st(x, sy - 6, T.HIVE); this.st(x, sy - 5, T.HIVE);
        if (this.gt(x, sy - 1) === T.AIR) this.st(x, sy - 1, T.FLOWER_Y);
    }
    buildCanyonBridge(x, sy) {
        for (let dx = -8; dx <= 8; dx++) {
            this.st(x + dx, sy - 5, T.WOOD);
            if (Math.abs(dx) === 8) { for (let dy = 0; dy < 5; dy++)this.st(x + dx, sy - dy, T.WOOD); }
        }
    }
    buildFrozenShip(x, sy) {
        const len = 10;
        for (let dx = 0; dx < len; dx++) {
            const h2 = Math.floor(2 + Math.sin(dx / len * Math.PI) * 2);
            for (let dy = 0; dy < h2; dy++)this.st(x + dx, sy - dy, T.ICE);
            if (dx === 0 || dx === len - 1) for (let dy = 0; dy < h2 + 2; dy++)this.st(x + dx, sy - dy, T.ICE);
        }
        for (let dx = 2; dx < len - 2; dx++)for (let dy = 1; dy < 2; dy++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.ICE); }
        this.st(x + Math.floor(len / 2), sy - 1, T.FROZCHEST);
    }

    // === PHASE 3: UNIQUE STRUCTURES ===
    buildGiantSkull(x, sy) {
        // A massive skull structure made of bone blocks embedded in the Blood Plains
        const sw = 12, sh = 14;
        // Skull outline — oval cranium
        for (let dx = -sw; dx <= sw; dx++) for (let dy = -sh; dy <= 2; dy++) {
            const nx = x + dx, ny = sy + dy - 4;
            if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
            const ex = (dx * dx) / (sw * sw) + (dy * dy) / (sh * sh);
            if (ex <= 1 && ex > 0.65) this.st(nx, ny, T.GIANTSKULL);
            else if (ex <= 0.65) { this.st(nx, ny, T.AIR); this.sb(nx, ny, T.BONE); }
        }
        // Eye sockets — two elliptical holes
        for (let dx = -5; dx <= -1; dx++) for (let dy = -6; dy <= -2; dy++) {
            const nx = x + dx, ny = sy + dy;
            if (nx >= 2 && nx < WW - 2 && ny >= 2 && ny < WH - 2) {
                const ex = ((dx + 3) * (dx + 3)) / 9 + ((dy + 4) * (dy + 4)) / 9;
                if (ex <= 1) { this.st(nx, ny, T.AIR); if (this.rng.n() > 0.7) this.st(nx, ny, T.EMBER); }
            }
        }
        for (let dx = 1; dx <= 5; dx++) for (let dy = -6; dy <= -2; dy++) {
            const nx = x + dx, ny = sy + dy;
            if (nx >= 2 && nx < WW - 2 && ny >= 2 && ny < WH - 2) {
                const ex = ((dx - 3) * (dx - 3)) / 9 + ((dy + 4) * (dy + 4)) / 9;
                if (ex <= 1) { this.st(nx, ny, T.AIR); if (this.rng.n() > 0.7) this.st(nx, ny, T.EMBER); }
            }
        }
        // Nose hole
        for (let dx = -1; dx <= 1; dx++) for (let dy = -1; dy <= 1; dy++) {
            const nx = x + dx, ny = sy + dy + 1;
            if (nx >= 2 && nx < WW - 2) this.st(nx, ny, T.AIR);
        }
        // Jaw / teeth at bottom
        for (let dx = -8; dx <= 8; dx++) {
            const nx = x + dx, ny = sy + 3;
            if (nx >= 2 && nx < WW - 2 && ny < WH - 2) {
                this.st(nx, ny, T.GIANTSKULL);
                if (Math.abs(dx) % 3 === 0 && Math.abs(dx) < 8) this.st(nx, ny - 1, T.BONE);
            }
        }
        // Chest inside skull
        this.st(x, sy - 1, T.DCHEST);
        // Torches in eyes
        this.st(x - 3, sy - 4, T.TORCH); this.st(x + 3, sy - 4, T.TORCH);
    }

    buildPulsatingHeart(x, sy) {
        // A giant organic heart structure for the Fungal Abyss
        const hw = 8, hh = 10;
        for (let dx = -hw; dx <= hw; dx++) for (let dy = -hh; dy <= hh; dy++) {
            const nx = x + dx, ny = sy + dy - 6;
            if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
            // Heart shape equation
            const ndx = dx / hw, ndy = dy / hh;
            const heart = ndx * ndx + (ndy - 0.4 * Math.sqrt(Math.abs(ndx))) * (ndy - 0.4 * Math.sqrt(Math.abs(ndx)));
            if (heart <= 0.7) {
                if (heart > 0.45) this.st(nx, ny, T.HEARTBLOCK);
                else { this.st(nx, ny, T.AIR); this.sb(nx, ny, T.SPORESTONE); }
            }
        }
        // Arteries/veins radiating outward
        for (let i = 0; i < 4; i++) {
            const angle = (i / 4) * Math.PI * 2 + 0.3;
            for (let r = hw; r < hw + 8; r++) {
                const vx = x + Math.floor(Math.cos(angle) * r);
                const vy = sy - 6 + Math.floor(Math.sin(angle) * r * 0.7);
                if (vx >= 2 && vx < WW - 2 && vy >= 2 && vy < WH - 2) this.st(vx, vy, T.PULSEVINE);
            }
        }
        this.st(x, sy - 6, T.DSPAWNER); this.st(x - 2, sy - 5, T.CHEST);
    }

    buildFrozenTitan(x, sy) {
        // A giant humanoid fossil partially buried in the Petrified Wastes
        // Torso (partially above ground)
        for (let dx = -6; dx <= 6; dx++) for (let dy = -14; dy <= 0; dy++) {
            const nx = x + dx, ny = sy + dy;
            if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
            // Torso shape
            const tw = 6 - Math.abs(dy + 7) * 0.3;
            if (Math.abs(dx) <= tw) {
                this.st(nx, ny, T.TITANLIMB);
                this.sb(nx, ny, T.PETRISTONE);
            }
        }
        // Head (above torso)
        for (let dx = -4; dx <= 4; dx++) for (let dy = -19; dy <= -14; dy++) {
            const nx = x + dx, ny = sy + dy;
            if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
            const ex = (dx * dx) / 16 + ((dy + 16.5) * (dy + 16.5)) / 6.25;
            if (ex <= 1) this.st(nx, ny, T.TITANLIMB);
        }
        // Eye sockets
        this.st(x - 2, sy - 17, T.AIR); this.st(x + 2, sy - 17, T.AIR);
        this.st(x - 2, sy - 17, T.TIMECRACK); this.st(x + 2, sy - 17, T.TIMECRACK);
        // Arms reaching out from ground
        for (let i = 0; i < 6; i++) { this.st(x - 8 + i, sy - i * 2, T.TITANLIMB); this.st(x + 8 - i, sy - i * 2 - 1, T.TITANLIMB); }
        this.st(x, sy - 10, T.CHEST);
    }

    buildCrystalLighthouse(x, sy) {
        // A tall prismatic tower on Prism Shores
        const tw = 3, th = 16;
        // Tower body
        for (let dx = -tw; dx <= tw; dx++) for (let dy = -th; dy <= 0; dy++) {
            const nx = x + dx, ny = sy + dy;
            if (nx < 2 || nx >= WW - 2 || ny < 2 || ny >= WH - 2) continue;
            const w2 = Math.max(1, tw - Math.floor(Math.abs(dy) / 5));
            if (Math.abs(dx) <= w2) {
                this.st(nx, ny, T.LIGHTHOUSEBLOCK);
                this.sb(nx, ny, T.GLASSTONE);
            }
        }
        // Beacon at top
        this.st(x, sy - th - 1, T.PRISMCORE); this.st(x - 1, sy - th - 1, T.PRISMCORE); this.st(x + 1, sy - th - 1, T.PRISMCORE);
        this.st(x, sy - th - 2, T.PRISMCORE);
        // Platform
        for (let dx = -tw - 1; dx <= tw + 1; dx++) {
            const nx = x + dx;
            if (nx >= 2 && nx < WW - 2) this.st(nx, sy - th + 1, T.GLASSTONE);
        }
        // Windows
        for (let dy = -th + 3; dy < 0; dy += 4) this.st(x, sy + dy, T.GLASS);
        // Door
        this.st(x, sy - 1, T.AIR); this.st(x, sy - 2, T.AIR);
        // Chest
        this.st(x - 1, sy - 1, T.CHEST);
    }

    buildDarkObeliskCluster(x, sy) {
        // Cluster of dark stone obelisks for Corruption biome
        const num = 3 + Math.floor(this.rng.n() * 3);
        for (let i = 0; i < num; i++) {
            const ox = x + Math.floor((this.rng.n() - 0.5) * 16);
            const oh = 6 + Math.floor(this.rng.n() * 8);
            if (ox < 5 || ox >= WW - 5) continue;
            for (let dy = 0; dy < oh; dy++) {
                const w2 = Math.max(1, 2 - Math.floor(dy / 3));
                for (let dx = -w2; dx <= w2; dx++) {
                    const nx = ox + dx, ny = sy - dy;
                    if (ny >= 2 && ny < WH - 2 && nx >= 2 && nx < WW - 2) this.st(nx, ny, T.CORSTONE);
                }
            }
            // Glowing top
            this.st(ox, sy - oh, T.DTORCH);
            // Runes on sides
            if (this.rng.n() > 0.5) this.st(ox - 1, sy - Math.floor(oh / 2), T.RCRYSTAL);
            if (this.rng.n() > 0.5) this.st(ox + 1, sy - Math.floor(oh / 2), T.RCRYSTAL);
        }
        this.st(x, sy - 1, T.DCHEST);
    }

    buildTidalLighthouse(x, sy) {
        const bw = 5, bh = 14;
        // Foundation
        for (let dx = -bw; dx <= bw; dx++) for (let dy = 0; dy < 3; dy++) this.st(x + dx, sy + dy, T.TIDALROCK);
        // Tower body
        for (let dy = 1; dy <= bh; dy++) {
            const tw = Math.max(2, bw - Math.floor(dy / 4));
            for (let dx = -tw; dx <= tw; dx++) {
                if (Math.abs(dx) === tw) this.st(x + dx, sy - dy, T.TIDALROCK);
                else { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.TIDALROCK); }
            }
        }
        // Lantern room at top
        for (let dx = -2; dx <= 2; dx++) {
            this.st(x + dx, sy - bh, T.STORMGLASS);
            this.st(x + dx, sy - bh - 1, T.STORMGLASS);
            this.st(x + dx, sy - bh - 2, T.TIDALROCK);
        }
        // Light source
        this.st(x, sy - bh - 1, T.CHARGEDCRYSTAL);
        // Interior ladder and chest
        for (let dy = 1; dy < bh; dy++) this.st(x, sy - dy, T.LADDER);
        this.st(x - 1, sy - 1, T.CHEST);
        // Tidal pool at base
        for (let dx = -bw - 2; dx <= bw + 2; dx++) {
            if (Math.abs(dx) > bw && this.gt(x + dx, sy) !== T.AIR) {
                this.st(x + dx, sy, T.WATER); this.sll(x + dx, sy, 0.7);
            }
        }
    }

    buildStormObservatory(x, sy) {
        const ow = 8, oh = 10;
        // Base platform
        for (let dx = -ow; dx <= ow; dx++) {
            this.st(x + dx, sy, T.STORMSTONE);
            this.st(x + dx, sy + 1, T.STORMSTONE);
        }
        // Walls
        for (let dy = 1; dy <= oh; dy++) {
            this.st(x - ow, sy - dy, T.STORMSTONE);
            this.st(x + ow, sy - dy, T.STORMSTONE);
            // Interior background
            for (let dx = -ow + 1; dx < ow; dx++) { this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.STORMSTONE); }
        }
        // Dome roof
        for (let dx = -ow; dx <= ow; dx++) {
            const dh = Math.floor(Math.sqrt(Math.max(0, ow * ow - dx * dx)) * 0.5);
            for (let dy = 0; dy <= dh; dy++) this.st(x + dx, sy - oh - dy, T.STORMGLASS);
        }
        // Charged crystal telescope at top
        this.st(x, sy - oh - 3, T.CHARGEDCRYSTAL);
        this.st(x, sy - oh - 4, T.CHARGEDCRYSTAL);
        // Interior: ladder + chests
        for (let dy = 1; dy < oh; dy++) this.st(x, sy - dy, T.LADDER);
        this.st(x - 3, sy - 1, T.CHEST); this.st(x + 3, sy - 1, T.CHEST);
        // Lightning rods on corners
        for (let dy = 1; dy <= 4; dy++) { this.st(x - ow, sy - oh - dy, T.OBSIDIANSPIRE); this.st(x + ow, sy - oh - dy, T.OBSIDIANSPIRE); }
        this.st(x - ow, sy - oh - 5, T.CHARGEDCRYSTAL);
        this.st(x + ow, sy - oh - 5, T.CHARGEDCRYSTAL);
    }

    // === BIOME STRUCTURES: Crystal Caverns (19) ===
    buildCrystalShrine(x, sy) {
        // Glowing crystal formation around a central altar
        for (let dx = -4; dx <= 4; dx++) this.st(x + dx, sy, T.CRYSTALBLOCK);
        // Crystal pillars of varying height
        const pillars = [-3, -1, 1, 3];
        for (const px of pillars) {
            const ph = 3 + Math.floor(this.rng.n() * 4);
            for (let dy = 1; dy <= ph; dy++) this.st(x + px, sy - dy, T.CRYSTALBLOCK);
            this.st(x + px, sy - ph - 1, T.CRYSTAL);
        }
        // Central altar with chest
        this.st(x, sy - 1, T.CRYSTALBLOCK); this.st(x, sy - 2, T.CHEST);
        // Ambient crystal shards scattered around base
        for (let dx = -5; dx <= 5; dx++) {
            if (Math.abs(dx) > 4 && this.rng.n() > 0.5 && this.gt(x + dx, sy - 1) === T.AIR)
                this.st(x + dx, sy - 1, T.CRYSTSHARD);
        }
    }

    // === BIOME STRUCTURES: The Abyss (20) ===
    buildAbyssGateway(x, sy) {
        // Dark archway made of abyss stone — feels like a portal to nowhere
        const hw = 5, hh = 8;
        // Two pillars
        for (let dy = 1; dy <= hh; dy++) {
            this.st(x - hw, sy - dy, T.ABYSSSTONE); this.st(x - hw + 1, sy - dy, T.ABYSSSTONE);
            this.st(x + hw, sy - dy, T.ABYSSSTONE); this.st(x + hw - 1, sy - dy, T.ABYSSSTONE);
        }
        // Arch connecting pillars
        for (let dx = -hw; dx <= hw; dx++) {
            const archH = Math.floor(Math.sqrt(Math.max(0, hw * hw - dx * dx)) * 0.4);
            this.st(x + dx, sy - hh - archH, T.ABYSSSTONE);
            if (archH > 0) this.st(x + dx, sy - hh - archH + 1, T.ABYSSSTONE);
        }
        // Eerie glow at center top
        this.st(x, sy - hh, T.GLOWMUSH);
        // Interior: empty with background walls
        for (let dx = -hw + 2; dx <= hw - 2; dx++) for (let dy = 1; dy < hh; dy++) {
            this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.ABYSSSTONE);
        }
        // Chest hidden inside
        this.st(x, sy - 1, T.CHEST);
        // Cobwebs in upper corners
        this.st(x - hw + 2, sy - hh + 1, T.COBWEB); this.st(x + hw - 2, sy - hh + 1, T.COBWEB);
    }

    // === BIOME STRUCTURES: Ember Wastes (21) ===
    buildEmberForge(x, sy) {
        // Ruined forge with lava pool and anvil remnants
        const fw = 6;
        // Foundation
        for (let dx = -fw; dx <= fw; dx++) {
            this.st(x + dx, sy, T.EMBERITE);
            this.st(x + dx, sy + 1, T.EMBERITE);
        }
        // Crumbling walls
        for (let dy = 1; dy <= 5; dy++) {
            this.st(x - fw, sy - dy, T.EMBERITE);
            this.st(x + fw, sy - dy, T.EMBERITE);
            if (dy < 4) { this.st(x - fw + 1, sy - dy, this.rng.n() > 0.4 ? T.EMBERITE : T.AIR); }
        }
        // Partial roof (ruined)
        for (let dx = -fw; dx <= -2; dx++) this.st(x + dx, sy - 5, T.EMBERITE);
        // Interior cleared
        for (let dx = -fw + 1; dx < fw; dx++) for (let dy = 1; dy <= 4; dy++) {
            this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.EMBERITE);
        }
        // Small lava pool
        for (let dx = -2; dx <= 0; dx++) { this.st(x + dx, sy, T.LAVA); this.sll(x + dx, sy, 1.0); }
        // Anvil remnant and chest
        this.st(x + 3, sy - 1, T.FORGEANVIL); this.st(x + 4, sy - 1, T.CHEST);
        // Embers floating above
        this.st(x - 1, sy - 3, T.EMBER); this.st(x + 2, sy - 4, T.EMBER);
    }

    // === BIOME STRUCTURES: Overgrown Ruins (22) ===
    buildRuinedArch(x, sy) {
        // Crumbling stone arch overgrown with vines and moss
        const aw = 7, ah = 7;
        // Two broken columns
        for (let dy = 1; dy <= ah; dy++) {
            const broken = this.rng.n() > 0.85;
            if (!broken) this.st(x - aw, sy - dy, T.RUINBRICK);
            if (!broken || dy < ah - 1) this.st(x + aw, sy - dy, T.RUINBRICK);
        }
        // Partial arch (partially collapsed)
        for (let dx = -aw; dx <= aw - 2; dx++) {
            if (this.rng.n() > 0.25) this.st(x + dx, sy - ah, T.RUINBRICK);
        }
        // Floor platform
        for (let dx = -aw; dx <= aw; dx++) this.st(x + dx, sy, T.RUINBRICK);
        // Interior cleared
        for (let dx = -aw + 1; dx < aw; dx++) for (let dy = 1; dy < ah; dy++) {
            this.st(x + dx, sy - dy, T.AIR); this.sb(x + dx, sy - dy, T.RUINMOSS);
        }
        // Vines draping from arch
        for (let dx = -aw + 1; dx < aw; dx++) {
            if (this.rng.n() > 0.5) {
                const vl = 2 + Math.floor(this.rng.n() * 3);
                for (let v = 0; v < vl; v++) {
                    if (this.gt(x + dx, sy - ah + 1 + v) === T.AIR) this.st(x + dx, sy - ah + 1 + v, T.VINE);
                }
            }
        }
        // Moss on surfaces
        for (let dx = -aw - 1; dx <= aw + 1; dx++) {
            if (this.gt(x + dx, sy - 1) === T.AIR && this.rng.n() > 0.5) this.st(x + dx, sy - 1, T.FERN);
        }
        this.st(x, sy - 1, T.CHEST);
    }

    // === BIOME STRUCTURES: Blood Plains (23) ===
    buildBloodAltar(x, sy) {
        // Sacrificial altar with bone pillars and crimson glow
        for (let dx = -3; dx <= 3; dx++) this.st(x + dx, sy, T.BLOODSTONE);
        // Bone pillars
        for (let dy = 1; dy <= 4; dy++) { this.st(x - 3, sy - dy, T.BONE); this.st(x + 3, sy - dy, T.BONE); }
        this.st(x - 3, sy - 5, T.BONEPILE); this.st(x + 3, sy - 5, T.BONEPILE);
        // Central altar block raised
        this.st(x, sy - 1, T.BLOODSTONE); this.st(x - 1, sy - 1, T.BLOODSTONE); this.st(x + 1, sy - 1, T.BLOODSTONE);
        this.st(x, sy - 2, T.DALTAR);
        // Skull decorations
        this.st(x - 2, sy - 1, T.BONEPILE); this.st(x + 2, sy - 1, T.BONEPILE);
        // Thorns around perimeter
        for (let dx = -4; dx <= 4; dx++) {
            if (Math.abs(dx) > 3 && this.gt(x + dx, sy - 1) === T.AIR) this.st(x + dx, sy - 1, T.THORN);
        }
        this.st(x, sy - 3, T.CHEST);
    }

    // === BIOME STRUCTURES: Shimmer Glades (24) ===
    buildShimmerWell(x, sy) {
        // Mystical well with shimmering water and crystal accents
        for (let dx = -3; dx <= 3; dx++) {
            this.st(x + dx, sy, T.SHIMMERSTONE);
            if (Math.abs(dx) === 3) { this.st(x + dx, sy - 1, T.SHIMMERSTONE); this.st(x + dx, sy - 2, T.SHIMMERSTONE); }
        }
        // Water pool in center
        for (let dx = -2; dx <= 2; dx++) { this.st(x + dx, sy, T.WATER); this.sll(x + dx, sy, 1.0); }
        // Crystal accents on pillars
        this.st(x - 3, sy - 3, T.ACRYSTAL); this.st(x + 3, sy - 3, T.ACRYSTAL);
        // Flower ring around well
        for (let dx = -4; dx <= 4; dx++) {
            if (Math.abs(dx) > 3 && this.gt(x + dx, sy - 1) === T.AIR) {
                const ft = [T.FLOWER_B, T.FLOWER_P, T.FLOWER_W];
                this.st(x + dx, sy - 1, ft[Math.floor(this.rng.n() * ft.length)]);
            }
        }
        this.st(x, sy - 1, T.CHEST);
    }

    // === BIOME STRUCTURES: Living Caves surface (30) ===
    buildFleshMound(x, sy) {
        // Organic mound rising from the ground — pulsing and alive
        const mw = 5 + Math.floor(this.rng.n() * 3), mh = 4 + Math.floor(this.rng.n() * 3);
        // Mound shape
        for (let dx = -mw; dx <= mw; dx++) for (let dy = 0; dy <= mh; dy++) {
            const distSq = (dx * dx) / (mw * mw) + (dy * dy) / (mh * mh);
            if (distSq <= 1) {
                const nx = x + dx, ny = sy - dy;
                if (nx < 2 || nx >= WW - 2) continue;
                if (distSq > 0.75) this.st(nx, ny, T.PULSEWALL);
                else { this.st(nx, ny, T.AIR); this.sb(nx, ny, T.FLESHSTONE); }
            }
        }
        // Nerve veins radiating outward on surface
        for (let v = 0; v < 3; v++) {
            const vDir = (v - 1) * 3;
            for (let s = 1; s <= 3 + Math.floor(this.rng.n() * 3); s++) {
                const vx = x + vDir * s + Math.floor(this.rng.n() * 2 - 1);
                if (vx >= 2 && vx < WW - 2 && this.gt(vx, sy - 1) === T.AIR) this.st(vx, sy - 1, T.NERVEVEIN);
            }
        }
        // Eye block watching from top
        this.st(x, sy - mh, T.EYEBLOCK);
        // Heart at core
        this.st(x, sy - Math.floor(mh / 2), T.HEARTBLOCK);
        // Organic moss at base
        for (let dx = -mw - 1; dx <= mw + 1; dx++) {
            if (this.gt(x + dx, sy - 1) === T.AIR && this.rng.n() > 0.5) this.st(x + dx, sy - 1, T.ORGANICMOSS);
        }
        if (this.rng.n() > 0.4) this.st(x + 1, sy - 1, T.CHEST);
    }

    genTrees() {
        let last = -10;
        for (let x = 5; x < WW - 5; x++) {
            const sy = Math.floor(this.heightMap[x]), b = this.biomeMap[x], s = this.gt(x, sy);
            if (x - last < 4 || s === T.WATER || s === T.LAVA || this.gt(x, sy - 1) !== T.AIR) continue;
            const r = this.rng.n();
            switch (b) {
                case 0: if (r > 0.95) { this.bgTree(x, sy, 4 + Math.floor(r * 3), T.PINEWOOD, T.PINELEAF, 'spruce'); last = x; } break;
                case 1: if (r > 0.8) { this.bgTree(x, sy, 6 + Math.floor(r * 5), T.PINEWOOD, T.PINELEAF, 'spruce'); last = x; } break;
                case 2: if (r > 0.93 && s === T.GRASS) { this.bgTree(x, sy, 4 + Math.floor(r * 4), T.WOOD, T.LEAVES, 'round'); last = x; } break;
                case 3: if (r > 0.73) { this.bgTree(x, sy, 5 + Math.floor(r * 5), T.WOOD, T.LEAVES, 'round'); last = x; } break;
                case 4: if (r > 0.78) { this.bgTree(x, sy, 5 + Math.floor(r * 4), T.CHERRYWOOD, T.CHERRYLEAF, 'round'); last = x; } break;
                case 5: if (r > 0.92) { const h2 = 3 + Math.floor(r * 5); for (let dy = 1; dy <= h2; dy++)this.st(x, sy - dy, T.CACTUS); last = x; } break;
                case 6: if (r > 0.93) { const h2 = 3 + Math.floor(r * 3); for (let dy = 1; dy <= h2; dy++)this.sb(x, sy - dy, T.DEADTREE); last = x; } break;
                case 7: if (r > 0.78) {
                    this.bgTree(x, sy, 9 + Math.floor(r * 7), T.WOOD, T.LEAVES, 'round');
                    for (let vy = sy - 7; vy < sy; vy++)if (this.rng.n() > 0.5) for (const dx of [-4, -3, 3, 4]) if (this.gt(x + dx, vy) === T.AIR) this.st(x + dx, vy, T.VINE); last = x;
                } break;
                case 8: if (r > 0.83) {
                    this.bgTree(x, sy, 4 + Math.floor(r * 3), T.WILLWOOD, T.WILLLEAF, 'round');
                    for (let dx = -4; dx <= 4; dx++)if (this.rng.n() > 0.4) { const vl = 3 + Math.floor(r * 4); for (let vv = 0; vv < vl; vv++)if (this.gt(x + dx, sy - 2 + vv) === T.AIR) this.st(x + dx, sy - 2 + vv, T.VINE); } last = x;
                } break;
                case 9: if (r > 0.9) {
                    const h2 = 6 + Math.floor(r * 3); for (let dy = 1; dy <= h2; dy++)this.sb(x, sy - dy, T.PALMWOOD);
                    for (let dx = -3; dx <= 3; dx++) { this.st(x + dx, sy - h2, T.PALMLEAF); if (Math.abs(dx) < 3) this.st(x + dx, sy - h2 - 1, T.PALMLEAF); } last = x;
                } break;
                case 10: if (r > 0.83) { const h2 = 5 + Math.floor(r * 4); for (let dy = 1; dy <= h2; dy++)this.sb(x, sy - dy, T.DEADTREE); last = x; } break;
                case 11: if (r > 0.83) { this.bgTree(x, sy, 5 + Math.floor(r * 3), T.WOOD, T.CHERRYLEAF, 'round'); last = x; } break;
                case 12: if (r > 0.78) {
                    const h2 = 5 + Math.floor(r * 5); for (let dy = 1; dy <= h2; dy++)this.sb(x, sy - dy, T.MUSTEM);
                    for (let dx = -3; dx <= 3; dx++) { this.st(x + dx, sy - h2 - 1, T.MUCAP); if (Math.abs(dx) < 3) this.st(x + dx, sy - h2 - 2, T.MUCAP); } last = x;
                } break;
                case 14: if (r > 0.88) { this.acaciaTree(x, sy); last = x; } break;
                case 15: if (r > 0.6) { this.bgTree(x, sy, 6 + Math.floor(r * 4), T.DARKWOOD, T.DARKLEAF, 'round'); last = x; } break;
                case 16: if (r > 0.91) { this.bgTree(x, sy, 4 + Math.floor(r * 3), T.WOOD, T.LEAVES, 'round'); last = x; } break;
                case 17: if (r > 0.96) { const h2 = 3 + Math.floor(r * 3); for (let dy = 1; dy <= h2; dy++)this.sb(x, sy - dy, T.DEADTREE); last = x; } break;
                case 19: if (r > 0.85) { const h2 = 4 + Math.floor(r * 4); for (let dy = 1; dy <= h2; dy++)this.st(x, sy - dy, T.CRYSTALBLOCK); this.st(x, sy - h2 - 1, T.CRYSTAL); this.st(x - 1, sy - h2, T.CRYSTAL); this.st(x + 1, sy - h2, T.CRYSTAL); last = x; } break;
                case 20: if (r > 0.92) { const h2 = 3 + Math.floor(r * 3); for (let dy = 1; dy <= h2; dy++)this.sb(x, sy - dy, T.DEADTREE); last = x; } break;
                case 21: if (r > 0.93) { const h2 = 2 + Math.floor(r * 3); for (let dy = 1; dy <= h2; dy++)this.sb(x, sy - dy, T.DEADTREE); if (this.rng.n() > 0.5) this.st(x, sy - h2 - 1, T.EMBER); last = x; } break;
                case 22: if (r > 0.7) { this.bgTree(x, sy, 7 + Math.floor(r * 6), T.WOOD, T.LEAVES, 'round'); for (let vy = sy - 6; vy < sy; vy++)if (this.rng.n() > 0.35) for (const dx of [-3, -2, 2, 3]) if (this.gt(x + dx, vy) === T.AIR) this.st(x + dx, vy, T.VINE); last = x; } break;
                case 23: if (r > 0.87) { const h2 = 4 + Math.floor(r * 3); for (let dy = 1; dy <= h2; dy++)this.sb(x, sy - dy, T.DEADTREE); if (this.rng.n() > 0.4) this.st(x - 1, sy - 1, T.THORN); if (this.rng.n() > 0.4) this.st(x + 1, sy - 1, T.THORN); last = x; } break;
                case 24: if (r > 0.85) { this.bgTree(x, sy, 5 + Math.floor(r * 4), T.WOOD, T.CHERRYLEAF, 'round'); last = x; } break;
                case 25: if (r > 0.7) {
                    const h2 = 6 + Math.floor(r * 6); for (let dy = 1; dy <= h2; dy++) this.st(x, sy - dy, T.SPORESTONE);
                    // Giant mushroom cap shape — wider and more organic
                    const capW = 3 + Math.floor(r * 3);
                    for (let dx = -capW; dx <= capW; dx++) {
                        const capH = capW - Math.abs(dx);
                        for (let dy = 0; dy < Math.min(capH, 3); dy++)
                            if (this.gt(x + dx, sy - h2 - dy) === T.AIR) this.st(x + dx, sy - h2 - dy, T.SPORECAP);
                    }
                    // Pulsing vines hanging down
                    if (this.rng.n() > 0.4) for (let vl = 0; vl < 3 + Math.floor(r * 3); vl++)
                        if (this.gt(x - capW + Math.floor(r * capW * 2), sy - h2 + 1 + vl) === T.AIR)
                            this.st(x - capW + Math.floor(r * capW * 2), sy - h2 + 1 + vl, T.PULSEVINE);
                    last = x;
                } break;
                case 26: if (r > 0.82) {
                    // Petrified trees — stone pillars with jagged tops
                    const h2 = 5 + Math.floor(r * 5);
                    for (let dy = 1; dy <= h2; dy++) this.sb(x, sy - dy, T.FOSSILTREE);
                    // Broken top
                    if (this.rng.n() > 0.3) {
                        this.st(x - 1, sy - h2, T.PETRISTONE);
                        this.st(x + 1, sy - h2, T.PETRISTONE);
                    }
                    last = x;
                } break;
                case 27: if (r > 0.88) {
                    // Crystal spires — tall, thin, colorful
                    const h2 = 4 + Math.floor(r * 5);
                    for (let dy = 1; dy <= h2; dy++) this.st(x, sy - dy, T.GLASSTONE);
                    this.st(x, sy - h2 - 1, T.PRISMCORE);
                    if (this.rng.n() > 0.5) { this.st(x - 1, sy - h2 + 1, T.GLASSTONE); this.st(x + 1, sy - h2 + 1, T.GLASSTONE); }
                    last = x;
                } break;
                case 28: if (r > 0.85) {
                    // Driftwood — broken ship masts and beached logs
                    const h2 = 3 + Math.floor(r * 4);
                    const lean = Math.floor(this.rng.n() * 2);
                    for (let dy = 1; dy <= h2; dy++) {
                        const lx = x + (dy > h2 / 2 ? lean : 0);
                        if (lx >= 2 && lx < WW - 2) this.sb(lx, sy - dy, T.DRIFTWOOD);
                    }
                    // Barnacles at base
                    if (this.rng.n() > 0.5) this.st(x - 1, sy - 1, T.BARNACLE);
                    if (this.rng.n() > 0.5) this.st(x + 1, sy - 1, T.BARNACLE);
                    last = x;
                } break;
                case 29: if (r > 0.9) {
                    // Lightning rods — obsidian spires with charged crystal tops
                    const h2 = 6 + Math.floor(r * 6);
                    for (let dy = 1; dy <= h2; dy++) this.st(x, sy - dy, T.OBSIDIANSPIRE);
                    this.st(x, sy - h2 - 1, T.CHARGEDCRYSTAL);
                    if (this.rng.n() > 0.5) { this.st(x - 1, sy - h2, T.CHARGEDCRYSTAL); this.st(x + 1, sy - h2, T.CHARGEDCRYSTAL); }
                    // Stormglass formations at base
                    for (let dx = -2; dx <= 2; dx++) {
                        if (Math.abs(dx) > 0 && this.rng.n() > 0.6) this.st(x + dx, sy - 1, T.STORMGLASS);
                    }
                    last = x;
                } break;
                case 30: if (r > 0.8) {
                    // Organic tendrils — fleshy growths reaching upward (background, non-blocking)
                    const h2 = 3 + Math.floor(r * 4);
                    for (let dy = 1; dy <= h2; dy++) this.sb(x, sy - dy, T.FLESHSTONE);
                    // Nerve veins branching from top
                    if (this.rng.n() > 0.4) {
                        for (let dx = -2; dx <= 2; dx++) {
                            if (dx !== 0 && this.rng.n() > 0.5 && this.gt(x + dx, sy - h2) === T.AIR)
                                this.st(x + dx, sy - h2, T.NERVEVEIN);
                        }
                    }
                    // Eye watching from trunk (sometimes)
                    if (this.rng.n() > 0.75) this.st(x, sy - Math.floor(h2 / 2), T.EYEBLOCK);
                    last = x;
                } break;
            }
        }
    }
    bgTree(x, sy, h, trunk, leaf, shape) {
        for (let dy = 1; dy <= h; dy++)this.sb(x, sy - dy, trunk);
        if (shape === 'spruce') {
            for (let dy = 2; dy <= h; dy++) {
                const r2 = Math.max(0, Math.floor((h - dy) / 2) + 1);
                if (dy % 2 === 0) for (let dx = -r2; dx <= r2; dx++)if (this.gt(x + dx, sy - dy) === T.AIR) this.st(x + dx, sy - dy, leaf);
            }
            this.st(x, sy - h - 1, leaf);
        } else {
            for (let dy = h - 3; dy <= h + 1; dy++) {
                const r2 = 3 - Math.abs(dy - h) * 0.5;
                for (let dx = -Math.ceil(r2); dx <= Math.ceil(r2); dx++)
                    if (Math.sqrt(dx * dx + (dy - h) * (dy - h) * 0.5) <= r2 + 0.5 && this.gt(x + dx, sy - dy) === T.AIR) this.st(x + dx, sy - dy, leaf);
            }
        }
    }
    acaciaTree(x, sy) {
        const h = 5 + Math.floor(this.rng.n() * 4); let ox = 0;
        for (let dy = 1; dy <= h; dy++) { this.sb(x + ox, sy - dy, T.ACACIA); if (dy > 2 && this.rng.n() > 0.6) ox += this.rng.n() > 0.5 ? 1 : -1; }
        const cw = 4 + Math.floor(this.rng.n() * 3);
        for (let dx = -cw; dx <= cw; dx++)for (let dy = 0; dy < 2; dy++) {
            if (Math.abs(dx) <= cw - dy && this.gt(x + ox + dx, sy - h - dy) === T.AIR) this.st(x + ox + dx, sy - h - dy, T.ACACLEAF);
        }
    }

    genVeg() {
        for (let x = 1; x < WW - 1; x++) {
            const sy = Math.floor(this.heightMap[x]), b = this.biomeMap[x];
            if (this.gt(x, sy - 1) !== T.AIR) continue; const r = this.rng.n();
            switch (b) {
                case 0: if (r > 0.95) this.st(x, sy - 1, T.DEADGRASS); break;
                case 1: if (r > 0.85) this.st(x, sy - 1, T.FERN); if (r > 0.97) this.st(x, sy - 1, T.BERRYBUSH); break;
                case 2: if (r > 0.8) this.st(x, sy - 1, T.TALLGRASS); if (r > 0.94) this.st(x, sy - 1, this.rng.n() > 0.5 ? T.FLOWER_R : T.FLOWER_Y); break;
                case 3: if (r > 0.75) this.st(x, sy - 1, T.TALLGRASS); if (r > 0.9) this.st(x, sy - 1, T.FERN); if (r > 0.95) this.st(x, sy - 1, T.MUSHROOM); break;
                case 4: if (r > 0.85) this.st(x, sy - 1, T.TALLGRASS); if (r > 0.9) this.st(x, sy - 1, T.SAKURA); break;
                case 5: if (r > 0.96) this.st(x, sy - 1, T.DEADGRASS); if (r > 0.98) this.st(x, sy - 1, T.BONEPILE); break;
                case 6: if (r > 0.93) this.st(x, sy - 1, T.DEADGRASS); if (r > 0.97) this.st(x, sy - 1, T.BONEPILE); break;
                case 7: if (r > 0.7) this.st(x, sy - 1, T.TALLGRASS); if (r > 0.85) this.st(x, sy - 1, T.FERN); break;
                case 8: if (r > 0.85) this.st(x, sy - 1, T.CATTAIL); if (r > 0.95) this.st(x, sy - 1, T.MUSHROOM); break;
                case 9: if (r > 0.95) this.st(x, sy - 1, T.SEASHELL); if (r > 0.98) this.st(x, sy - 1, T.CORAL); break;
                case 10: if (r > 0.85) this.st(x, sy - 1, T.THORN); break;
                case 11: if (r > 0.8) this.st(x, sy - 1, T.TALLGRASS); if (r > 0.92) this.st(x, sy - 1, T.RCRYSTAL); break;
                case 12: if (r > 0.7) this.st(x, sy - 1, T.MUSHROOM); if (r > 0.85) this.st(x, sy - 1, T.GLOWMUSH); break;
                case 13: if (r > 0.9) this.st(x, sy - 1, T.EMBER); break;
                case 14: if (r > 0.6) this.st(x, sy - 1, T.TALLGRASS); break;
                case 15: if (r > 0.6) this.st(x, sy - 1, T.FERN); if (r > 0.8) this.st(x, sy - 1, T.MUSHROOM); break;
                case 16: {
                    if (r > 0.5) this.st(x, sy - 1, T.TALLGRASS);
                    if (r > 0.7) {
                        const ft = [T.FLOWER_R, T.FLOWER_Y, T.FLOWER_B, T.FLOWER_P, T.FLOWER_W];
                        this.st(x, sy - 1, ft[Math.floor(this.rng.n() * ft.length)]);
                    } break;
                }
                case 17: if (r > 0.92) this.st(x, sy - 1, T.DEADGRASS); if (r > 0.97) this.st(x, sy - 1, T.BONEPILE); break;
                case 18: if (r > 0.95) this.st(x, sy - 1, T.DEADGRASS); break;
                case 19: if (r > 0.6) this.st(x, sy - 1, T.CRYSTSHARD); if (r > 0.9) this.st(x, sy - 1, T.ACRYSTAL); break;
                case 20: if (r > 0.92) this.st(x, sy - 1, T.GLOWMUSH); if (r > 0.97) this.st(x, sy - 1, T.COBWEB); break;
                case 21: if (r > 0.7) this.st(x, sy - 1, T.EMBER); if (r > 0.9) this.st(x, sy - 1, T.DEADGRASS); break;
                case 22: if (r > 0.5) this.st(x, sy - 1, T.FERN); if (r > 0.7) this.st(x, sy - 1, T.TALLGRASS); if (r > 0.9) this.st(x, sy - 1, T.MUSHROOM); break;
                case 23: if (r > 0.6) this.st(x, sy - 1, T.THORN); if (r > 0.85) this.st(x, sy - 1, T.BONEPILE); break;
                case 24: { if (r > 0.4) this.st(x, sy - 1, T.TALLGRASS); if (r > 0.6) { const ft = [T.FLOWER_B, T.FLOWER_P, T.FLOWER_W]; this.st(x, sy - 1, ft[Math.floor(this.rng.n() * ft.length)]); } break; }
                case 25: if (r > 0.5) this.st(x, sy - 1, T.GLOWMUSH); if (r > 0.75) this.st(x, sy - 1, T.PULSEVINE); if (r > 0.92) this.st(x, sy - 1, T.SPORECAP); break;
                case 26: if (r > 0.7) this.st(x, sy - 1, T.DEADGRASS); if (r > 0.88) this.st(x, sy - 1, T.BONEPILE); if (r > 0.95) this.st(x, sy - 1, T.TIMECRACK); break;
                case 27: {
                    if (r > 0.5) this.st(x, sy - 1, T.TALLGRASS);
                    if (r > 0.65) { const ft = [T.FLOWER_B, T.FLOWER_P, T.FLOWER_W, T.FLOWER_R, T.FLOWER_Y]; this.st(x, sy - 1, ft[Math.floor(this.rng.n() * ft.length)]); }
                    if (r > 0.9) this.st(x, sy - 1, T.CRYSTSHARD); break;
                }
                case 28: {
                    if (r > 0.5) this.st(x, sy - 1, T.SEAGRASS);
                    if (r > 0.8) this.st(x, sy - 1, T.BARNACLE);
                    if (r > 0.93) this.st(x, sy - 1, T.SEASHELL);
                    if (r > 0.97) this.st(x, sy - 1, T.CORAL);
                    break;
                }
                case 29: {
                    if (r > 0.7) this.st(x, sy - 1, T.TALLGRASS);
                    if (r > 0.9) this.st(x, sy - 1, T.STORMGLASS);
                    if (r > 0.96) this.st(x, sy - 1, T.CHARGEDCRYSTAL);
                    break;
                }
                case 30: {
                    if (r > 0.5) this.st(x, sy - 1, T.ORGANICMOSS);
                    if (r > 0.75) this.st(x, sy - 1, T.NERVEVEIN);
                    if (r > 0.92) this.st(x, sy - 1, T.THORN);
                    break;
                }
            }
        }
    }

    genUnderFeatures() {
        for (let x = 2; x < WW - 2; x++)for (let y = Math.floor(this.heightMap[x]) + 10; y < WH - 5; y++) {
            if (this.gt(x, y) !== T.AIR) continue;
            const ub = this.getUnderBiome2D ? this.getUnderBiome2D(x, y) : this.underBiomeMap[x];
            const r = this.rng.n();
            if (SOLID.has(this.gt(x, y - 1)) && r > 0.96) this.st(x, y, T.STALAC);
            if (SOLID.has(this.gt(x, y + 1)) && r > 0.95) this.st(x, y, T.STALAGM);
            if (SOLID.has(this.gt(x, y + 1))) {
                if (ub === 4 && r > 0.88) this.st(x, y, T.GLOWMUSH);
                if (ub === 5 && r > 0.85) this.st(x, y, T.GLOWMUSH);
                if (ub === 7 && r > 0.92) this.st(x, y, T.CRYSTSHARD);
                if (ub === 8 && r > 0.93) this.st(x, y, T.EMBER);
                if (ub === 9 && r > 0.94) this.st(x, y, T.BONEPILE);
            }
            if (SOLID.has(this.gt(x, y - 1))) {
                if (ub === 4 && r > 0.9) { const vl = 2 + Math.floor(r * 4); for (let vv = 0; vv < vl; vv++)if (this.gt(x, y + vv) === T.AIR) this.st(x, y + vv, T.CAVEVINE); }
                if (ub === 3 && r > 0.93) this.st(x, y, T.DRIPSTONE);
                if (ub === 10 && r > 0.88) { const vl = 2 + Math.floor(r * 3); for (let vv = 0; vv < vl; vv++) if (this.gt(x, y + vv) === T.AIR) this.st(x, y + vv, T.NERVEVEIN); }
                if (ub === 6 && r > 0.92) this.st(x, y, T.COBWEB);
                if (ub === 1 && r > 0.95) this.st(x, y, T.STALAC);
                if (ub === 2 && r > 0.95) this.st(x, y, T.STALAGM);
                if (y < this.heightMap[x] + 20 && r > 0.97) this.st(x, y, T.ROOT);
            }
        }
        // === UNDERGROUND WATERFALLS ===
        this._genWaterfalls();
        // Deep lava
        for (let x = 5; x < WW - 5; x++)for (let y = Math.floor(WH * 0.82); y < WH - 3; y++) {
            if (this.gt(x, y) === T.AIR && this.nO.noise(x / 15, y / 15) > 0.4 && SOLID.has(this.gt(x, y + 1))) {
                this.st(x, y, T.LAVA); this.sll(x, y, 1.0);
            }
        }
    }

    _genWaterfalls() {
        // Create underground waterfalls — vertical water columns in cave openings
        const count = 12 + Math.floor(this.rng.n() * 10);
        for (let i = 0; i < count; i++) {
            const wx = 30 + Math.floor(this.rng.n() * (WW - 60));
            const sy = Math.floor(this.heightMap[Math.min(wx, WW - 1)]);
            const startY = sy + 15 + Math.floor(this.rng.n() * 40);
            if (startY >= WH - 20) continue;
            // Find a ceiling-to-air transition (cave opening from above)
            let ceilY = -1;
            for (let y = startY; y < Math.min(startY + 15, WH - 5); y++) {
                if (SOLID.has(this.gt(wx, y - 1)) && this.gt(wx, y) === T.AIR) { ceilY = y; break; }
            }
            if (ceilY < 0) continue;
            // Pour water downward from ceiling until we hit solid or max length
            const maxLen = 8 + Math.floor(this.rng.n() * 12);
            let fell = 0;
            for (let dy = 0; dy < maxLen; dy++) {
                const fy = ceilY + dy;
                if (fy >= WH - 2) break;
                if (SOLID.has(this.gt(wx, fy))) break;
                // Place water column
                this.st(wx, fy, T.WATER); this.sll(wx, fy, 1.0);
                fell++;
            }
            if (fell < 3) continue;
            // Create a small pool at the bottom
            const poolY = ceilY + fell;
            if (poolY < WH - 5 && SOLID.has(this.gt(wx, poolY))) {
                for (let dx = -2; dx <= 2; dx++) {
                    const px = wx + dx;
                    if (px < 1 || px >= WW - 1) continue;
                    for (let dy = -1; dy >= -2; dy--) {
                        const py = poolY + dy;
                        if (py < 0) continue;
                        if (this.gt(px, py) === T.AIR) { this.st(px, py, T.WATER); this.sll(px, py, 1.0); }
                    }
                }
            }
            // Moss/vines near waterfall
            for (let dy = 0; dy < fell; dy++) {
                const fy = ceilY + dy;
                for (const dx of [-1, 1]) {
                    const nx = wx + dx;
                    if (nx < 1 || nx >= WW - 1) continue;
                    if (this.gt(nx, fy) === T.AIR && SOLID.has(this.gt(nx + dx, fy)) && this.rng.n() > 0.6) {
                        this.st(nx, fy, T.CAVEMOSS);
                    }
                }
            }
        }
    }

    // === SKY ISLANDS GENERATION ===
    genSkyIslands() {
        this.skyIslands = [];
        const num = 4 + Math.floor(this.rng.n() * 4);
        for (let i = 0; i < num; i++) {
            const ix = 40 + Math.floor(this.rng.n() * (WW - 80));
            const iy = 8 + Math.floor(this.rng.n() * 15); // very high in the world
            const iw = 12 + Math.floor(this.rng.n() * 18);
            const ih = 4 + Math.floor(this.rng.n() * 3);
            this.skyIslands.push({ x: ix, y: iy, w: iw, h: ih });
            // Build island — elliptical shape
            for (let dx = -iw; dx <= iw; dx++) {
                for (let dy = -ih; dy <= ih; dy++) {
                    const nx2 = ix + dx, ny2 = iy + dy;
                    if (nx2 < 1 || nx2 >= WW - 1 || ny2 < 1 || ny2 >= WH - 1) continue;
                    const ex = (dx * dx) / (iw * iw) + (dy * dy) / (ih * ih);
                    if (ex <= 1) {
                        if (dy === -ih || (dy < 0 && ex > 0.7)) {
                            this.st(nx2, ny2, T.SKYGRASS);
                        } else if (dy >= ih - 1) {
                            this.st(nx2, ny2, T.CLOUDBLOCK);
                        } else {
                            this.st(nx2, ny2, T.SKYSTONE);
                        }
                        this.sb(nx2, ny2, T.SKYSTONE);
                    }
                }
            }
            // Add clouds around the bottom
            for (let dx = -iw - 2; dx <= iw + 2; dx++) {
                for (let dy = ih; dy <= ih + 2; dy++) {
                    const nx2 = ix + dx, ny2 = iy + dy;
                    if (nx2 < 1 || nx2 >= WW - 1 || ny2 < 1 || ny2 >= WH - 1) continue;
                    if (this.gt(nx2, ny2) === T.AIR && this.rng.n() > 0.3) {
                        this.st(nx2, ny2, T.CLOUDBLOCK);
                    }
                }
            }
            // Place a chest with loot on some islands
            if (this.rng.n() > 0.4) {
                const cx2 = ix + Math.floor((this.rng.n() - 0.5) * iw * 0.5);
                const cy2 = iy - ih - 1;
                if (cy2 > 0 && this.gt(cx2, cy2) === T.AIR) {
                    this.st(cx2, cy2, T.CHEST);
                }
            }
            // Place starblocks as rare decoration
            for (let dx = -iw + 2; dx <= iw - 2; dx++) {
                const nx2 = ix + dx;
                if (nx2 < 1 || nx2 >= WW - 1) continue;
                const surfY2 = iy - ih;
                if (surfY2 > 0 && this.gt(nx2, surfY2) === T.AIR && this.rng.n() > 0.85) {
                    this.st(nx2, surfY2, T.STARBLOCK);
                }
            }
            // Place sunplates as floor in some structures
            if (this.rng.n() > 0.5) {
                const sw2 = 4 + Math.floor(this.rng.n() * 4);
                const scx = ix + Math.floor((this.rng.n() - 0.5) * iw * 0.4);
                for (let dx = -sw2; dx <= sw2; dx++) {
                    const nx2 = scx + dx;
                    if (nx2 < 1 || nx2 >= WW - 1) continue;
                    const platY = iy - ih;
                    if (platY > 0) this.st(nx2, platY, T.SUNPLATE);
                    // Walls
                    if (dx === -sw2 || dx === sw2) {
                        for (let dy2 = -3; dy2 < 0; dy2++) {
                            if (platY + dy2 > 0) this.st(nx2, platY + dy2, T.SKYSTONE);
                        }
                    }
                }
            }
        }
    }

    initLiquids() { for (let x = 0; x < WW; x++)for (let y = 0; y < WH; y++) { const t = this.gt(x, y); if (LIQUID.has(t) && this.gll(x, y) === 0) this.sll(x, y, 1.0); } }

    calcLight() {
        // Light sources include new mutation tiles
        this.light.fill(0);
        this.blockLight.fill(0);
        const SKY_TILES = new Set([T.SKYSTONE, T.CLOUDBLOCK, T.STARBLOCK, T.SKYGRASS, T.SUNPLATE]);
        // Sky Light
        for (let x = 0; x < WW; x++) {
            let sl = 1;
            for (let y = 0; y < WH; y++) {
                const i = y * WW + x, t = this.tiles[i];
                if (SOLID.has(t) && !SKY_TILES.has(t)) sl *= 0.82;
                else if (LIQUID.has(t)) sl *= 0.94;
                this.light[i] = sl;
                if (sl < 0.01) sl = 0;
            }
        }

        // Horizontal Sky Spread (Left -> Right)
        for (let y = 0; y < WH; y++) {
            for (let x = 1; x < WW; x++) {
                const i = y * WW + x;
                const l = this.light[i], ln = this.light[i - 1];
                const reduction = SOLID.has(this.tiles[i]) ? 0.7 : 0.9;
                if (ln * reduction > l) this.light[i] = ln * reduction;
            }
        }
        // Horizontal Sky Spread (Right -> Left)
        for (let y = 0; y < WH; y++) {
            for (let x = WW - 2; x >= 0; x--) {
                const i = y * WW + x;
                const l = this.light[i], ln = this.light[i + 1];
                const reduction = SOLID.has(this.tiles[i]) ? 0.7 : 0.9;
                if (ln * reduction > l) this.light[i] = ln * reduction;
            }
        }
        const lights = {
            [T.TORCH]: [10, 1], [T.DTORCH]: [9, 0.9], [T.LAVA]: [6, 0.85], [T.CRYSTAL]: [6, 0.7],
            [T.GLOWMUSH]: [8, 0.8], [T.SPAWNER]: [4, 0.5], [T.DSPAWNER]: [5, 0.6], [T.LANTERN]: [9, 0.95],
            [T.EMBER]: [5, 0.7], [T.ACRYSTAL]: [8, 0.9], [T.RCRYSTAL]: [7, 0.85], [T.BIOVINE]: [5, 0.6],
            [T.CRYSTSHARD]: [4, 0.5], [T.GEYSER]: [6, 0.7], [T.HELLSTONE]: [4, 0.5],
            [T.PULSEVINE]: [6, 0.7], [T.SPORECAP]: [5, 0.6], [T.TIMECRACK]: [7, 0.8],
            [T.PRISMCORE]: [10, 1],
            [T.CHARGEDCRYSTAL]: [10, 1], [T.NERVEVEIN]: [5, 0.5], [T.EYEBLOCK]: [6, 0.6],
            [T.HEARTBLOCK]: [8, 0.8], [T.LIGHTHOUSEBLOCK]: [3, 0.3],
            [T.WORLDHEART]: [12, 1], [T.WORLDSAP]: [8, 0.9], [T.WORLDCORE]: [10, 0.95],
            [T.ROOTCRYSTAL]: [7, 0.8], [T.CRACKSHARD]: [5, 0.6], [T.WORLDCRACK]: [6, 0.7],
            [T.ROOTVEIN]: [4, 0.5]
        };
        // Block Light
        for (let x = 0; x < WW; x++)for (let y = 0; y < WH; y++) {
            const l = lights[this.gt(x, y)]; if (!l) continue; const [lr, li] = l;
            for (let dx = -lr; dx <= lr; dx++)for (let dy = -lr; dy <= lr; dy++) {
                const d = Math.sqrt(dx * dx + dy * dy); if (d <= lr) {
                    const lx = x + dx, ly = y + dy;
                    if (lx >= 0 && lx < WW && ly >= 0 && ly < WH) {
                        const idx = ly * WW + lx;
                        this.blockLight[idx] = Math.max(this.blockLight[idx], li * (1 - d / lr));
                    }
                }
            }
        }
    }
    updateLight(cx, cy, r) {
        // Safety radius to ensure large light sources are cleared
        r = Math.max(r, 20);
        // Clear lights in range
        for (let x = cx - r; x <= cx + r; x++)for (let y = cy - r; y <= cy + r; y++) {
            if (x >= 0 && x < WW && y >= 0 && y < WH) {
                this.light[y * WW + x] = 0;
                this.blockLight[y * WW + x] = 0;
            }
        }
        // Recalculate Sky Light
        for (let x = cx - r; x <= cx + r; x++) {
            if (x < 0 || x >= WW) continue; let sl = 1;
            const SKY_TILES = new Set([T.SKYSTONE, T.CLOUDBLOCK, T.STARBLOCK, T.SKYGRASS, T.SUNPLATE]);
            for (let y = 0; y < WH; y++) {
                const i = y * WW + x, t = this.tiles[i];
                if (SOLID.has(t) && !SKY_TILES.has(t)) sl *= 0.82;
                else if (LIQUID.has(t)) sl *= 0.94;
                if (y >= cy - r) this.light[i] = sl;
                if (sl < 0.01) sl = 0;
            }
        }

        // Horizontal Sky Spread (Left -> Right) - Local Area
        const minX = Math.max(1, cx - r), maxX = Math.min(WW - 2, cx + r);
        for (let y = 0; y < WH; y++) {
            for (let x = minX; x <= maxX; x++) {
                const i = y * WW + x;
                const l = this.light[i], ln = this.light[i - 1];
                const reduction = SOLID.has(this.tiles[i]) ? 0.7 : 0.9;
                if (ln * reduction > l) this.light[i] = ln * reduction;
            }
        }
        // Horizontal Sky Spread (Right -> Left) - Local Area
        for (let y = 0; y < WH; y++) {
            for (let x = maxX; x >= minX; x--) {
                const i = y * WW + x;
                const l = this.light[i], ln = this.light[i + 1];
                const reduction = SOLID.has(this.tiles[i]) ? 0.7 : 0.9;
                if (ln * reduction > l) this.light[i] = ln * reduction;
            }
        }
        const lights = {
            [T.TORCH]: [10, 1], [T.DTORCH]: [9, 0.9], [T.LAVA]: [6, 0.85], [T.CRYSTAL]: [6, 0.7],
            [T.GLOWMUSH]: [8, 0.8], [T.SPAWNER]: [4, 0.5], [T.DSPAWNER]: [5, 0.6], [T.LANTERN]: [9, 0.95],
            [T.EMBER]: [5, 0.7], [T.ACRYSTAL]: [8, 0.9], [T.RCRYSTAL]: [7, 0.85], [T.BIOVINE]: [5, 0.6],
            [T.CRYSTSHARD]: [4, 0.5], [T.GEYSER]: [6, 0.7], [T.HELLSTONE]: [4, 0.5],
            [T.PULSEVINE]: [6, 0.7], [T.SPORECAP]: [5, 0.6], [T.TIMECRACK]: [7, 0.8],
            [T.PRISMCORE]: [10, 1],
            [T.CHARGEDCRYSTAL]: [10, 1], [T.NERVEVEIN]: [5, 0.5], [T.EYEBLOCK]: [6, 0.6],
            [T.HEARTBLOCK]: [8, 0.8], [T.LIGHTHOUSEBLOCK]: [3, 0.3],
            [T.WORLDHEART]: [12, 1], [T.WORLDSAP]: [8, 0.9], [T.WORLDCORE]: [10, 0.95],
            [T.ROOTCRYSTAL]: [7, 0.8], [T.CRACKSHARD]: [5, 0.6], [T.WORLDCRACK]: [6, 0.7],
            [T.ROOTVEIN]: [4, 0.5]
        };
        // Recalculate Block Light
        for (let x = cx - r - 10; x <= cx + r + 10; x++)for (let y = cy - r - 10; y <= cy + r + 10; y++) {
            if (x < 0 || x >= WW || y < 0 || y >= WH) continue;
            const l = lights[this.gt(x, y)]; if (!l) continue; const [lr, li] = l;
            for (let dx = -lr; dx <= lr; dx++)for (let dy = -lr; dy <= lr; dy++) {
                const d = Math.sqrt(dx * dx + dy * dy); if (d <= lr) {
                    const lx = x + dx, ly = y + dy;
                    if (lx >= cx - r && lx <= cx + r && ly >= cy - r && ly <= cy + r && lx >= 0 && lx < WW && ly >= 0 && ly < WH) {
                        const idx = ly * WW + lx;
                        if (this.castRay(x, y, lx, ly)) {
                            this.blockLight[idx] = Math.max(this.blockLight[idx], li * (1 - d / lr));
                        }
                    }
                }
            }
        }
    }
    getBiomeName(x) { return BIOME_NAMES[this.biomeMap[Math.max(0, Math.min(WW - 1, Math.floor(x)))]] || 'Unknown'; }
    getUnderBiomeName(x) { return this.UNDER_NAMES[this.underBiomeMap[Math.max(0, Math.min(WW - 1, Math.floor(x)))]] || 'Caverns'; }
    // Returns blend weight (0..1) for how strongly a biome-specific modifier should apply at position x
    _biomeBlendWeight(x, b) {
        const EDGE = 25;
        let distToEdge = EDGE;
        for (let dx = 1; dx <= EDGE; dx++) {
            const lx = x - dx, rx = x + dx;
            if ((lx >= 0 && this.biomeMap[lx] !== b) || (rx < WW && this.biomeMap[rx] !== b)) { distToEdge = dx; break; }
        }
        return Math.min(1, distToEdge / EDGE);
    }
    // Get blended biome weights for a tile position (used for smooth visual transitions)
    getBiomeWeights(tileX) {
        const RADIUS = 80;
        const weights = {};
        let total = 0;
        const cx = Math.max(0, Math.min(WW - 1, Math.floor(tileX)));
        for (let dx = -RADIUS; dx <= RADIUS; dx++) {
            const x = cx + dx;
            if (x < 0 || x >= WW) continue;
            const w = Math.exp(-Math.abs(dx) * 0.04);
            const b = this.biomeMap[x];
            weights[b] = (weights[b] || 0) + w;
            total += w;
        }
        for (const b in weights) weights[b] /= total;
        return weights;
    }
}